// Workspace projects — the runtime LOADER that resolves a workspace's MODULE and
// applies it to the live context. A "workspace module" (type:'workspace') is a
// self-contained personalization bundle — design system, navbar, dashboard
// panels, apps section, seed records, AND its own components / pages / functions.
//
// On workspace activation loadWorkspaceProject():
//   1. resolves the module for the active workspace (server-driven, org-guarded),
//   2. LOADS its code — a PUBLISHED PROJECT fetched by reference (manifest
//      v2: the module lives in the CLIENT's own repo/project and arrives as
//      serialized data — zero client code in this repo; the legacy v1 bundled
//      chunk was retired with the WS6 GreenIG migration, 2026-07-30),
//   3. normalizes the payload to ONE manifest (_normalizeManifest),
//   4. INJECTS its components + functions into the live context (context.components
//      / context.functions) so bare-key resolution + el.call work ONLY here —
//      skipping (with a warn) any key the shell already owns,
//   5. registers its pages into root.workspaceProject.pages AND the live router
//      map (the home AppWindow / openAppPage / el.router consult these),
//   6. stashes the manifest surface (widgets / aiFunctions / chatStarters) on
//      root.workspaceProject and seeds its declared state keys.
// On switch-away it TEARS DOWN exactly the injected keys + state, so a plain
// workspace sees none of it — every workspace's personalization is isolated
// from the shared shell. Any load failure = plain workspace, never a broken shell.
//
// WHICH module a workspace runs — and all of its config/panels/data — is
// SERVER-driven: the Mongo workspace record's `settings.workspaceModule` holds a
// project reference ({type:'project', owner, key, channel, version, tier,
// capabilities}); the legacy `settings.projectModule` string selects a bundled
// chunk. No workspace id is hardcoded in the frontend.

import {
  deepDestringifyFunctions,
  destringifyGlobalScope,
  isWrappedLibrary,
  mergeSharedLibraries
} from '@symbo.ls/utils'
// OPTIONAL widget `help` → `{ tip, ariaLabel }` | null. One normaliser, read by
// this loader and by both widget-header render sites. See functions/widgetHelp.js.
import { normalizeWidgetHelp } from './widgetHelp.js'
import { localWorkspaceAppLoader } from './localWorkspaceApps.js'
// Bare shared-library import, not a deep workspacePrefix.js subpath — see
// app.js's identical fix note (tickets/catalog-fixall.md P0).
// ⚠ The `.functions` read MUST stay deferred to call time (arrow, never a
// module-scope destructure): this module loads inside the shared-barrel init
// cycle, where `_wsShared` is still undefined — a module-scope
// `const { x } = _wsShared.functions` killed the ENTIRE shell boot
// (blank app, 2026-08-12, twice: pageContext.js first, then here).
import _wsShared from '@symbo-ls/workspace-shared'
import { shellStripWorkspacePrefix, shellGetAiService } from './shellSharedDelegates.js'
const fetchProjectFromSDK = (...args) => _wsShared.functions.fetchProjectFromSDK(...args)
const resolveEnvPublishConfig = (...args) => _wsShared.functions.resolveEnvPublishConfig(...args)
const recordsEntryKey = (...args) => _wsShared.functions.recordsEntryKey(...args)
const shellWorkspaceSettings = (...args) => _wsShared.functions.workspaceSettings(...args)
const shellIsCommHost = () => _wsShared.functions.isCommHost()
import { applyWorkspaceConfig, installedAppRefs, extensionAppRows } from './workspaceConfigs.js'
// THE accessor for `root.workspace.settings` (spec §5). Both loaders below read
// the bag off the held record before deciding whether to fetch, and both keyed
// their in-flight dedup off it — reading it raw let a LIST-shaped record (which
// never carries `settings`) be indistinguishable from a FULL one that simply
// has no overrides.
import {
  buildModuleActionFns,
  MODULE_ACTION_AI_FNS,
  moduleActionDocs,
  moduleActionKeys,
  moduleActionLabels
} from './moduleActions.js'
import { currentUserIsAdmin } from './roleHelpers.js'
import { buildModuleDataBridge } from './moduleDataBridge.js'
import {
  buildModuleImportBridge,
  buildModuleAssetUrlBridge,
  buildModuleMountBridge,
  buildModuleUnmountBridge,
  buildModuleApiBridge,
  buildModuleTokenBridge,
  buildModuleThemeBridge
} from './moduleImportBridge.js'
import { buildModuleOpenBridge, buildModuleRouteQueryBridge } from './moduleOpenBridge.js'
import {
  buildModuleShellComposerBridge,
  buildModuleComposerErrorPlugin
} from './moduleShellComposer.js'
import { readPageParam, readPageParams, readPageRoute, readPageUrl } from './pageParam.js'
import {
  buildModuleAiBridge,
  buildModuleAiStreamBridge,
  buildModuleAiAdminBridge
} from './moduleAiBridge.js'
import { buildModuleRegistryBridge } from './moduleRegistryBridge.js'
import {
  buildModuleFileBridge,
  buildModuleFileListBridge,
  buildModuleFileReadBridge,
  buildModuleFileExtractTextBridge,
  buildModuleFileUpdateBridge,
  buildModuleFileCreateBridge,
  buildModuleFileUploadLargeBridge
} from './moduleFileBridge.js'
import { buildModuleCalendarBridge } from './moduleCalendarBridge.js'
import { buildModuleNotifyBridge, buildModuleNotifyCloseBridge } from './moduleNotifyBridge.js'
import { buildModuleRoleBridge, buildModuleCanBridge } from './moduleAccessBridge.js'
import { buildModuleAskBridge } from './moduleAskBridge.js'
import { getWorkspaceCached } from './getWorkspaceCached.js'
// WS-MODULE-SCOPED-KEYS-CONTRACT-1 — the module tenant-scoped-keys registry
// (manifest `scopedKeys` + `hooks.onWorkspaceSwitch`); scopes.js spreads it
// into the PHASE-1 switch wipe, this loader registers/unregisters + tears down.
import {
  registerModuleScopedKeys,
  unregisterModuleScopedKeys,
  moduleScopedWipeFor,
  runModuleSwitchHook,
  normalizeScopedKeys,
  __resetModuleScopedKeysForTests
} from './moduleScopedKeys.js'
// WS-MODULE-STATE-SCOPE-1 — the STATE half of per-module scoping. Read the
// contract at the top of moduleStateScope.js before changing anything here.
import {
  createModuleStateRoot,
  aliasShellOwnedKeys,
  attachModuleStateScope,
  isShellOwnedStateKey
} from './moduleStateScope.js'

const _lc = (v) => (v == null ? '' : String(v).toLowerCase())

// Drop null/undefined-valued keys before a source merges INTO a fallback
// object, so an explicitly-cleared settings key falls through to the next
// precedence layer instead of clobbering it with `null` (the four "reset"
// call sites — see the config: comment below — persist a reset as
// `settings.<key>:null`; the merge-safe PATCH writer has no real $unset yet,
// see tickets/server.md). Mirrors workspaceConfigs.js's _normalizeSavedRow,
// which already does the same per-key `!= null` filter for the no-module
// Mongo-settings path — this brings the module-config path in line with it.
// Keeps real falsy values (`false`/`0`/`''`/`[]`) since those ARE deliberate
// overrides; only null/undefined mean "absent." Pure — never mutates its
// input.
const _stripNullish = (obj) => {
  const out = {}
  for (const k of Object.keys(obj || {})) {
    if (obj[k] != null) out[k] = obj[k]
  }
  return out
}

// Settings-bag strip for the config merge below. On top of _stripNullish,
// navbar is the one key where an EMPTY ARRAY is ALSO "cleared", not a
// deliberate override — `settings.navbar: []` (a legacy admin-console
// Save-after-Reset write; mcSavePersonalization now normalizes it to null)
// would otherwise win the spread and render a header with ZERO tabs instead
// of falling through to configDefaults.navbar / DEFAULT_NAVBAR. Other keys
// keep [] (an empty home_default_panels is a deliberate hide-all). Same
// guard as workspaceConfigs.js's _normalizeSavedRow and HeaderTabs' readers.
const _stripClearedSettings = (obj) => {
  const out = _stripNullish(obj)
  if (Array.isArray(out.navbar) && !out.navbar.length) delete out.navbar
  return out
}

// Stale-safety org-guard. root.workspace can lag root.org across an ORG switch
// (cross-org stale-workspace leak) — only adopt a module when the workspace's
// parent org matches the active org (root.org updates reliably on switch).
const _belongsToActiveOrg = (root) => {
  const org = root.org || {}
  const ws = root.workspace || {}
  const orgId = _lc(org.id || org._id)
  const wsOrg = _lc(ws.organization || ws.organizationId || ws.org)
  if (orgId && wsOrg && orgId !== wsOrg) return false
  return true
}

// Live context registry (context.components / context.functions) reachable from
// the root STATE. The state itself carries no .context — but state.__element is
// the root element, and element.context is the singleton registry DOMQL resolves
// bare keys + el.call against (verified: injecting into it makes el.call resolve).
const _ctx = (root) => {
  const elx = root && (root.__element || (root.getRoot && root.getRoot()))
  return (elx && elx.context) || (root && root.context) || null
}

// The ROUTED root element — the one carrying `.routes`, which smbls' router
// matches `pathname` against LIVE on every navigation (router/index.js:
// matchRoute(pathname, element.routes, …)). preparePages() assigns the merged
// page map to this element's `.routes` at boot. Injecting module routes here
// (and tearing them down on switch-away) lets a module page navigate as a real
// full shell page (el.router('/policies')) — resolving ONLY while this module
// is active, exactly like its components/functions. `el.router(path, el.getRoot())`
// from a nav item reads this same element, so target getRoot().routes.
const _routedEl = (root) => {
  if (!root) return null
  const elx = root.__element || (root.getRoot && root.getRoot())
  if (!elx) return null
  const top = (elx.getRoot && elx.getRoot()) || elx
  if (top && top.routes) return top
  return elx.routes ? elx : null
}

// ── Published-module pipeline ─────────────────────────────────────────────────
// SDK-backed fetch + channel resolution, held in a swappable holder so the
// colocated node --test suite can inject mocks (no network). Production code
// never reassigns it.
const _pipeline = {
  fetchProject: fetchProjectFromSDK,
  resolveEnv: resolveEnvPublishConfig
}

// NOTE: the prefetch concurrency limit (3 — of the browser's ~6 per-host
// HTTP/1.1 sockets, leaving room for the route's own data) is INLINED at its
// single call site in loadWorkspaceApps rather than held in a module const.
// See the comment there: a boot-path helper must not depend on cross-scope
// resolution.

// Minimal concurrency gate. Returns `start(fn)` which resolves/rejects exactly
// like `fn()` but waits for a free slot before invoking it — so callers keep
// building their promise map eagerly (the `_prefetched` Map contract is
// unchanged) while the network work stays bounded. Exported for the colocated
// node --test suite.
export const _concurrencyGate = (limit) => {
  let active = 0
  const queued = []
  const pump = () => {
    while (active < limit && queued.length) {
      active++
      queued.shift()()
    }
  }
  return (fn) =>
    new Promise((resolve, reject) => {
      queued.push(() => {
        Promise.resolve()
          .then(fn)
          .then(resolve, reject)
          .finally(() => {
            active--
            pump()
          })
      })
      pump()
    })
}

// Loaded modules memoized per `${owner}/${key}@${branch}#${version}` — a
// workspace re-activation (or an A→B→A switch) within one session never
// re-fetches; a different branch of the same project is a different entry.
const _moduleMemo = new Map()

// Content refusals by `owner/key` — audit, tier, manifest, missing — for the
// apps pass to tell a settled 'blocked' from a transient 'partial'. See
// _loadPublishedModule's `_refuse`.
const _refusals = new Map()

// In-flight loads keyed the same way — the dual-trigger's two concurrent
// onWorkspaceChange runs share ONE fetch+process instead of both hitting the
// backend (the memo above is only SET after the await, leaving a dedup gap).
const _moduleInflight = new Map()

// CHANNEL HANDOFF (perf/shell-boot, 2026-10-09). A channel resolves through
// two serial reads (getProjectByKey → listEnvironments). Measured live on
// staging /w/vault/default with a cold env-config cache: the takeover pass
// resolved vault/vaultos-app at 9.6 s and the apps pass resolved the SAME
// channel again at 18.3 s (the workspace also lists the module in
// settings.workspaceApps) — too far apart for the SDK's in-flight GET dedupe.
// A resolution is now handed to the NEXT consumer of the same key, once:
//   'prefetch' (prefetchWorkspaceModule, at /core/boot) → the takeover pass
//   'takeover' (the takeover pass)                     → the apps pass
// The takeover never accepts a 'takeover' entry, so an explicit reload (module
// console, converge retry) still re-resolves. One read per entry, 30 s max age.
// The key carries the workspace id, so no handoff crosses workspaces.
const _channelHandoff = new Map()
const _CHANNEL_HANDOFF_MAX_AGE_MS = 30000
const _channelHandoffKey = (owner, key, channel, wsId) =>
  String(owner) + '/' + String(key) + '@' + String(channel) + '#' + String(wsId || '')
const _channelHandoffFresh = (k, accept) => {
  const h = _channelHandoff.get(k)
  return !!(h && accept.includes(h.from) && Date.now() - h.at < _CHANNEL_HANDOFF_MAX_AGE_MS)
}
const _takeChannelHandoff = (k, accept) => {
  if (!accept.length || !_channelHandoff.has(k)) return null
  const fresh = _channelHandoffFresh(k, accept)
  const h = _channelHandoff.get(k)
  if (accept.includes(h.from)) _channelHandoff.delete(k)
  return fresh ? h.p : null
}
// Which handoffs a caller may take: the takeover only takes the boot
// prefetch's; the prefetch takes none; the apps pass (no role) takes either.
const _channelAccept = (role) =>
  role === 'prefetch' ? [] : role === 'takeover' ? ['prefetch'] : ['takeover', 'prefetch']

// One boot prefetch per workspace per page (prefetchWorkspaceModule).
let _bootPrefetchWsId = null

// Capability-bridge SDK seam (test-only): `buildModuleDataBridge` defaults to
// the REAL `getSDK()` (network-backed), which the loader never overrides in
// production. A unit test that exercises a GRANTED capability (as opposed to
// the deny-by-default path, which short-circuits before ever calling
// getSdkFn()) reaches that real SDK — CI-load-dependent, not a hang locally
// where the SDK's own client-side arg validation rejects fast (see
// tickets/workspace.md "CI Test red on main: capabilities.integrations tests
// hang at 5000ms (local-green)"). `__setModuleSdkFnForTests` lets a test
// inject the same `getSdkFn` seam `buildModuleDataBridge` already exposes,
// with zero source-behavior change when unset (production never calls it).
let _sdkFnOverrideForTests = null
export const __setModuleSdkFnForTests = (fn) => {
  _sdkFnOverrideForTests = fn
}

// Test seams (node --test): swap the fetch pipeline / reset module-level state
// between cases. Never called by production code.
export const __setModulePipelineForTests = (overrides) => Object.assign(_pipeline, overrides)
export const __resetModuleLoaderForTests = () => {
  if (globalThis.__wsEnvWarm) globalThis.__wsEnvWarm.clear()
  _channelHandoff.clear()
  _bootPrefetchWsId = null
  _refusals.clear()
  _moduleMemo.clear()
  _libraryFnTexts.clear()
  _moduleDsCtxCache.clear()
  _moduleScopeCache.clear()
  _moduleStateCache.clear()
  _moduleInflight.clear()
  _activeProjectId = null
  _moduleLoadedWorkspaceId = null
  _injectedComponentKeys = []
  _injectedFunctionKeys = []
  _injectedPageKeys = []
  _injectedBags = null
  _injectedTranslations = null
  _injectedStateKeys = []
  _injectedStateSeeds = {}
  _takeoverActions = null
  _takeoverStash = null
  _lastTakeoverFp = null
  _lastAppsFp = null
  _injectedByApp.clear()
  _lastLoadOutcome = 'idle'
  _lastAppsOutcome = 'idle'
  // Dev-override warn-once latches — module-level, so without this a test that
  // exercises the unpinned path would suppress the warning for every later one.
  _warnedUnpinnedOverride = false
  _announcedOverrideFor = ''
  // Invalidate any in-flight converge loop (it checks the gen after every
  // await) and re-open the singleton slot for the next test case.
  _convergeGen++
  _convergeRunning = null
  _convergeOwner = null
  // Single-flight slots (F4) — a settled-in-a-previous-test promise must
  // never be handed to the next test's same-key call.
  _projectFlightKey = null
  _projectFlight = null
  _projectFlightRoot = null
  _appsFlightKey = null
  _appsFlight = null
  _appsFlightRoot = null
  _appsSettled = null
  _projectSettled = null
  for (const waiter of _routeWaiters) waiter.resolve(true)
  _routeWaiters.clear()
  _appActivations.clear()
  _activationQueueSeq++
  _navLangLedgers.clear()
  // A previous test's injected fake SDK must never leak into the next case.
  _sdkFnOverrideForTests = null
  // Var-ownership latches — module-level; a test that left the module layer
  // owning `--stage-bg`/`--home-card-bg` would make a later test's release
  // branch remove a value that test never wrote.
  _moduleStageVarOwned = false
  _moduleCardVarOwned = false
  __resetModuleScopedKeysForTests()
}

// WS-MODULE-PAYLOAD-CACHE-1 (BF-SIGNIN-LAYOUT-LATENCY-1) — persistent
// single-slot payload cache. The 1.1MB module payload fetch measured
// 2.6-3.0s of EVERY boot's reveal window (sidebar at 12.7s cold / 7.2s warm,
// 2026-08-18); a warm slot mounts the module with ZERO payload wait and
// revalidates through the SAME fetch in the background (write-through lives
// in shared fetchProjectFromSDK — the pre-revive seam). ONE fixed slot keeps
// the localStorage quota bounded to a single payload; multi-workspace users
// evict each other's slot — correct, just fewer hits. The payload
// self-describes its version; a superseded slot logs and the fresh slot
// serves on the NEXT load (in-session hot-swap is deliberately NOT done —
// the reveal-once design exists to avoid mid-session repaints). The cached
// row carries moduleAudit, so the publish-time refusal gate applies to
// cached payloads identically. Module CODE, not user data — no sign-out
// clearing; a cross-user hit revalidates immediately.
//
// Review D4 (perf/shell-boot, 2026-10-10): the slot is keyed by the WORKSPACE
// RECORD id (`workspaceModulePayloadCache_v2:<wsId>`), and at most two
// workspaces keep a slot (LRU through a tiny index row, so no 1 MB row is
// parsed to decide). A boot prefetch that guessed the wrong workspace now
// writes that workspace's own slot instead of overwriting the one the next
// boot needs. The row still carries its own version; the background
// revalidation compares it. The legacy single slot (v1) is dropped on the
// first write.
const PAYLOAD_CACHE_PREFIX = 'workspaceModulePayloadCache_v2:'
const PAYLOAD_CACHE_INDEX = 'workspaceModulePayloadCache_v2__index'
const PAYLOAD_CACHE_LEGACY = 'workspaceModulePayloadCache_v1'
const PAYLOAD_CACHE_MAX_SLOTS = 2
export const _payloadSlotKey = (wsId) => PAYLOAD_CACHE_PREFIX + String(wsId || '')
export const _payloadCacheRead = (owner, key, branch, wsId) => {
  try {
    // No workspace id → no slot (review Delta 2 N7b: never a shared empty key).
    if (!wsId || typeof localStorage === 'undefined') return null
    const raw = localStorage.getItem(_payloadSlotKey(wsId))
    if (!raw) return null
    const row = JSON.parse(raw)
    if (!row || row.v !== 1 || !row.data) return null
    // The row names the workspace it was cached for (shared fetchProjectFromSDK
    // stores options.workspaceId) — the key alone is not the guard (N7b).
    if (String(row.wsId || '') !== String(wsId)) return null
    if (row.owner !== owner || row.key !== key) return null
    if ((row.branch || 'main') !== (branch || 'main')) return null
    return row
  } catch (_) {
    return null
  }
}
// Called AFTER a slot for `wsId` was read or written (review Delta 2 N7a: the
// SDK writes the row when the fetch ENDS, so a touch before it let a late
// write land outside the index): records it as the newest slot, removes every
// slot beyond the newest PAYLOAD_CACHE_MAX_SLOTS, any v2 slot the index does
// not name (an orphan), and the legacy single slot. Never throws.
export const _payloadSlotTouch = (wsId) => {
  try {
    if (!wsId || typeof localStorage === 'undefined') return
    const mine = _payloadSlotKey(wsId)
    let order = []
    try {
      const parsed = JSON.parse(localStorage.getItem(PAYLOAD_CACHE_INDEX) || '[]')
      if (Array.isArray(parsed)) order = parsed.filter((k) => typeof k === 'string' && k !== mine)
    } catch (_) {
      order = []
    }
    order.unshift(mine)
    for (const k of order.slice(PAYLOAD_CACHE_MAX_SLOTS)) localStorage.removeItem(k)
    localStorage.setItem(
      PAYLOAD_CACHE_INDEX,
      JSON.stringify(order.slice(0, PAYLOAD_CACHE_MAX_SLOTS))
    )
    localStorage.removeItem(PAYLOAD_CACHE_LEGACY)
    const kept = new Set(order.slice(0, PAYLOAD_CACHE_MAX_SLOTS))
    const n = typeof localStorage.length === 'number' ? localStorage.length : 0
    const orphans = []
    for (let i = 0; i < n; i++) {
      const k = localStorage.key(i)
      if (typeof k === 'string' && k.startsWith(PAYLOAD_CACHE_PREFIX) && !kept.has(k))
        orphans.push(k)
    }
    for (const k of orphans) localStorage.removeItem(k)
  } catch (_) {
    /* the cache is best-effort */
  }
}

// Dev-only module override: `localStorage.workspaceModuleDev` holds a JSON
// reference ({owner, key, channel?, version?}) that REPLACES the server ref's
// target — the local iteration loop for authoring a module against a dev
// channel. Allowed on a development build OR any *.localhost host (the dev
// hostnames) — a RUNTIME check, so it survives a production-mode bundle where
// `process.env.NODE_ENV` is inlined and the NODE_ENV branch is dead-code
// stripped. Never fires on a real deployment (prod is *.symbols.app), so it
// can't leak into production.
const _devOverrideAllowed = () => {
  if (process.env.NODE_ENV === 'development') return true
  try {
    const h = typeof location !== 'undefined' && location.hostname
    return h === 'localhost' || (typeof h === 'string' && h.endsWith('.localhost'))
  } catch (_) {
    return false
  }
}

// The override MUST name the workspace it was authored against. Without this
// pin the key is GLOBAL: it merges over whatever workspace you happen to be in
// (see _loadPublishedModule's `{...ref, ...dev}`) AND forces a module load into
// workspaces the server says have none (_resolveModuleLoader). That is exactly
// how the Bellforge workspace rendered the AI House module's navbar and pages
// (2026-08-05 per Nika "why I'm in bellforge account and its showing aihouse
// navbar") — a dev pin left over from authoring a different org's module,
// silently shadowing real tenant content with no visible signal anywhere.
//
// So the pin is REQUIRED and this fails CLOSED: an unpinned (legacy) override
// is ignored outright rather than applied globally, and a pinned override is
// applied ONLY to its own workspace. Use setWorkspaceModuleDev() below, which
// stamps the active workspace for you.
const DEV_OVERRIDE_KEY = 'workspaceModuleDev'
let _warnedUnpinnedOverride = false
let _announcedOverrideFor = ''

// Same derivation loadWorkspaceProject uses for the pass's `wsId`
// (`root.workspace.id || ._id`), so a pin written here matches what the loader
// compares against.
const _activeWsIdFromRoot = (root) => {
  const ws = (root && root.workspace) || {}
  return ws.id || ws._id || ''
}

const _readDevOverrideRaw = () => {
  if (!_devOverrideAllowed()) return null
  try {
    const raw = typeof localStorage !== 'undefined' && localStorage.getItem(DEV_OVERRIDE_KEY)
    if (!raw) return null
    const ref = JSON.parse(raw)
    if (ref && typeof ref === 'object' && ref.owner && ref.key) return ref
  } catch (_) {
    /* unparsable override — ignore */
  }
  return null
}

// `wsId` is the workspace this pass is resolving a module FOR. Callers that
// genuinely have no workspace in hand pass nothing — and get null, because an
// override that can't be matched against a workspace must never apply.
const _devModuleOverride = (wsId) => {
  const ref = _readDevOverrideRaw()
  if (!ref) return null

  const pinned = String(ref.workspaceId || '')
  if (!pinned) {
    if (!_warnedUnpinnedOverride) {
      _warnedUnpinnedOverride = true
      console.warn(
        `[workspaceModule] IGNORING an unpinned ${DEV_OVERRIDE_KEY} override ` +
          `(${ref.owner}/${ref.key}). An override with no workspaceId applies to EVERY ` +
          'workspace and silently replaces real tenant content. Re-set it pinned:\n' +
          "  el.call('setWorkspaceModuleDev', { owner: '<owner>', key: '<key>', channel: 'development' })\n" +
          `  — or clear it:  localStorage.removeItem('${DEV_OVERRIDE_KEY}')`
      )
    }
    return null
  }

  if (!wsId || String(wsId) !== pinned) return null

  // Applied — say so, loudly and once per workspace. A dev override that
  // shadows the server's module with no visible signal is the whole hazard.
  if (_announcedOverrideFor !== pinned) {
    _announcedOverrideFor = pinned
    console.warn(
      `[workspaceModule] DEV OVERRIDE ACTIVE — loading ${ref.owner}/${ref.key}` +
        `${ref.channel ? `@${ref.channel}` : ''} instead of this workspace's configured module. ` +
        `Pinned to workspace ${pinned}. Clear with el.call('clearWorkspaceModuleDev').`
    )
  }
  return ref
}

// Set the dev override for the CURRENTLY ACTIVE workspace — the supported way
// to start an authoring loop, so the pin can never be forgotten.
//   el.call('setWorkspaceModuleDev', { owner: 'aihouse', key: 'workspace-module', channel: 'development' })
export function setWorkspaceModuleDev(ref) {
  const el = this
  if (!_devOverrideAllowed()) {
    console.warn('[workspaceModule] dev override is only available on a dev build / *.localhost')
    return null
  }
  if (!ref || !ref.owner || !ref.key) {
    console.warn('[workspaceModule] setWorkspaceModuleDev needs at least { owner, key }')
    return null
  }
  const root = el && el.state && el.state.root
  const wsId = root && _activeWsIdFromRoot(root)
  if (!wsId) {
    console.warn('[workspaceModule] no active workspace to pin the override to — aborting')
    return null
  }
  const pinned = { ...ref, workspaceId: String(wsId) }
  try {
    localStorage.setItem(DEV_OVERRIDE_KEY, JSON.stringify(pinned))
  } catch (e) {
    console.warn('[workspaceModule] could not persist the dev override', e && e.message)
    return null
  }
  _warnedUnpinnedOverride = false
  _announcedOverrideFor = ''
  console.warn(
    `[workspaceModule] dev override pinned to workspace ${wsId} -> ${pinned.owner}/${pinned.key}. Reload to apply.`
  )
  return pinned
}

export function clearWorkspaceModuleDev() {
  try {
    localStorage.removeItem(DEV_OVERRIDE_KEY)
  } catch (_) {
    /* storage unavailable — nothing to clear */
  }
  _warnedUnpinnedOverride = false
  _announcedOverrideFor = ''
  console.warn('[workspaceModule] dev override cleared. Reload to return to the server module.')
  return true
}

// ── Manifest normalization ────────────────────────────────────────────────────

// Map a manifest pageGuard to the permission key shouldRedirectNoPerm gates on.
const _guardPermFor = (guard) => {
  if (guard && typeof guard.permission === 'string') return guard.permission
  if (guard && guard.workspaceRole === 'superadmin') return 'admin.danger.access'
  return 'admin.pages.read'
}

// Wrap a guarded module page: onInit/onUpdate re-check the viewer against the
// SAME signals the shell's admin pages use (roleHelpers.shouldRedirectNoPerm —
// registered in context.functions, so el.call resolves it) and bounce to '/'.
// Cold-load-safe: shouldRedirectNoPerm bails (false) until the user id +
// people/claims hydrate, AND while an org/workspace switch is still
// converging (`_wsSwitching`/`_wpSwitching`/`_dataLoading` — see that
// function's WS-REDIRECT-1 comment), so a guarded page never bounces
// mid-hydration OR mid-switch on stale/outgoing-scope data. `onUpdate`
// firing on every reactive render is what turned a transient scope-in-flux
// read into a silent re-navigation before that fix landed.
// Client-UX only — the server is the wall.
//
// THE COLD DEEP LINK (F2, 2026-10-31). The check's inputs — identity, the
// claims, the roster, the "scope not settled" flags — can land AFTER onInit
// on a cold deep link, and smbls runs a page's update hooks on a root-state
// write ONLY for the keys the page subscribes to — so a module page with no
// subscription of its own never ran `onUpdate` on those writes, and the
// re-check never fired. Such a page now subscribes to the gate's
// inputs (roleHelpers.js `routeGateKeys`), and a subscription pass runs the
// CHECK ALONE: `onBeforeUpdate` answers `false` for `{ updateBySubscription:
// true }`, which vetoes the rest of the pass (the page's forced prop re-runs
// and its own onUpdate — loads, fetches, renders), so the module's own work
// runs no more often than before. Every other pass reaches the module's own
// `onBeforeUpdate` as before. A module page that declares its OWN
// `subscribeTo` keeps exactly its shape of before: its passes run its own
// onUpdate, and the shell cannot tell its passes from the gate's.
export const _guardPage = (def, guard) => {
  const perm = _guardPermFor(guard)
  const check = (el, s) => {
    try {
      if (!el.call('shouldRedirectNoPerm', s, perm)) return
      el.call('routeGateBounce', '/')
    } catch (_) {
      /* guard helpers unavailable (non-workspace shell) — server still enforces */
    }
  }
  const page = {
    ...def,
    onInit: (el, s, ...rest) => {
      check(el, s)
      if (typeof def.onInit === 'function') return def.onInit(el, s, ...rest)
    },
    onUpdate: (el, s, ...rest) => {
      check(el, s)
      if (typeof def.onUpdate === 'function') return def.onUpdate(el, s, ...rest)
    }
  }
  if (def.subscribeTo != null) return page
  page.subscribeTo = (el) => el.call('routeGateKeys')
  page.onBeforeUpdate = (el, s, ctx, opts, ...rest) => {
    if (opts && opts.updateBySubscription) {
      check(el, s)
      return false
    }
    if (typeof def.onBeforeUpdate !== 'function') return undefined
    return def.onBeforeUpdate(el, s, ctx, opts, ...rest)
  }
  return page
}

const _WIDGET_ID_RE = /^[a-z0-9-]+$/

// ── A module's own shared libraries ─────────────────────────────────────────
//
// A published module project can link shared libraries (`useLibraries` on the
// project record). The server RESOLVES those links and ships the library
// CONTENT on the payload as `sharedLibraries: [{ id, key, name, projectType,
// components, functions, pages, designSystem, … }]` (ProjectDataService
// `pickSharedLibrarySlices`). Until WS-MODULE-LOADER-DROPS-SHARED-LIBRARIES-1
// this loader read `mod.components` / `mod.functions` ONLY and dropped that
// array on the floor — so a module that adopted a library rendered nothing
// (bellforge → system/pipeline: `ctx.components.PipelineBoard` undefined, zero
// of the 20 `pipeline*` functions merged, console clean because an unresolved
// bare key renders silently).
//
// The merge REUSES the platform's one resolver, `mergeSharedLibraries` from
// `@symbo.ls/utils` — the same function `smbls/packages/smbls/src/prepare.js`
// `prepareSharedLibs` runs for every standalone app boot. That buys the exact
// platform precedence for free: it is non-destructive (`if (!(k in context[key]))`),
// so framework built-ins → shared libraries → the module's OWN keys, the
// module always winning, never shadowed (Rule 63). It also honors the
// library-side `sharedLibIgnore` / `sharePages:false` privacy declarations.
//
// Only the sections this loader actually consumes are merged (components /
// functions / pages / designSystem / globalScope). The payload entries also
// carry METADATA (`id`, `key`, `name`, `version`, `projectType`) which
// `mergeOneLibrary` would copy wholesale into the merge target as new top-level
// keys — `_libContentSlice` strips them so a library can never inject an
// `id`/`version` into the module manifest it is merged into.
//
// `globalScope` is in the list for the same reason the other four are: the
// server ships a resolved library's globalScope bag on the payload
// (ProjectDataService `pickSharedLibrarySlices`) precisely so the client can
// fold it under the consumer, and a standalone boot of the same app does fold
// it (prepareSharedLibs merges every section key generically). Leaving it out
// made a library component's `__scope.X` read resolve standalone and come back
// undefined in-shell — the exact divergence class of
// WS-MODULE-CTX-DROPS-APP-GLOBALSCOPE-1.
const _LIB_MERGE_SECTIONS = ['components', 'functions', 'pages', 'designSystem', 'globalScope']

// Deep copy of plain data. `deepDefaults` (the designSystem branch of the
// merge) recurses INTO the target, so merging straight onto the fetched
// payload's own designSystem objects would mutate the memoized payload (and
// the sessionStorage warm slot it was revived from). Tokens are plain data —
// non-plain values (functions, class instances) pass through by reference.
const _clonePlain = (v) => {
  if (Array.isArray(v)) return v.map(_clonePlain)
  if (v && typeof v === 'object' && (v.constructor === Object || !v.constructor)) {
    const out = {}
    for (const k in v) {
      if (Object.prototype.hasOwnProperty.call(v, k)) out[k] = _clonePlain(v[k])
    }
    return out
  }
  return v
}

// One payload library entry → the content-only slice that is safe to merge.
// Returns null when the entry carries nothing this loader consumes.
const _libContentSlice = (lib) => {
  if (!lib || typeof lib !== 'object') return null
  const slice = {}
  for (const section of _LIB_MERGE_SECTIONS) {
    if (lib[section] && typeof lib[section] === 'object') slice[section] = lib[section]
  }
  if (!Object.keys(slice).length) return null
  // Merge DIRECTIVES, not content — carried through so the library's own
  // privacy declaration is honored by mergeOneLibrary.
  if (lib.sharedLibIgnore !== undefined) slice.sharedLibIgnore = lib.sharedLibIgnore
  if (lib.sharePages !== undefined) slice.sharePages = lib.sharePages
  return slice
}

// Fold a module payload's linked libraries under its own sections.
// A module with NO libraries returns the payload's own objects untouched (same
// references, no clone, no merge) — the pre-existing path, byte for byte.
export const _mergeModuleLibraries = (mod) => {
  const own = {
    components: (mod && mod.components) || {},
    functions: (mod && mod.functions) || {},
    pages: (mod && mod.pages) || {},
    globalScope:
      mod && mod.globalScope && typeof mod.globalScope === 'object' ? mod.globalScope : {},
    designSystem:
      mod && mod.designSystem && typeof mod.designSystem === 'object' ? mod.designSystem : null
  }
  const raw = mod && Array.isArray(mod.sharedLibraries) ? mod.sharedLibraries : []
  if (!raw.length) return own

  const entries = []
  for (const entry of raw) {
    // A wrapped `{ library, ignoreList }` (utils `sharedLibrary()`) may reach
    // the payload from a project that declared consumer-side ignores.
    let library = entry
    let ignoreList = null
    if (isWrappedLibrary(entry)) {
      ignoreList = entry.ignoreList
      library = entry.library
    }
    const slice = _libContentSlice(library)
    if (!slice) continue
    entries.push(ignoreList ? { library: slice, ignoreList } : slice)
  }
  if (!entries.length) return own

  // Descriptor copies: lazy component entries stay lazy (boot fix step 4).
  const merged = {
    components: _copyEntries(own.components),
    functions: { ...own.functions },
    pages: _copyEntries(own.pages),
    globalScope: { ...own.globalScope },
    designSystem: own.designSystem ? _clonePlain(own.designSystem) : {}
  }
  mergeSharedLibraries(merged, entries)
  if (!Object.keys(merged.designSystem).length) merged.designSystem = null
  return merged
}

// Normalize a loaded module to ONE engine shape, whatever its source:
//
//   v2 (published project, `ref` present) — `manifest: 2` on the project data
//   or its config object. The project's components/pages/functions ARE the
//   module; the manifest extras (id/widgets/aiFunctions/chatStarters/actions/
//   collections/configDefaults) ride on the config object. Validated: id must
//   equal `${ref.owner}/${ref.key}` (a module can't impersonate another),
//   page keys must start with '/', widget ids match /^[a-z0-9-]+$/, string
//   widget bodies must resolve to a module component. Guarded pages are
//   wrapped here so every consumer (router, PageSlot) mounts the wrapped def.
//   Capabilities come from the SERVER ref only — never from the manifest (a
//   module cannot self-grant).
//
//   Manifest v1 (the legacy bundled GreenIG chunk) is GONE — WS6 migration
//   completed 2026-07-30: the last v1 module ships as the published v2
//   project silverbreeze-labs/greenig-app, and no workspace resolves the
//   legacy string path anymore (dev-Mongo verified: exactly one workspace
//   carried `settings.projectModule` and it holds the v2 ref, which wins).
//   A payload without a v2 manifest is refused outright.

// MODULE-FN-READONLY-ENFORCED-1 — the `aiFunctions` names marked
// `{ name, readOnly: true }` (only the literal true; a bare string never is),
// and only when no other declaration of the same name leaves it writable.
export const _readOnlyAiFunctionNames = (list) => {
  if (!Array.isArray(list)) return []
  const verdict = new Map()
  for (const f of list) {
    const name =
      typeof f === 'string' ? f.trim() : f && typeof f.name === 'string' ? f.name.trim() : ''
    if (!name) continue
    const ro = !!(f && typeof f === 'object' && f.readOnly === true)
    verdict.set(name, verdict.has(name) ? verdict.get(name) && ro : ro)
  }
  return [...verdict].filter(([, ro]) => ro).map(([name]) => name)
}

export const _normalizeManifest = (mod, ref) => {
  if (!mod || typeof mod !== 'object') return null
  const cfg = mod.config && typeof mod.config === 'object' ? mod.config : {}
  const isV2 = mod.manifest === 2 || cfg.manifest === 2

  if (!isV2) {
    console.warn(
      '[workspaceModule] payload declares no v2 module manifest — refusing to load:',
      ref ? `${ref.owner}/${ref.key}` : mod.id || '(unknown)'
    )
    return null
  }

  const manifest = cfg.manifest === 2 ? cfg : mod
  const rawId = manifest.id || mod.id
  // The Symbols project's OWN top-level `id` (a Mongo ObjectId) shadows the
  // module's declared `id` when the manifest extras are spread onto project
  // data at publish. An ObjectId-shaped value therefore means "no module id
  // survived serialization" — fall back to the already-authenticated ref
  // (server settings / dev override) rather than rejecting. A real spoof would
  // declare a string `owner/key`, which is NOT ObjectId-shaped and is still
  // caught below. (TODO: namespace the manifest so `id` can't collide.)
  const id = /^[0-9a-f]{24}$/i.test(String(rawId)) && ref ? `${ref.owner}/${ref.key}` : rawId
  if (ref) {
    const expected = `${ref.owner}/${ref.key}`
    if (id !== expected) {
      console.warn(
        '[workspaceModule] manifest id mismatch — refusing to load:',
        id,
        '!==',
        expected
      )
      return null
    }
  }

  // The module's own sections WITH its linked shared libraries folded under
  // them (module keys always win — see _mergeModuleLibraries). Everything
  // downstream (widget body validation, the page loop, both injection paths in
  // loadWorkspaceProject / loadWorkspaceApps) reads these, so a library key is
  // indistinguishable from a module key from here on — exactly how a
  // standalone app boot sees it after prepareSharedLibs.
  const withLibs = _mergeModuleLibraries(mod)
  const components = withLibs.components
  const functions = withLibs.functions
  // WS-MODULE-CTX-DROPS-APP-GLOBALSCOPE-1 — the module's own globalScope bag
  // (frank rewrites every `__scope.<key>` read to a `<key>` read on
  // `el.context.globalScope`). Keep the dotted form out of this comment: frank
  // reads `globalScope` + dot + a name in a lifted helper's text as a member
  // read even inside a comment, and the phantom `X` it lifted from here broke
  // the shell emission (frank serializationScanner `_scanGlobalScopeMemberAccess`).
  // Passed through the platform's own resolver, exactly as `createDomql` does
  // for a standalone boot: a helper string that survived the payload-wide
  // `deepDestringifyFunctions` pass gets eval'd in ONE closure where every
  // sibling constant and helper is declared, and Set/Map tagged forms rehydrate.
  // With no strings left in the bag it is a copy and evals nothing.
  const globalScope = destringifyGlobalScope(withLibs.globalScope || {})

  const pages = {}
  for (const [path, def] of Object.entries(withLibs.pages || {})) {
    if (!path.startsWith('/')) {
      console.warn('[workspaceModule] page key must start with "/" — skipped:', path)
      continue
    }
    pages[path] = def && def.pageGuard ? _guardPage(def, def.pageGuard) : def
  }

  const widgets = []
  for (const w of Array.isArray(manifest.widgets) ? manifest.widgets : []) {
    if (!w || typeof w !== 'object') continue
    if (!_WIDGET_ID_RE.test(String(w.id || ''))) {
      console.warn('[workspaceModule] widget id must match /^[a-z0-9-]+$/ — skipped:', w.id)
      continue
    }
    // A key check, not a read: reading would revive a lazy entry at boot.
    if (typeof w.body === 'string' && !Object.prototype.hasOwnProperty.call(components, w.body)) {
      console.warn('[workspaceModule] widget body component not found — skipped:', w.id, w.body)
      continue
    }
    // OPTIONAL `help` — normalised ONCE, at load, to `{ tip, ariaLabel }` or
    // null, so the two header render sites (pages/main.js `_widget` and
    // `HomeModuleWidget`) read one shape instead of re-deciding what the
    // manifest meant. See functions/widgetHelp.js for the full contract. A
    // declared-but-unusable `help` warns like a bad body does, and the widget
    // still ships — the (?) is chrome, never a reason to drop a card.
    const help = normalizeWidgetHelp(w.help)
    if (w.help && !help) {
      console.warn(
        '[workspaceModule] widget `help` needs a non-empty `tip` string (or a bare string) — ignored:',
        w.id
      )
    }
    widgets.push(w.help === undefined && !help ? w : { ...w, help })
  }

  // Declared collections → owned state keys (+ optional seeds).
  const stateKeys = []
  const seeds = {}
  for (const c of Array.isArray(manifest.collections) ? manifest.collections : []) {
    const stateKey = c && (c.stateKey || c.key)
    if (!stateKey) continue
    stateKeys.push(stateKey)
    if (c.seed !== undefined) seeds[stateKey] = c.seed
  }
  // …and → WHERE ONE OF THOSE ROWS OPENS. Same array, second reading. See
  // _normalizeRecordRoutes below.
  const recordRoutes = _normalizeRecordRoutes(manifest.collections, pages, id)
  // …and → WHICH OF THE MODULE'S OWN COMPONENTS DRAWS ONE ROW, wherever the
  // shell lists that collection's records outside the module's page (an AI
  // answer's records block). Third reading. See _normalizeRecordRows below.
  const recordRows = _normalizeRecordRows(manifest.collections, components, id)
  // …and the app's READ-ONLY VIEWS for AI answers (manifest `views[]`). See
  // _normalizeViews below.
  const views = _normalizeViews(manifest.views, components, id)
  // …and the app's OWN FORMS an AI answer may open, prefilled (manifest
  // `forms[]`). See _normalizeForms below.
  const forms = _normalizeForms(manifest.forms, components, id, functions)

  // Declared data recipes — OPTIONAL manifest metadata for the AI's workspace
  // catalog: declarative fetch descriptors ({ id, from, method, params, as, …})
  // a drawn answer view can paste verbatim. Pure catalog data — nothing in the
  // shell executes them; capability walls still apply at call time.
  const dataRecipes = Array.isArray(manifest.dataRecipes)
    ? manifest.dataRecipes.filter((r) => r && typeof r === 'object' && (r.id || r.from))
    : []

  return {
    id,
    type: mod.type || 'workspace',
    manifestVersion: 2,
    components,
    functions,
    // The module's OWN scope root. `_moduleScopeFor` lays it over the shell's
    // as `modCtx.globalScope`; every element under a module page then resolves
    // `el.scope` to it (element/create.js: `baseScope = ancestorScope ||
    // element.context.globalScope`). Without this carrier a module's
    // `__scope.X` read returned the SHELL's bag — measured on prod
    // scheduling-app 1.2.0, where `__scope.NAT_COLLECTIONS` was undefined and
    // `natFetchAll` threw before its first request (the Salons tab read
    // "0 salons · 0 professionals" behind an error banner).
    globalScope,
    pages,
    widgets,
    // Names only — every consumer (the AI call gate, the merged action trio)
    // matches on a string, so that shape is load-bearing and stays.
    //
    // An entry MAY also be `{ name, description }`. The description is split
    // out into `aiFunctionDocs` below rather than widening this array, because
    // the description is for the MODEL and the names are for the gate: telling
    // the assistant a verb exists and letting it run are different decisions.
    aiFunctions: Array.isArray(manifest.aiFunctions)
      ? manifest.aiFunctions
          .map((f) => (typeof f === 'string' ? f : f && typeof f.name === 'string' ? f.name : null))
          .filter(Boolean)
      : [],
    // How each AI-drivable verb should be CALLED, keyed by name. Without this
    // the catalog could only tell the model a function exists — not what to
    // pass it — so an assistant had to guess the argument shape. Measured cost
    // of guessing, on excalidraw: free-floating text elements overlapping their
    // boxes instead of bound, wrapped labels.
    aiFunctionDocs: Array.isArray(manifest.aiFunctions)
      ? manifest.aiFunctions.reduce((acc, f) => {
          if (f && typeof f === 'object' && typeof f.name === 'string' && f.description) {
            acc[f.name] = String(f.description)
          }
          return acc
        }, {})
      : {},
    // The verbs the manifest marks `readOnly: true` (MODULE-FN-READONLY-
    // ENFORCED-1): the assistant runs them without the Run / Skip row, on a
    // read-only caller that refuses every write (moduleReadOnlyCall.js). A
    // name declared twice is read-only only when every declaration says so.
    readOnlyAiFunctions: _readOnlyAiFunctionNames(manifest.aiFunctions),
    chatStarters: Array.isArray(manifest.chatStarters) ? manifest.chatStarters : [],
    stateKeys,
    seeds,
    // WHERE a row of a declared collection OPENS — `[{ key, route, param }]`,
    // or null. Manifest-read like `opens`/`libraries` and for the same reason:
    // it grants nothing. It only names one of the module's OWN pages and the
    // query key that page already reads. See _normalizeRecordRoutes.
    recordRoutes,
    // The module's OWN row component for a declared collection —
    // `[{ key, row, moduleId }]`, or null. Names only: the def stays in the
    // module's components, mounted under the module context. See
    // _normalizeRecordRows.
    recordRows,
    // The app's read-only views an AI answer may mount (manifest `views[]`),
    // or null. See _normalizeViews.
    views,
    // The app's own forms an AI answer may open, prefilled (manifest
    // `forms[]`), or null. See _normalizeForms.
    forms,
    dataRecipes,
    // Presentation-mode manifest: `{ immersive: 'non-admin'|true, landing:
    // '/route' }`. Immersive hides the shell's top/bottom chrome for the
    // matched members and lands them on the module's own page (with the
    // module rail) instead of the home board — a module UX decision, so it
    // lives in the manifest like navbar/widgets, NOT a capability grant
    // (those are server-ref-only by design).
    chrome: manifest.chrome && typeof manifest.chrome === 'object' ? manifest.chrome : null,
    // The module's own pages that are pan/zoom CANVAS pages (manifest
    // `canvasRoutes: ['/route', …]`): the shell lands them full-bleed like
    // /tasks and shows its Zoombar, which drives the instance the page
    // registers (canvas registerCanvasPanzoom). shared moduleRoutes.js
    // isModuleCanvasRoute only honours a route the module also serves.
    canvasRoutes: Array.isArray(manifest.canvasRoutes)
      ? manifest.canvasRoutes.filter((r) => typeof r === 'string' && r.startsWith('/') && r !== '/')
      : null,
    // The module project's OWN raw design system (designSystem/ token files —
    // color, spacing, typography…). Without threading this, every module
    // token resolves against the SHELL's scratch config where the names don't
    // exist: unknown color names pass through as raw CSS ("navy" paints the
    // CSS keyword, "paper" silently drops the declaration) — the module looks
    // right standalone and broken in-shell. Processed into an isolated
    // per-module scratch config at inject time (_ensureModuleDsContext).
    // …with the linked libraries' tokens folded in as DEFAULTS (deepDefaults —
    // the module's own token always wins), so a library component that paints
    // with its own palette names resolves in-shell too.
    designSystem: withLibs.designSystem,
    actions: manifest.actions && typeof manifest.actions === 'object' ? manifest.actions : null,
    configDefaults:
      manifest.configDefaults && typeof manifest.configDefaults === 'object'
        ? manifest.configDefaults
        : null,
    // WS-MODULE-DECLARED-STATE-NOT-SEEDED-IN-SHELL-1 — the module's own
    // `state.js` default export. A top-level payload field exactly like
    // `globalScope` (never nested under the v2 manifest object — see
    // ProjectDataService.js `pick('state')`). Standalone, `prepareState`
    // (smbls prepare.js) merges `app.state` into the boot-time root; nothing
    // in this loader did the equivalent for a module's own store, so a key
    // only ever READ before a handler writes it (a url, a flag, an empty
    // list) stayed undefined in-shell forever. Seeded once per module store
    // at inject — see _seedDeclaredState.
    declaredState: mod.state && typeof mod.state === 'object' ? mod.state : null,
    // Vendor libraries the app may load at runtime (`{ pkg: 'x.y.z' }`), the
    // allowlist for the `moduleImport` bridge. Unlike `capabilities` this IS
    // read from the manifest: it grants no workspace data or authority — it
    // only names which public npm packages the shell will fetch on the app's
    // behalf, pinned to the exact declared version (moduleImportBridge.js).
    //
    // Keyed `libraries`, NOT `dependencies`: a project's own `dependencies.js`
    // (the runtime importmap, FA209) lands on the SAME payload as a top-level
    // `dependencies` and silently overwrote the manifest's map — the published
    // project came back with `dependencies: {}` and every moduleImport call
    // would have thrown "not declared". Caught by reading the published
    // payload back, which is why we do that.
    libraries:
      manifest.libraries && typeof manifest.libraries === 'object' ? manifest.libraries : null,
    // File formats this app OPENS, and the function that opens one. Installing
    // an app is what teaches the desktop a new file type — before this the
    // /files package carried a hardcoded row per app and drove the app's own
    // surface from outside to deliver the file, which is the shell knowing
    // another app's internals (tickets/workspace.md).
    //
    // Manifest-read for the same reason `libraries` is, and it needs saying
    // because `capabilities` sitting two lines below reads the opposite way:
    // this grants NOTHING. It claims an extension and names a function the app
    // already exports — every one of which the shell could call anyway.
    // `capabilities.files` is a different thing entirely (an upload grant) and
    // is NOT a place to declare formats.
    //
    // A row needs `ext` + `entry` to be usable at all; anything short of that
    // is dropped rather than half-registered, so a typo shows up as "no app
    // opens this" instead of a handler that routes and then does nothing.
    opens: _normalizeOpens(manifest.opens, pages),
    // WS-MODULE-SCOPED-KEYS-CONTRACT-1 — the module's DECLARED tenant-scoped
    // root keys (`'key'` | `'prefix*'` | `{ key|prefix, empty }`) and its
    // tenant-boundary hook (`hooks.onWorkspaceSwitch: '<moduleFn>'`). Both are
    // manifest-read like widgets/chrome: they grant nothing, they only tell
    // the shell which root keys are this module's to wipe on a switch and
    // which module function fences its late writes. See
    // functions/moduleScopedKeys.js for the full contract.
    scopedKeys: _normalizeScopedKeysWarn(manifest.scopedKeys, id),
    // WS-MODULE-STATE-SCOPE-1 — the module's EXPLICIT opt-in to the SHELL state
    // plane. Same entry grammar as `scopedKeys` (`'key'` | `'prefix*'`). A
    // module's `s.root` writes are module-local by default; a key declared here
    // writes THROUGH to the shell root instead, for the rare key a module
    // genuinely shares with the shell or with a sibling app. Declaring nothing
    // is the correct default — the shell's own keys are already write-through
    // by schema (see functions/moduleStateScope.js SHELL_OWNED_STATE_KEYS).
    rootStateKeys: _normalizeScopedKeysWarn(manifest.rootStateKeys, id, 'rootStateKeys'),
    hooks: _normalizeHooks(manifest.hooks, id),
    // The grant is DATA on the server ref — the manifest can declare wants,
    // but only the workspace record's reference grants capabilities.
    capabilities: (ref && ref.capabilities) || null
  }
}

// Manifest `scopedKeys` → normalized entries; a malformed entry is dropped
// with a warn naming the module (never fatal — the rest of the contract holds).
const _normalizeScopedKeysWarn = (raw, id, field = 'scopedKeys') => {
  if (raw === undefined || raw === null) return []
  if (!Array.isArray(raw)) {
    console.warn('[workspaceModule] manifest ' + field + ' must be an array — ignored:', id)
    return []
  }
  const out = normalizeScopedKeys(raw)
  if (out.length !== raw.length) {
    console.warn(
      '[workspaceModule] manifest ' + field + ' — malformed entries dropped:',
      id,
      raw.length - out.length
    )
  }
  return out
}

// Manifest `hooks` → `{ onWorkspaceSwitch }` (function NAMES only — the shell
// resolves them through the module's injected functions at call time) or null.
const _normalizeHooks = (raw, id) => {
  if (raw === undefined || raw === null) return null
  if (typeof raw !== 'object') {
    console.warn('[workspaceModule] manifest hooks must be an object — ignored:', id)
    return null
  }
  const out = {}
  if (typeof raw.onWorkspaceSwitch === 'string' && raw.onWorkspaceSwitch.trim()) {
    out.onWorkspaceSwitch = raw.onWorkspaceSwitch.trim()
  } else if (raw.onWorkspaceSwitch !== undefined) {
    console.warn(
      '[workspaceModule] manifest hooks.onWorkspaceSwitch must name a module function — ignored:',
      id
    )
  }
  return Object.keys(out).length ? out : null
}

// `collections[]` → the rows the RECORD-ROUTE REGISTRY can merge, or null.
//
// A workspace RECORD (`ref:record:<collection>/<rowId>`, design §2) has no
// shell page: its only home is whatever module page owns its collection. The
// shell must not learn that mapping — it would have to name a module, and
// every new app would be a shell change. So the module declares it, on the
// SAME `collections` array that already declares its owned state keys:
//
//   collections: [{ key: 'vault.items', route: '/vault-rooms', param: 'item' }]
//
//   key    the collection name AS STORED. Folded to lower case here because
//          the ref grammar lower-cases exactly that segment (both the server's
//          minter and shared/functions/entityRef.js do), so a manifest that
//          spells it 'Vault.Items' must still claim 'vault.items' — otherwise
//          every citation of that collection silently resolves to nothing.
//   route  one of THIS module's own pages. A route the module does not serve
//          is dropped: the shell would navigate somewhere blank and wait for a
//          surface that is never coming.
//   param  the query key that page reads the row id from.
//
// A row short of all three is DROPPED rather than half-registered, exactly
// like `_normalizeOpens` drops a row without `ext` + `entry` — a typo must
// surface as "nothing opens this", never as a link that routes and then does
// nothing. First declaration of a key wins.
//
// Reader: functions/moduleRecordRoutes.js (the union across every installed
// module). Consumer: shared/functions/entityRef.js `entityRefHref`.
export const _normalizeRecordRoutes = (collections, pages, id) => {
  if (collections === undefined || collections === null) return null
  if (!Array.isArray(collections)) {
    console.warn('[workspaceModule] manifest collections must be an array — ignored:', id)
    return null
  }
  const out = []
  const seen = new Set()
  for (const c of collections) {
    if (!c || typeof c !== 'object') continue
    const key = c.key == null ? '' : String(c.key).trim().toLowerCase()
    const route = c.route == null ? '' : String(c.route).trim()
    const param = c.param == null ? '' : String(c.param).trim()
    // A collection with no route at all is the NORM — most declare state keys
    // only. Say nothing about those; warn only about a half-declaration,
    // which is the one a developer meant to work.
    if (!key || (!route && !param)) continue
    if (!route || !param || route.charAt(0) !== '/') {
      console.warn(
        '[workspaceModule] collection record route needs { key, route: "/path", param } — skipped:',
        id,
        c.key
      )
      continue
    }
    if (!pages || !pages[route]) {
      console.warn(
        '[workspaceModule] collection record route names a page this module does not serve — skipped:',
        id,
        route
      )
      continue
    }
    if (seen.has(key)) continue
    seen.add(key)
    out.push({ key, route, param })
  }
  return out.length ? out : null
}

// `collections[].row` → the RECORD-ROW REGISTRY rows, or null.
//
// Where the shell lists a collection's records OUTSIDE the module's own page —
// an AI answer's records block today — the module may name its OWN component
// for one row, so a deal reads like a deal (its stage, its money, its contact)
// instead of the shell's generic title + two facts:
//
//   collections: [{ key: 'crm_deals', route: '/crm', param: 'deal', row: 'CrmDealRecordRow' }]
//
//   key  the collection name AS STORED, folded to lower case (the same key the
//        record-route registry uses — one name for one collection).
//   row  a component the module SHIPS. A key check, not a read (reading would
//        revive a lazy entry at boot), exactly like a widget `body`: a name the
//        module does not ship is dropped with a warning, never mounted as an
//        unknown tag.
//
// THE MOUNT CONTRACT (functions/assistantRecords.js assistantRecordsRowMount):
// the shell mounts `{ extends: row }` under the module's OWN context
// (`root._moduleDsCtxGet(moduleId)`, the one a widget body mounts with), with a
// COPY of the module's globalScope plus
//   scope.record  the records-plane row { id, collection, name, data } — the
//                 answer's row: `name` its title, `data` its values (only the
//                 fields the answer asked for);
//   scope.fields  the answer's fields, [{ key, type, label? }], in order.
// The shell's list keeps the click (the row is drawn inside the shell's row
// button), so the row is read-only: no buttons, nothing draggable. THE
// FALLBACK is the shell's generic row, whenever no row is declared, the module
// context is not there yet, or the mount throws.
//
// `moduleId` rides each row because the module context is keyed by it. First
// declaration of a key wins, like the record routes. A collection without a
// `row` is the norm and says nothing.
export const _normalizeRecordRows = (collections, components, id) => {
  if (!Array.isArray(collections)) return null
  const out = []
  const seen = new Set()
  for (const c of collections) {
    if (!c || typeof c !== 'object' || c.row === undefined || c.row === null) continue
    const key = c.key == null ? '' : String(c.key).trim().toLowerCase()
    const row = typeof c.row === 'string' ? c.row.trim() : ''
    if (!key || !row) {
      console.warn(
        '[workspaceModule] collection record row needs { key, row: "<Component>" } — skipped:',
        id,
        c.key
      )
      continue
    }
    if (!components || !Object.prototype.hasOwnProperty.call(components, row)) {
      console.warn(
        '[workspaceModule] collection record row component not found — skipped:',
        id,
        row
      )
      continue
    }
    if (seen.has(key)) continue
    seen.add(key)
    out.push({ key, row, moduleId: id == null ? null : String(id) })
  }
  return out.length ? out : null
}

// `views[]` → the app's READ-ONLY VIEWS an AI answer may mount, or null
// (functions/assistantAppViews.js — the widget contract generalised:
// { id, title, describe, component, params, size, link, access }). A key
// check on `component`, not a read (like a widget body): a view naming a
// component the module does not ship is dropped with a warning. The rest of
// the entry is data the vocabulary reads; first id wins.
export const _normalizeViews = (views, components, id) => {
  if (!Array.isArray(views)) return null
  const out = []
  const seen = new Set()
  for (const v of views) {
    if (!v || typeof v !== 'object') continue
    const vid = String(v.id || '')
    if (!/^[a-z0-9-]{1,60}$/.test(vid)) {
      console.warn('[workspaceModule] view id must match /^[a-z0-9-]+$/ — skipped:', id, v.id)
      continue
    }
    const component = typeof v.component === 'string' ? v.component.trim() : ''
    if (!component || !components || !Object.prototype.hasOwnProperty.call(components, component)) {
      console.warn('[workspaceModule] view component not found — skipped:', id, vid, v.component)
      continue
    }
    if (seen.has(vid)) continue
    seen.add(vid)
    out.push(component === v.component ? v : { ...v, component })
  }
  return out.length ? out : null
}

// `forms[]` → the app's OWN FORMS an AI answer may open prefilled, or null
// (functions/assistantAppForms.js: { id, title, describe, fields, open,
// component?, access? }). `open` must name a function the module ships; a
// `component` (the sheet the shell's form host mounts) must be a component it
// ships — key checks, like a widget body. First id wins.
export const _normalizeForms = (forms, components, id, functions) => {
  if (!Array.isArray(forms)) return null
  const out = []
  const seen = new Set()
  for (const f of forms) {
    if (!f || typeof f !== 'object') continue
    const fid = String(f.id || '')
    if (!/^[a-z0-9-]{1,60}$/.test(fid)) {
      console.warn('[workspaceModule] form id must match /^[a-z0-9-]+$/ — skipped:', id, f.id)
      continue
    }
    if (typeof f.open !== 'string' || !f.open) {
      console.warn('[workspaceModule] form needs `open: "<function>"` — skipped:', id, fid)
      continue
    }
    if (functions && !Object.prototype.hasOwnProperty.call(functions, f.open)) {
      console.warn('[workspaceModule] form open function not found — skipped:', id, fid, f.open)
      continue
    }
    if (
      f.component != null &&
      (typeof f.component !== 'string' ||
        !components ||
        !Object.prototype.hasOwnProperty.call(components, f.component))
    ) {
      console.warn('[workspaceModule] form component not found — skipped:', id, fid, f.component)
      continue
    }
    if (seen.has(fid)) continue
    seen.add(fid)
    out.push(f)
  }
  return out.length ? out : null
}

// `opens[]` → the rows the /files handler registry can merge, or null.
//
// `route` defaults to the app's FIRST page rather than being required: an app
// with one page has nothing to disambiguate, and a route that does not exist on
// this app is dropped — the desktop would otherwise navigate somewhere blank
// and wait for a canvas that is never coming.
export const _normalizeOpens = (opens, pages) => {
  if (!Array.isArray(opens) || !opens.length) return null
  const routes = Object.keys(pages || {})
  const out = []
  for (const row of opens) {
    if (!row || typeof row !== 'object') continue
    const ext = (Array.isArray(row.ext) ? row.ext : [row.ext])
      .filter((e) => typeof e === 'string' && e.trim())
      // Stored bare and lowercase — the desktop matches on a bare extension
      // (fileExtOf), so a declared '.excalidraw' and 'EXCALIDRAW' both have to
      // land as 'excalidraw' or the row silently never matches.
      .map((e) => e.trim().replace(/^\./, '').toLowerCase())
    const entry = typeof row.entry === 'string' && row.entry.trim() ? row.entry.trim() : null
    if (!ext.length || !entry) continue
    const route = typeof row.route === 'string' && row.route ? row.route : routes[0]
    if (!route || (routes.length && routes.indexOf(route) < 0)) continue
    out.push({
      ext,
      entry,
      route,
      mime: typeof row.mime === 'string' ? row.mime : '',
      label: typeof row.label === 'string' ? row.label : '',
      icon: typeof row.icon === 'string' ? row.icon : ''
    })
  }
  return out.length ? out : null
}

// ── Loader discovery (AI workspace catalog) ──────────────────────────────────
// Modules commonly load their data IMPERATIVELY: a page onInit calls a module
// function (aihouse: `ahFetchTransactions`) that writes rows + status flags
// into root state (`ahTx`, `ahTxLoading`, `ahTxLoaded`, `ahTxError`). Nothing
// declares this — the manifest's stateKeys only cover records-plane
// collections, so the AI catalog couldn't see where the module's REAL data
// lives. Recover the mapping from the functions themselves: for each injected
// function whose name reads like a loader, statically parse its source for the
// state keys it writes and split data keys from status flags. Best-effort —
// a parse miss just means a thinner catalog hint, never an error.
const _LOADER_NAME_RE = /fetch|load|refresh/i
const _LOADER_FLAG_RE = /(Loading|Loaded|Error|Busy|Status)$/
const _discoverLoaders = (functions) => {
  const out = {}
  for (const [name, fn] of Object.entries(functions || {})) {
    if (typeof fn !== 'function' || !_LOADER_NAME_RE.test(name)) continue
    let src = ''
    try {
      src = String(fn)
    } catch (_) {
      continue
    }
    const keys = new Set()
    for (const call of src.matchAll(/update\(\s*\{([^}]*)/g)) {
      for (const key of call[1].matchAll(/(\w+)\s*:/g)) keys.add(key[1])
    }
    const writes = [...keys].filter((k) => !_LOADER_FLAG_RE.test(k))
    const flags = [...keys].filter((k) => _LOADER_FLAG_RE.test(k))
    if (writes.length) out[name] = { writes: writes.slice(0, 4), flags: flags.slice(0, 4) }
    if (Object.keys(out).length >= 16) break
  }
  return out
}

// ── Module resolution ─────────────────────────────────────────────────────────

// A published-module reference from the workspace settings bag:
// `settings.workspaceModule` (canonical) or an object-form `settings
// .projectModule` (transitional). Must be `{type:'project', owner, key, …}`.
const _publishedRef = (settings) => {
  const candidates = [settings && settings.workspaceModule, settings && settings.projectModule]
  for (const c of candidates) {
    if (c && typeof c === 'object' && c.type === 'project' && c.owner && c.key) return c
  }
  return null
}

// The additive workspace-app references from `settings.workspaceApps[]`. Each
// must be a `{type:'project', owner, key, …}` ref (same shape as workspaceModule
// but installMode:'app'). Deduped by `owner/key` (last wins) so a double-write
// can't inject the same app twice. A malformed entry is skipped, never fatal.
const _appId = (ref) => `${_lc(ref && ref.owner)}/${_lc(ref && ref.key)}`
// WS-SWITCH-MEMO-PLANE-1 — the `_injectedByApp` ledger key must carry the
// WORKSPACE; `_appId` alone is a GLOBAL catalog identity. Keyed by `owner/key`
// only, the same app installed in two workspaces owned ONE ledger entry, and
// both guards that read it then did the wrong thing on a switch: the teardown
// loop keeps it (it IS in the new workspace's desired list) and the injection
// gate skips it (`_injectedByApp.has` is already true), so the new workspace's
// `ctx.pages`/`routedEl.routes` never receive the app's pages — installed,
// listed, and blank until a full reload.
// Tenant-keying and NOT a teardown call, on purpose: once the key carries the
// workspace, the previous tenant's entries are simply absent from the new
// tenant's `desiredIds`, so the teardown loop that already exists collects them
// on its own. `_appId` keeps its catalog meaning for ref dedupe, the `_prefetched`
// map and the published `appId` field.
const _ledgerKey = (wsId, ref) => `${String(wsId || '')}::${_appId(ref)}`
// The installed-app entries — the shared reader in workspaceConfigs.js (moved
// from here; every consumer calls the same function).
const _appRefs = (settings) => installedAppRefs(settings)

// Deep equality for plain capability-config JSON — objects/arrays/primitives
// straight off the stored server ref, so no cycles and no functions to worry
// about. Used to tell an actual ai/files grant COLLISION (two apps shipping
// different configs, which one shared bridge cannot serve) apart from two apps
// declaring the SAME grant, which is no collision at all.
const _capConfigsEqual = (a, b) => {
  if (a === b) return true
  if (!a || !b || typeof a !== 'object' || typeof b !== 'object') return false
  if (Array.isArray(a) !== Array.isArray(b)) return false
  const ka = Object.keys(a)
  if (ka.length !== Object.keys(b).length) return false
  return ka.every((k) => _capConfigsEqual(a[k], b[k]))
}

// Merge two apps' `files` capability configs when compatible; null when they
// genuinely diverge. `mimeTypes` unions to the WIDEST grant — an absent list
// already means "the server's own allowlist", so if either side omits it the
// merge omits it too. Every OTHER field must be deep-equal: `visibility`
// changes where an upload lands and cannot be reconciled inside one shared
// bridge (serving app B public uploads under app A's private grant would leak,
// the reverse would break app A's serving).
export const _mergeFilesCaps = (a, b) => {
  const { mimeTypes: mimesA, ...restA } = a || {}
  const { mimeTypes: mimesB, ...restB } = b || {}
  if (!_capConfigsEqual(restA, restB)) return null
  if (!Array.isArray(mimesA) || !Array.isArray(mimesB)) return { ...restA }
  return { ...restA, mimeTypes: [...new Set([...mimesA, ...mimesB])] }
}

// Each published request must settle so a failed activation can retry instead
// of sharing one permanently pending flight.
//
// The budget is charged in TICKS, never by subtracting two wall-clock reads. A
// page has ONE javascript thread and every app on it shares that thread, so a
// wall-clock deadline bills app B for app A's synchronous payload revival.
// Measured 2026-09-23 on my.symbols.app/w/aihouse: `system/warehouse-app`
// logged "Workspace app request timed out" while the server answered that same
// data read in 0.3s (272 KB), because `aihouse/workspace-module` (736 revived
// function strings) held the thread past the 15s mark. The workspace then
// showed "Some apps couldn't load - 1 of 2" on every boot, and the converge
// loop paid five more full reload passes before it gave up.
//
// A tick that arrives LATE measures time in which the page could not run ANY
// javascript. The request never had that time, so the budget is charged at most
// one interval for it. A slow NETWORK leaves the thread free, its ticks land on
// schedule, and the budget drains exactly as it did before.
let _moduleRequestBudgetMs = 15000
export const __setModuleRequestBudgetForTests = (ms) => {
  _moduleRequestBudgetMs = ms
}

// One budget per app load, shared by that app's channel resolution and its
// payload fetch — the same single allowance the deadline gave them.
const _newRequestBudget = () => ({
  left: _moduleRequestBudgetMs,
  tick: Math.max(20, Math.min(500, Math.round(_moduleRequestBudgetMs / 8)))
})

// Every phase entry carries a stamp (`seq`), so an owner can settle ONLY the
// entry it wrote last. The root store wraps each nested write in a new proxy,
// so object identity cannot tell an owner "this is still mine"; a stamp can.
let _activationStampSeq = 0
// 'slow' is the reveal budget's voice — the page the person waits for is late —
// and never a final state. An SDK request carries no deadline of its own, so a
// read that never answers used to keep "Workspace is still loading" on screen
// for the rest of the session (dev 2026-10-30: stuck past 25 s while dev Redis
// was down; prod 2026-10-21: the Shaker workspace in the 503 incident). Past
// this ceiling the phase turns into the retryable failure. A late answer still
// lands and clears it, because the owner keeps its stamp.
let _activationCeilingMs = 30000
export const __setActivationCeilingForTests = (ms) => {
  _activationCeilingMs = ms
}
const _activationCeilingTimers = new WeakMap()

// The one write path: re-arm the phase's ceiling BEFORE the update, so a
// listener that writes the same phase during it keeps its own timer.
const _writeActivationPhase = (root, base, phase, entry) => {
  const timers = _activationCeilingTimers.get(root) || {}
  clearTimeout(timers[phase])
  timers[phase] = null
  if (entry.state === 'slow') {
    timers[phase] = setTimeout(() => {
      if (root._workspaceActivation?.[phase]?.state !== 'slow') return
      try {
        settleWorkspaceActivation(root, phase, entry.seq, { state: 'error', timedOut: true })
      } catch (_) {
        /* torn down while the ceiling waited */
      }
    }, _activationCeilingMs)
    timers[phase]?.unref?.()
  }
  _activationCeilingTimers.set(root, timers)
  root.update({ _workspaceActivation: { ...base, [phase]: entry } })
}

export const updateWorkspaceActivation = (root, phase, next) => {
  if (!root || typeof root.update !== 'function') return 0
  const workspaceId = String(root.workspace?.id || root.workspace?._id || '')
  const orgId = String(root.org?.id || root.org?._id || '')
  const previous = root._workspaceActivation
  const held = previous?.workspaceId === workspaceId && previous?.orgId === orgId ? previous : {}
  const seq = ++_activationStampSeq
  _writeActivationPhase(root, { ...held, workspaceId, orgId }, phase, { ...next, seq })
  return seq
}

// Compare-and-set: rewrites `phase` only while it still holds the entry
// stamped `seq`, inside the status that entry belongs to — never re-scoped to
// the workspace on screen now. The stamp is kept, so its owner can settle the
// same entry again (a late answer after the ceiling's failure). Returns
// whether it wrote.
export const settleWorkspaceActivation = (root, phase, seq, next) => {
  const status = root?._workspaceActivation
  if (!seq || !status || status[phase]?.seq !== seq || typeof root.update !== 'function') {
    return false
  }
  _writeActivationPhase(root, status, phase, { ...next, seq })
  return true
}

export const workspaceActivationView = (root) => {
  // THE WELCOME IS THE LAYER (R2c-m2, elena-r2c + sam-r2): while the new-account
  // welcome holds the screen (components/OnboardingWizard.js — opaque, the shell
  // inert and "simply not on screen"), the shell's loading strip waits. It sat
  // over the welcome's Continue, inert itself. The status stays on the root, so
  // a failure shows the moment the welcome closes. Both keys are read on every
  // run, so the view follows either.
  const welcomeOpen = !!root?._onboardingOpen
  const welcomeHold = !!root?._onboardingHold
  if (welcomeOpen || welcomeHold) return { visible: false, busy: false }
  const status = root?._workspaceActivation
  const workspaceId = String(root?.workspace?.id || root?.workspace?._id || '')
  const orgId = String(root?.org?.id || root?.org?._id || '')
  if (
    !status ||
    root.signedIn === false ||
    status.workspaceId !== workspaceId ||
    status.orgId !== orgId
  ) {
    return { visible: false, busy: false }
  }
  const phases = [status.core, status.reconcile, status.module, status.apps]
  // 'blocked' is a SETTLED failure: the payload arrived and the shell refused
  // it on content grounds (publish-time audit, sandboxed tier, a manifest it
  // cannot read, an extension the workspace no longer holds). It belongs in
  // `failed` because the user must be told, and OUT of every retry path
  // because no amount of retrying changes a content verdict.
  const failed = phases.some(
    (phase) => phase?.state === 'error' || phase?.state === 'partial' || phase?.state === 'blocked'
  )
  // A QUIET load is the hot-activation after the person's own install
  // (loadWorkspaceApps `{ quiet: true }`, BLD-28): it is no reason to show
  // "Retrying workspace loading". Its failure still shows (the settle writes
  // the outcome without `quiet`).
  const pending = phases.some((phase) => phase?.state === 'loading' && !phase.quiet)
  const retrying = status.core?.state === 'loading' && status.core?.visible === true
  const visible =
    failed ||
    phases.some((phase) => phase?.state === 'slow') ||
    retrying ||
    (status.core?.state === 'ready' && pending)
  const busy = retrying || (status.core?.state === 'ready' && pending)
  const appsState = status.apps?.state
  const variant =
    appsState === 'blocked' || appsState === 'partial'
      ? appsState
      : failed
        ? 'error'
        : busy
          ? 'retrying'
          : 'slow'
  const messages = {
    slow: { titleKey: 'workspace.activation.slowTitle', bodyKey: 'workspace.activation.slowBody' },
    error: {
      titleKey: 'workspace.activation.errorTitle',
      bodyKey: 'workspace.activation.errorBody'
    },
    partial: {
      titleKey: 'workspace.activation.partialTitle',
      bodyKey: 'workspace.activation.partialBody'
    },
    blocked: {
      titleKey: 'workspace.activation.blockedTitle',
      bodyKey: 'workspace.activation.blockedBody'
    },
    retrying: {
      titleKey: 'workspace.activation.retryingTitle',
      bodyKey: 'workspace.activation.retryingBody'
    }
  }
  return {
    visible,
    busy,
    // The Retry button exists only where a retry can change the answer. On a
    // blocked verdict it promised an outcome it could never deliver, and the
    // converge loop behind it paid five full reload passes to learn the same
    // thing five times.
    retryable: variant !== 'blocked',
    ...messages[variant],
    values: {
      loaded: status.apps?.loaded || 0,
      total: status.apps?.total || 0,
      blocked: Array.isArray(status.apps?.blocked) ? status.apps.blocked.join(', ') : ''
    }
  }
}

const _waitForModuleRequest = async (work, budget = _newRequestBudget()) => {
  let timer
  try {
    return await Promise.race([
      work,
      new Promise((resolve, reject) => {
        // A timer never fires before its delay, so a tick charges exactly the
        // delay it was armed with — at most one interval, however late it
        // lands. That is the whole mechanism: a blocked thread delays the
        // tick, it does not inflate the charge. No clock is read.
        const arm = () => {
          const delay = Math.min(budget.tick, budget.left)
          timer = setTimeout(() => {
            budget.left -= delay
            if (budget.left > 0) return arm()
            const error = new Error('Workspace app request timed out')
            error.code = 'WORKSPACE_MODULE_TIMEOUT'
            reject(error)
          }, delay)
        }
        arm()
      })
    ])
  } finally {
    clearTimeout(timer)
  }
}

// Per-app boot spans (boot fix step 0) — `wsboot:app:<key>:<phase>` measures
// with an explicit start, so a synchronous phase reads as its own main-thread
// time even in a background tab that delays every timer around it. Phases:
// channel (env resolution), cache (warm-slot read + revive), fetch (payload
// read; the SDK revives inside it), build (audit + normalize), wait (the
// serial loop waiting on the prefetch), inject (components + functions),
// wrap (module DS context + state root + page wrap), routes (page + route
// table registration). The pass adds `wsboot:app:module-apps:publish` around
// the one surface publish. Telemetry only — never throws, never gates.
const _spanStart = () =>
  typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : 0
const _span = (key, phase, start) => {
  try {
    if (typeof performance === 'undefined' || typeof performance.measure !== 'function') return
    performance.measure('wsboot:app:' + key + ':' + phase, { start, end: performance.now() })
  } catch (_) {
    /* telemetry only */
  }
}

// Fetch + normalize a published module by reference. Channel resolves to a
// {branch, version} pair via the project's env publish config unless the ref
// pins a version. Memoized per owner/key@version (dev override skips the memo
// so the authoring loop always sees fresh data).
const _loadPublishedModule = async (ref, sdk, wsId, opts = {}) => {
  const budget = _newRequestBudget()
  // Scoped to THIS workspace — see _devModuleOverride: an override pinned to a
  // different workspace (or not pinned at all) resolves null here, so the
  // spread below can no longer hijack another tenant's module ref.
  // `devOverride: false` (the catalog-apps pipeline) keeps the override OFF
  // while still carrying wsId for the SERVER's workspace-module grant —
  // dropping the id entirely made every catalog-app read arrive with no
  // workspaceId, which the grant middleware refuses outright
  // ("Required roles: guest, editor, admin, owner" on system/crm-app +
  // system/excalidraw for every white-label member, measured 2026-08-31).
  // `channelRole` — 'prefetch' | 'takeover' | undefined (an app): who offers
  // and who may take a channel handoff (see _channelHandoff).
  const { devOverride = true, channelRole = null } = opts
  const dev = devOverride ? _devModuleOverride(wsId) : null
  const eff = dev ? { ...ref, ...dev } : ref
  // A content refusal is recorded in the module-level ledger under the app's
  // id, not in a per-call sink: `_moduleInflight` hands a second concurrent
  // caller the FIRST caller's promise, so a sink that lived in that caller's
  // closure was invisible to everyone else. The ledger is also what the
  // takeover loader reads through `_lastRefusal`, so each refusal site is one
  // call. A successful load clears the entry.
  const appId = `${eff.owner}/${eff.key}` // eff, not ref: a dev override may arrive with ref === null
  const _refuse = (code, message) => {
    _refusals.set(appId, { code, message })
    _lastRefusal = message
    if (code === 'manifest') _lastNullDefinitive = true
    return null
  }

  // Tier enforcement — `sandboxed` has no runtime isolation yet (the server
  // clamps cross-org, non-catalog refs to it; see WORKSPACE_APPS.md "Trust
  // model"). Until a real sandbox execution tier ships, refuse instead of
  // silently running third-party code in-realm. The dev override is exempt —
  // it's the authoring loop on dev builds / *.localhost only.
  if (!dev && eff.tier === 'sandboxed') {
    console.warn(
      '[workspaceModule] sandboxed tier is not executable yet — refusing to load:',
      `${eff.owner}/${eff.key}`
    )
    return _refuse('tier', 'This app is pending trust review and can’t run in your workspace yet.')
  }

  let branch = eff.branch || 'main'
  let version = eff.version || null
  if (!version && eff.channel) {
    try {
      // MODULE-CHANNEL-PIN-1: thread the in-scope workspace id through so the
      // channel-resolution reads (getProjectByKey/listEnvironments inside
      // resolveEnvPublishConfig) can use the workspace-module read grant.
      // Without it every non-admin member 403s on both reads and silently
      // falls back to 'latest' below — the pin only ever applies to admins.
      // MODULE-CHANNEL-PIN-1: the shared/** sdk.js side now forwards
      // `workspaceId` into the two SDK calls too — all three parts landed.
      const tChannel = _spanStart()
      // The apps pass may already have started this exact read (the channel
      // warm-up in _loadWorkspaceAppsPass) - take that promise, never a
      // second request.
      const _warmKey = _channelHandoffKey(eff.owner, eff.key, eff.channel, wsId)
      // A handed-over resolution (boot prefetch → takeover → apps) first.
      // Never under a dev override: the authoring loop always re-resolves.
      const _handed = dev ? null : _takeChannelHandoff(_warmKey, _channelAccept(channelRole))
      const _warmHit = _handed
        ? null
        : globalThis.__wsEnvWarm && globalThis.__wsEnvWarm.get(_warmKey)
      if (_warmHit) globalThis.__wsEnvWarm.delete(_warmKey)
      const _resolveChannel = () =>
        _pipeline.resolveEnv({ owner: eff.owner, key: eff.key }, eff.channel, {
          workspaceId: wsId
        })
      const _cfgFlight = Promise.resolve(
        _handed || (_warmHit && Date.now() - _warmHit.at < 30000 ? _warmHit.p : _resolveChannel())
      )
      // Offer the SAME flight (in flight or settled) to the next consumer.
      if (!dev && (channelRole === 'prefetch' || channelRole === 'takeover')) {
        _cfgFlight.catch(() => {})
        _channelHandoff.set(_warmKey, { p: _cfgFlight, from: channelRole, at: Date.now() })
      }
      let cfg
      try {
        cfg = await _waitForModuleRequest(_cfgFlight, budget)
      } catch (handedErr) {
        // A handed-over flight that failed is the GIVER's failure: resolve
        // once on our own, exactly as before the handoff existed.
        if (!_handed) throw handedErr
        cfg = await _waitForModuleRequest(_resolveChannel(), budget)
      }
      _span(eff.key, 'channel', tChannel)
      if (cfg) {
        branch = cfg.branch || branch
        if (cfg.version && cfg.version !== 'latest') version = cfg.version
      }
    } catch (e) {
      console.warn(
        '[workspaceModule] channel resolution failed — falling back to latest:',
        e && e.message
      )
    }
  }

  // WS-SWITCH-MEMO-PLANE-1 — the BRANCH is in the key. A channel resolves to
  // `{branch, version:'latest'}` for every non-pinned env, so two workspaces
  // on different channels of the SAME project both read `owner/key@latest`
  // and the second one was served the first one's branch build until a full
  // reload. Put the dimension in the key; never a teardown call.
  const memoKey = `${eff.owner}/${eff.key}@${branch || 'main'}#${version || 'latest'}`
  if (!dev && _moduleMemo.has(memoKey)) return _moduleMemo.get(memoKey)
  // In-flight dedup — the memo is only SET after the fetch+process below, so the
  // dual-trigger's two concurrent onWorkspaceChange runs would otherwise both
  // fetch the SAME module project. Share one load; the resolved module still
  // lands in _moduleMemo, so post-load reads keep hitting the fast path.
  //
  // EVERY caller owns its own deadline (review D6, perf/shell-boot): the
  // shared flight carries no timer of its own, each caller races it against
  // its OWN budget. Before, a joiner got the creator's deadlined promise — a
  // slow boot PREFETCH failed the takeover pass that joined it later on the
  // prefetch's timer. The build still drops a late payload nobody waits for:
  // its guard reads how many callers are still waiting, and when the last one
  // gives up the flight leaves the map, so a retry fetches fresh.
  if (!dev && _moduleInflight.has(memoKey))
    return _awaitModuleFlight(_moduleInflight.get(memoKey), budget, memoKey)
  // The creator counts as waiting BEFORE the build starts: a warm-slot hit
  // reaches the build's guard synchronously.
  const flight = { waiting: 1, work: null }
  const stillWanted = {
    get left() {
      return flight.waiting > 0 ? 1 : 0
    }
  }
  flight.work = _fetchAndBuildModule(
    eff,
    branch,
    version,
    dev,
    memoKey,
    wsId,
    stillWanted,
    _refuse,
    !dev && Array.isArray(opts.lazyParts) && opts.lazyParts.length ? opts.lazyParts : null
  ).then((mod) => {
    if (mod) _refusals.delete(appId)
    if (mod && !dev) _moduleMemo.set(memoKey, mod)
    return mod
  })
  const clear = () => {
    if (_moduleInflight.get(memoKey) === flight) _moduleInflight.delete(memoKey)
  }
  flight.work.then(clear, clear)
  if (!dev) _moduleInflight.set(memoKey, flight)
  return _awaitModuleFlight(flight, budget, memoKey, true)
}

// Wait for a shared module flight under THIS caller's budget (see above).
const _awaitModuleFlight = async (flight, budget, memoKey, counted = false) => {
  if (!counted) flight.waiting++
  try {
    return await _waitForModuleRequest(flight.work, budget)
  } catch (err) {
    flight.waiting--
    if (flight.waiting <= 0 && _moduleInflight.get(memoKey) === flight) {
      _moduleInflight.delete(memoKey)
    }
    throw err
  }
}

// WS-MODULE-LIB-FN-ONE-BINDING-1 — one shared library's function is ONE binding
// across every module that links the library.
//
// The server ships each module payload its OWN copy of every linked library
// (`sharedLibraries[].functions`, as text — ProjectDataService). Each payload
// revives on its own (_reviveModulePayload below), so two apps that link one
// library got two function objects for each library function.
// _mergeModuleLibraries folds each copy into its app's functions by
// reference, and the root inject (_injectEntries, same-binding rule) read the
// second copy as a collision: dev.my /w/bessi/orders-qa logged 27 `key
// collision skipped` lines per boot, one per business-kit function, because
// orders-app and production-app both link the kit (108 per four-page walk).
//
// The root bag must keep library functions: shell code runs app code with a
// shell element (an approved AI action runs on the shell card, moduleActions;
// the AI transport calls `el.call(fn)`), and the app code there finds its
// library peers through `this.call` in the root bag.
//
// The rule: a revived function has no closure (the revive is a global eval),
// so one text of one library is one behaviour. The first function revived
// from a library function's text becomes its binding, and every later payload
// that ships the SAME library (by id) with the SAME text reuses it — at the
// same version, or at a version that left that function unchanged. Two
// bindings, and the warn, stay for:
//   - a function the versions changed (another text): each app's module
//     scope keeps its own version, the root bag keeps the first app's;
//   - one name in two different libraries (another id);
//   - an app's OWN function (only `sharedLibraries` text is ever bound);
//   - a value that arrived as a real function (a local source): it may hold a
//     closure, so it is never shared.
// Session lifetime, like _moduleMemo, which keeps the revived modules anyway.
// (moduleLibrarySameBindingNoCollision.test.js)
const _libraryFnTexts = new Map() // `${libraryId}\n${name}` → Map(text → function)

// A library entry's identity: the record id the server ships (`id`, `_id`).
// An entry with no id has no identity and is never bound.
const _libraryIdOf = (lib) => {
  if (!lib || typeof lib !== 'object') return null
  const id = lib.id != null ? lib.id : lib._id
  return id == null || id === '' ? null : String(id)
}

// Bind each library function of a freshly revived payload to the first
// function revived from the same text of the same library. `raw` is the
// payload as fetched (text); `revived` is the revive walk's new copy of it.
// Only the copy's own function bags are written, never the raw payload (the
// warm cache slot keeps it).
const _bindLibraryFnTexts = (raw, revived) => {
  const rawLibs = raw && Array.isArray(raw.sharedLibraries) ? raw.sharedLibraries : null
  const libs = revived && Array.isArray(revived.sharedLibraries) ? revived.sharedLibraries : null
  if (!rawLibs || !libs) return
  for (let i = 0; i < rawLibs.length; i++) {
    const rawLib = isWrappedLibrary(rawLibs[i]) ? rawLibs[i].library : rawLibs[i]
    const lib = isWrappedLibrary(libs[i]) ? libs[i].library : libs[i]
    const libId = _libraryIdOf(rawLib)
    const texts = rawLib && rawLib.functions
    const fns = lib && lib.functions
    if (!libId || !texts || typeof texts !== 'object' || !fns || typeof fns !== 'object') continue
    for (const name of Object.keys(texts)) {
      const text = texts[name]
      if (typeof text !== 'string' || !Object.prototype.hasOwnProperty.call(fns, name)) continue
      if (typeof fns[name] !== 'function') continue
      const slot = libId + '\n' + name
      let held = _libraryFnTexts.get(slot)
      if (!held) _libraryFnTexts.set(slot, (held = new Map()))
      const first = held.get(text)
      if (first) fns[name] = first
      else held.set(text, fns[name])
    }
  }
}

// Same closure-eval revival `fetchProjectFromSDK` gives `globalScope` at the
// SDK seam (FW-GLOBALSCOPE-HELPER-CLOSURE-LOST-IN-PAYLOAD-REVIVE-1) — needed
// again here because these two paths (payload-cache hit, string-response
// fallback) run their OWN deepDestringifyFunctions pass on a still-raw
// payload the SDK layer never touched. Capture `globalScope` before the
// generic walk, revive it with the platform resolver after.
//
// `deferRevive` (boot fix step 4) names top-level parts to leave RAW — the same
// rule the SDK seam applies (shared sdk.js `_reviveProjectPayload`), so the
// cache-hit and string paths match the fetch path.
export const _reviveModulePayload = (data, deferRevive) => {
  const rawGlobalScope = data && typeof data === 'object' ? data.globalScope : null
  const defer =
    Array.isArray(deferRevive) && data && typeof data === 'object'
      ? deferRevive.filter((part) => Object.prototype.hasOwnProperty.call(data, part))
      : []
  let revived
  if (defer.length) {
    const eager = {}
    for (const part of Object.keys(data)) if (!defer.includes(part)) eager[part] = data[part]
    revived = deepDestringifyFunctions(eager)
    for (const part of defer) revived[part] = data[part]
  } else {
    revived = deepDestringifyFunctions(data)
  }
  // One library function text, one binding (WS-MODULE-LIB-FN-ONE-BINDING-1).
  _bindLibraryFnTexts(data, revived)
  if (rawGlobalScope && typeof rawGlobalScope === 'object' && revived) {
    revived.globalScope = destringifyGlobalScope(rawGlobalScope)
  }
  return revived
}

// Per-key lazy revive (boot fix step 4, design-d2 rules). Every key of a RAW
// (still frank-serialized) payload part becomes an enumerable, configurable
// accessor that revives THAT entry on its first read and then replaces itself
// with a plain data property. A reader always gets the real value — never a
// stub around a function — and every later read gets the same object. The
// accessor is shared by descriptor copies (module scope, root bag): a read
// through any copy revives once and settles the source and that copy. Keys
// enumerate without a read; `delete` and assignment behave as on a data bag.
export const _lazyRevivedEntries = (raw) => {
  const out = {}
  if (!raw || typeof raw !== 'object') return out
  for (const key of Object.keys(raw)) {
    const cell = { done: false, value: undefined }
    const settle = (target, value) => {
      try {
        Object.defineProperty(target, key, {
          value,
          writable: true,
          enumerable: true,
          configurable: true
        })
      } catch (_) {
        /* a frozen copy keeps its accessor; it still returns the one value */
      }
    }
    const get = function () {
      if (!cell.done) {
        cell.value = deepDestringifyFunctions({ [key]: raw[key] })[key]
        cell.done = true
      }
      const d = Object.getOwnPropertyDescriptor(out, key)
      if (d && d.get === get) settle(out, cell.value)
      if (this && this !== out && typeof this === 'object') {
        const own = Object.getOwnPropertyDescriptor(this, key)
        if (own && own.get === get) settle(this, cell.value)
      }
      return cell.value
    }
    const set = function (value) {
      settle(this && typeof this === 'object' ? this : out, value)
    }
    Object.defineProperty(out, key, { get, set, enumerable: true, configurable: true })
  }
  return out
}

// Copy every own property as a DESCRIPTOR — a lazy entry stays lazy in the
// copy. A spread or Object.assign would read (and revive) every entry.
const _copyEntries = (src, target = {}) => {
  if (!src || typeof src !== 'object') return target
  return Object.defineProperties(target, Object.getOwnPropertyDescriptors(src))
}

// Fetch + parse + audit-gate + normalize a published module. Extracted from
// _loadPublishedModule so the in-flight promise can be memoized for dedup.
const _fetchAndBuildModule = async (
  eff,
  branch,
  version,
  dev,
  memoKey,
  wsId,
  budget,
  refuse,
  lazyParts = null
) => {
  // WS-MODULE-PAYLOAD-CACHE-1 — warm-slot fast path, 'latest' requests only
  // (pinned versions are immutable admin-channel flows; they keep the direct
  // fetch). On a hit the module mounts from the slot with no payload wait,
  // and ONE background fetch (write-through) refreshes the slot — every boot
  // still costs exactly one payload fetch server-side, it just stops gating
  // the reveal. On a miss the direct fetch also writes the slot.
  // Dev-only local source (localWorkspaceApps.js): an opted-in app builds its
  // payload from apps/* — no cache slot, no fetch, nothing to revive.
  const local = localWorkspaceAppLoader(eff.owner, eff.key)
  if (local) lazyParts = null
  const cacheable = !local && !dev && !!wsId && (!version || version === 'latest')
  // Security review N3: every remote path below yields the payload RAW (not
  // one function string evaluated). The publish-time audit is read on the
  // raw data and a refused module is dropped BEFORE any revive — the revive
  // evaluates `(' + str + ')'`, so a revive-then-check ran a refused module's
  // side effects in the shell. Only an admitted payload is revived (below).
  let data = local ? await local() : null
  if (cacheable) {
    const tCache = _spanStart()
    const hit = _payloadCacheRead(eff.owner, eff.key, branch, wsId)
    if (hit) {
      _payloadSlotTouch(wsId)
      data = hit.data
      _span(eff.key, 'cache', tCache)
      _pipeline
        .fetchProject(eff.owner, eff.key, {
          branch,
          version: 'latest',
          workspaceId: wsId,
          payloadCacheKey: _payloadSlotKey(wsId),
          // N3: only the slot write and the version are needed — never
          // revive a payload no audit gate has read (`raw` is the shared
          // fetchProjectFromSDK option; `revive: false` is main's name for
          // the same intent — both kept so neither seam revives)
          raw: true,
          revive: false
        })
        .then((fresh) => {
          const fv = fresh && (fresh.version || fresh.latestVersion)
          if (fv && hit.version && fv !== hit.version) {
            console.info(
              '[workspaceModule] cached payload superseded in background (' +
                hit.version +
                ' → ' +
                fv +
                ') — the fresh slot serves on the next load'
            )
          }
        })
        .catch(() => {})
        // the revalidation wrote (or failed to write) the slot: index it now
        .finally(() => _payloadSlotTouch(wsId))
    }
  }
  if (!data) {
    const tFetch = _spanStart()
    try {
      data = await _pipeline.fetchProject(eff.owner, eff.key, {
        branch,
        version: version || 'latest',
        // Workspace-module read grant (GA onboarding): the member's workspace id
        // authorizes the bundle read server-side even without a project-level
        // role — without it, every invited (non-org-admin) member 403'd here and
        // the module never loaded.
        workspaceId: wsId,
        ...(cacheable ? { payloadCacheKey: _payloadSlotKey(wsId) } : {}),
        // N3: raw — the audit gate below reads it before anything revives
        raw: true
      })
    } finally {
      // AFTER the SDK's write-through (review Delta 2 N7a)
      if (cacheable) _payloadSlotTouch(wsId)
    }
    _span(eff.key, 'fetch', tFetch)
  }
  // A timed-out request has relinquished its flight. Do not revive or audit
  // its late payload: refusal flags would otherwise contaminate an active
  // retry, and processing unused module code adds avoidable main-thread work.
  if (budget.left <= 0) return null
  const tBuild = _spanStart()
  if (typeof data === 'string') {
    // A string payload: parse only (N3 — revive after the audit gate).
    try {
      data = JSON.parse(data)
    } catch (e) {
      console.warn('[workspaceModule] module payload unparsable:', e && e.message)
      return null
    }
  }
  if (!data) return null

  // Publish-time module audit stamp — the server's static scan of the
  // published fn strings rides the fetched project data as top-level
  // `moduleAudit` ({ok, findings, scannedFnStrings, at}). A version that
  // FAILED the audit is refused (fail to plain workspace). A MISSING stamp is
  // allowed with a warn — tolerant-by-absence in v1, so pre-audit publishes
  // and the dev-override authoring loop keep working.
  const audit = data.moduleAudit
  if (audit && audit.ok === false) {
    console.warn(
      '[workspaceModule] module failed the publish-time audit — refusing to load:',
      `${eff.owner}/${eff.key}`,
      `${(Array.isArray(audit.findings) && audit.findings.length) || 0} finding(s)`
    )
    return refuse('audit', 'This app failed its publish-time security check and was not loaded.')
  }
  if (!audit) {
    console.warn(
      '[workspaceModule] no publish-time audit stamp (pre-audit publish) — loading anyway:',
      `${eff.owner}/${eff.key}`
    )
  }
  // Admitted: revive now (a local dev source arrives as real code). Deferred
  // parts stay RAW here; each entry revives on its first read (boot fix
  // step 4).
  if (!local) data = _reviveModulePayload(data, lazyParts)
  if (lazyParts && !local) {
    for (const part of lazyParts) {
      if (data[part] && typeof data[part] === 'object') data[part] = _lazyRevivedEntries(data[part])
    }
  }

  const mod = _normalizeManifest(data, eff)
  _span(eff.key, 'build', tBuild)
  // The payload ARRIVED and normalization rejected it (no manifest / id
  // mismatch) — a content verdict, not a transient failure: retrying the
  // fetch returns the same payload, so the converge loop must not spin on it.
  if (!mod) refuse('manifest', 'This app’s published manifest could not be read.')
  return mod
}

// Resolve the workspace settings to an async module loader (or null): the
// published-project pipeline (manifest v2) is the ONLY plane — the legacy
// `settings.projectModule` string path (v1 bundled chunk) was retired with
// the WS6 GreenIG migration (2026-07-30); a leftover string on a record is
// ignored (the field may linger harmlessly).
export const _resolveModuleLoader = (settings, sdk, wsId) => {
  const ref = _publishedRef(settings)
  // A dev override drives a load even when the server names no module — it
  // carries its own {owner,key}, and _loadPublishedModule merges it over `ref`
  // (null included). Route to the published pipeline when EITHER is present.
  //
  // `wsId` scopes that override to the workspace it was pinned to. Without it
  // this branch forced a foreign module into EVERY workspace the server said
  // had none — the more severe half of the Bellforge/AI House mashup, since it
  // fabricated a module where the tenant had configured nothing at all.
  if (ref || _devModuleOverride(wsId)) {
    return () => _loadPublishedModule(ref, sdk, wsId, { channelRole: 'takeover' })
  }
  return null
}

// BOOT PREFETCH (perf/shell-boot, 2026-10-09) — start the takeover module's
// channel resolution + payload read the moment the workspace RECORD is known,
// instead of after the shell has mounted and the workspace turned
// authoritative. Measured live on staging /w/vault/default: /core/boot (whose
// `workspace` section is the full record, settings.workspaceModule included)
// answered at ~4.0 s, yet the first module read went out at 9.6 s and the
// payload at 13.5 s — the chain waited for the mount and the authority poll,
// with no data dependency on either.
//
// FETCH ONLY. Nothing is injected, no root/state is touched: the result lands
// in the module memo / in-flight map and the channel handoff, and
// loadWorkspaceProject stays the single place that decides (org guard,
// settings provenance, refusal surfacing) and injects. A wrong guess costs one
// read, never a wrong module on screen. The caller (index.js boot hook) only
// asks for the record the boot itself resolved for this tab.
//
// Returns the flight (resolves to the module or null), or null when nothing
// was started: no record for this id, no published module ref, a dev override
// (the authoring loop keeps its own path), or this workspace already
// prefetched on this page.
export const prefetchWorkspaceModule = (sdk, wsId, record) => {
  if (!wsId || !record || typeof record !== 'object') return null
  if (String(record._id || record.id || '') !== String(wsId)) return null
  if (_bootPrefetchWsId === String(wsId)) return null
  const ref = _publishedRef(record.settings)
  if (!ref || _devModuleOverride(wsId)) return null
  _bootPrefetchWsId = String(wsId)
  return _loadPublishedModule(ref, sdk, wsId, { channelRole: 'prefetch' }).catch(() => null)
}

// ── Module design-system threading ──────────────────────────────────────────
//
// A workspace module ships its own designSystem tokens (the bellforge desk
// palette, a tenant brand, …). The shell's scratch config knows nothing about
// those names, and css-in-props resolves color props against the ACTIVE
// config — so without threading, a module token either passes through as raw
// CSS (a name that happens to be a CSS keyword, e.g. "navy" → rgb(0,0,128))
// or silently drops (everything else), and the module renders broken ONLY
// in-shell (2026-08-11 bellforge desk).
//
// Fix = the framework's own secondary-in-document recipe (smbls prepare.js):
// an ISOLATED createConfig(cleanBase) processed via init() while pushed, then
// handed to the module's page subtrees via `definition.context` — element
// create honors it (element/src/create.js: definition.context || parent
// context) and css-in-props pushes context.designSystem per element, so the
// whole page subtree resolves tokens against the module palette. The atomic
// engine keys classes on RESOLVED values, so shell/module classes can never
// collide in the shared sheet. Object.create(ctx) keeps every other context
// read (components/functions/pages/state) falling through live to the shell.
//
// Document-level side effects are all disabled: no reset, no font import, no
// document-theme stamping, no CSS vars (useVariable:false — the config's own
// token tables resolve to literal values exactly like a standalone bundle
// with vars off). Failure is soft: the module loads with shell-resolved
// tokens, same as before this feature.
// Prewarmed at module evaluation: these dynamic chunks otherwise load INSIDE
// the workspace-activation pass, and a cold parcel dev chunk fetch there blew
// the shell's 10s bounded onWorkspaceChange window ("[shell] onWorkspaceChange
// exceeded its bounded window") on every bellforge activation. Kicking the
// import at module load makes the await in _ensureModuleDsContext a cache hit.
// ONE import, the 'smbls' umbrella: the published smbls dist rolls scratch
// INTO its bundle, so importing createConfig/pushConfig from
// '@symbo.ls/scratch' yields a SECOND scratch instance whose config stack
// the bundled css engine never reads — the threading then silently no-ops
// on deployed builds while working against local source symlinks (observed
// on dev.my 2026-08-11: defs wrapped, config raw/unprocessed, tokens
// passthrough). smbls re-exports the primitives from its own instance.
let _dsDepsPromise = null
// Plain `if`-assignment, not `||=`: frank classifies a module-level `let` as
// MUTABLE (and lifts it into the emitted state bag) only when it sees a real
// assignment to it; a logical-assignment operator was not recognised and the
// binding was dropped from the published payload.
const _dsDeps = () => {
  if (!_dsDepsPromise) _dsDepsPromise = import('smbls')
  return _dsDepsPromise
}
// Prewarm the chunk WITHOUT seeding the memo. frank evaluates this module
// during serialization, and a pending Promise parked on `_dsDepsPromise`
// there is not a serializable value — frank then drops the binding and the
// published `_dsDeps` read a name that does not exist at runtime
// (WORKSPACE-SHELL-FRANK-GATE-BLOCKS-REPUBLISH-1). A bare dynamic import
// warms the very same module-cache entry, so the `await` inside
// `_ensureModuleDsContext` still hits a resolved chunk.
import('smbls').catch(() => {}) // prewarm; failures re-surface at the real await

// ── Per-module resolution scope (WS-MODULE-FN-NAME-COLLISION-CROSS-APP) ─────
//
// THE DEFECT, measured on my.symbols.app/w/natali with 5 apps installed (crm,
// scheduling, commerce, warehouse, bi): crm-app and scheduling-app BOTH define
// `natEnsureLoaded`. Every module used to register its functions and components
// into the ONE flat `ctx.functions` / `ctx.components`, first-writer-wins, so
// the console logged `[workspaceModule] key collision skipped: natEnsureLoaded`
// (and natCustomerById / natCustomerRows / natProcedureById) and scheduling's
// pages then resolved `el.call('natEnsureLoaded')` to CRM's loader. Its own
// `natFetchAll` never ran, natSalons / natProcedures / natProfessionals stayed
// undefined, four `effect error: reading 'find'` followed, the Salons list was
// empty and the header stuck on "Loading…". Independently-authored apps WILL
// pick the same names, so a flat registry is the defect — not the app.
//
// THE SCOPE. Each module gets ONE context object of its own, prototype-chained
// to the shell context, with its own components and functions as OWN
// properties:
//
//   modCtx           = Object.create(ctx)
//   modCtx.functions = Object.create(ctx.functions) + the module's own
//   modCtx.components= Object.create(ctx.components) + the module's own
//
// Resolution then reads "module first, then root" for free, because both
// resolvers are PLAIN PROPERTY READS and therefore already walk a prototype
// chain: `el.call` is `ctx?.functions?.[name]` (smbls packages/element
// src/methods.js `call`), and bare component keys are `components[prop] ||
// components['smbls.'+prop]` (src/create.js, src/extends.js). No framework
// change, no per-call routing table, and no copy: the chain stays LIVE, so
// bridges and shell keys injected into the root bags after a module loads are
// still visible through it.
//
// Every module page def carries this context (see _wrapModulePages), and DOMQL
// hands an element's context to its whole subtree, so a module's pages, its
// widget bodies and everything they mount resolve inside the module's own
// scope. The root bags keep receiving the module's keys exactly as before —
// shell surfaces, the merged approval-action registry and `root._moduleFnGet`
// all read root — so this is additive: it can only CHANGE the resolution of a
// name that two modules both define, which is precisely the broken case.
//
// STATE KEYS ARE SCOPED BY WS-MODULE-STATE-SCOPE-1, the data-plane half of the
// same defect: `natCustomersLoaded` (crm-app) and `natLoading` / `natSalons`
// (scheduling-app) used to land on the ONE reactive root store and collide the
// same way. State is not resolved through `context`, so it needs its own
// carrier: each module gets a module-local reactive store
// (`_moduleStateFor` below, built on the engine's own child-store-with-parent-
// fallback — see functions/moduleStateScope.js for the full contract), and
// every def that already carries `modCtx` also carries that store as its
// `s.root`. Reads still fall back to the shell root; writes land module-locally
// unless the key is shell-owned or the module opted in. Diagnosability from the
// function-plane pass stays: a cross-module key collision names BOTH module ids
// (see _injectEntries).
const _moduleScopeCache = new Map() // project.id → { ctx, modCtx, wrappedPages }

// The module's own function bag as it should appear INSIDE its scope. The
// caller passes the same bag it injects into root (module functions plus, for
// a takeover, its capability bridges) so the two planes can never disagree.
export const _moduleScopeFor = (ctx, project, ownFns) => {
  if (!ctx || !project || project.id == null) return null
  const hit = _moduleScopeCache.get(project.id)
  if (hit && hit.ctx === ctx) {
    // A later pass may know more of the module's own functions than the pass
    // that created the scope (bridges are built after the components inject).
    if (ownFns) for (const [k, v] of Object.entries(ownFns)) hit.modCtx.functions[k] = v
    // Same for the scope root: a later pass may carry a project object that
    // knows more of the module's own globalScope than the pass that minted the
    // scope. Own keys only — the prototype stays the shell's live bag.
    if (project.globalScope) {
      for (const [k, v] of Object.entries(project.globalScope)) hit.modCtx.globalScope[k] = v
    }
    // Same for the declared config: a later pass may carry the manifest's
    // configDefaults where the pass that minted the scope did not.
    if (project.configDefaults && typeof project.configDefaults === 'object') {
      hit.modCtx.configDefaults = project.configDefaults
    }
    return hit.modCtx
  }
  const modCtx = Object.create(ctx)
  // Own-property overlays over LIVE prototypes — not copies. `ctx.components`
  // / `ctx.functions` keep being mutated after this point (other modules, the
  // shared capability bridges, the approval trio); the chain must see all of
  // it.
  // Descriptors, not values: a lazy entry stays lazy in the scope.
  modCtx.components = _copyEntries(
    project.components || {},
    Object.create(ctx.components || Object.prototype)
  )
  modCtx.functions = Object.assign(
    Object.create(ctx.functions || Object.prototype),
    ownFns || project.functions || {}
  )
  // The module's SCOPE root — the third carrier, and the one that was missing
  // (WS-MODULE-CTX-DROPS-APP-GLOBALSCOPE-1). Same overlay shape as the two
  // above, and for the same two reasons: a shell globalScope key added after
  // this point stays visible through the live prototype, and a module key can
  // never land on the shell's own object where a sibling module would read it.
  //
  // `element/create.js` reads `element.context.globalScope` for the scope base
  // of every element that has no ancestor scope, so this one own property is
  // what makes a module page's `__scope.X` resolve to the app's own root
  // instead of the shell's.
  modCtx.globalScope = Object.assign(
    Object.create(ctx.globalScope || Object.prototype),
    project.globalScope || {}
  )
  // The module's DECLARED config (manifest `configDefaults`: websiteUrl,
  // integrationSlug, navbar, …) — the fourth carrier. Standalone, the app
  // context spreads config.js, so `el.context.configDefaults` IS the
  // declaration; in-shell only the takeover stash surfaced it
  // (`root.workspaceProject.config`), and an additive app could reach its
  // own declared values nowhere at all (aihouse admin: `websiteUrl` read as
  // undefined on every relation row's site link, measured live 2026-09-24,
  // WS-MODULE-CONFIG-DEFAULTS-MISSING-FROM-APP-CONTEXT-1). An OWN property
  // even when the module declares nothing, so a module never inherits the
  // shell's own config.js defaults through the prototype.
  modCtx.configDefaults =
    project.configDefaults && typeof project.configDefaults === 'object'
      ? project.configDefaults
      : null
  _moduleScopeCache.set(project.id, { ctx, modCtx })
  return modCtx
}

// The installed module that owns a scope context — the project id whose
// module scope context IS `ctx`. Every module surface carries that context
// (pages via _wrapModulePages, widget bodies and the rail via
// root._moduleDsCtxGet) and DOMQL hands it unchanged to the whole subtree, so
// `el.context` of any element a module renders names its module. A shell
// element's context is the shell context itself: no module, null.
//
// The seams that must know WHICH module calls them (the `prefs` entity of
// moduleData, setShellComposer) read it here instead of trusting an argument.
export const moduleIdOfContext = (ctx) => {
  if (!ctx || typeof ctx !== 'object') return null
  for (const cache of [_moduleScopeCache, _moduleDsCtxCache]) {
    for (const [id, hit] of cache) {
      if (hit && hit.modCtx === ctx && id != null) return String(id)
    }
  }
  return null
}

// Options for every buildModuleDataBridge call: the test SDK seam (when set)
// and the caller-module resolver above.
const _moduleDataBridgeOpts = () => ({
  ...(_sdkFnOverrideForTests ? { getSdkFn: _sdkFnOverrideForTests } : {}),
  moduleIdOf: moduleIdOfContext
})

// ── Per-module STATE root (WS-MODULE-STATE-SCOPE-1) ──────────────────────────
//
// One reactive store per installed module, chained to the SHELL root through
// the engine's own `store.root` delegation. The whole contract — read fallback,
// module-local writes, the shell-owned write-through set, the `s.root.shell`
// and `manifest.rootStateKeys` opt-ins, and the exact framework primitive that
// would replace the signal-alias step — is documented in moduleStateScope.js.
//
// Keyed on project.id AND on the shell root identity: the switch pipeline can
// replace the root store, and a module left holding a store chained to a dead
// root would read a frozen shell.
const _moduleStateCache = new Map() // project.id → { shellRoot, store }

// WS-MODULE-DECLARED-STATE-NOT-SEEDED-IN-SHELL-1 — seed `project.declaredState`
// (the module's own `state.js` defaults) onto `store`. Only a key that is (a)
// NOT shell-owned — the shell's own default already governs those, same rule
// `aliasShellOwnedKeys` uses for write-through — and (b) currently undefined
// on `store` is seeded, so a value a handler already wrote before this pass
// (or a declared default that is itself `undefined`) is never clobbered. The
// "currently undefined" gate also makes this idempotent across passes: once a
// key is seeded it reads defined and is never reconsidered.
const _seedDeclaredState = (project, store) => {
  const defaults = project && project.declaredState
  if (!defaults || typeof defaults !== 'object' || !store) return
  const rootStateKeys = normalizeScopedKeys(project.rootStateKeys || [])
  const patch = {}
  for (const key of Object.keys(defaults)) {
    if (isShellOwnedStateKey(key, rootStateKeys)) continue
    if (store[key] !== undefined) continue
    patch[key] = defaults[key]
  }
  if (Object.keys(patch).length) store.update(patch)
}

// The store, or null when scoping is unavailable (no engine / no root) — every
// caller then leaves the module on the shell root, exactly as before this
// feature. `engine` is the awaited `smbls` barrel.
const _moduleStateFor = (project, root, engine) => {
  if (!project || project.id == null || !root) return null
  const rootStateKeys = normalizeScopedKeys(project.rootStateKeys || [])
  const hit = _moduleStateCache.get(project.id)
  if (hit && hit.shellRoot === root) {
    // Re-sync on every pass: a shell `clean()`/`replace()` that dropped a
    // signal would otherwise leave this store holding an orphan, and a module
    // may declare a `prefix*` opt-in whose live matches grew since last pass.
    aliasShellOwnedKeys(hit.store, root, engine, rootStateKeys)
    return hit.store
  }
  const store = createModuleStateRoot(root, engine, { rootStateKeys })
  if (!store) return null
  // Fresh store — inject-time seeding happens exactly once here; a later
  // cache hit above never re-enters this branch for the same module+root.
  _seedDeclaredState(project, store)
  _moduleStateCache.set(project.id, { shellRoot: root, store })
  return store
}

// The live store for a module, without building one (render-time callers).
const _liveModuleState = (projectId, root) => {
  if (projectId == null) return null
  const hit = _moduleStateCache.get(projectId)
  if (!hit) return null
  if (root && hit.shellRoot !== root) return null
  return hit.store
}

// The keys that GENERATE a `typography`/`spacing` sequence. Stripped from a
// module's own design system so the shell's sequence is the only one in play —
// recursively, because a media block (`'@screenS': { base, ratio }`) restates
// the same generators for one breakpoint. A block left empty is dropped: the
// shell's block for that key then inherits whole.
const _SCALE_GENERATOR_KEYS = ['base', 'ratio', 'subSequence', 'range']
const _withoutScaleGenerators = (family) => {
  if (!family || typeof family !== 'object' || Array.isArray(family)) return family
  const out = { ...family }
  for (const key of _SCALE_GENERATOR_KEYS) delete out[key]
  for (const key of Object.keys(out)) {
    if (key.charAt(0) !== '@') continue
    const block = _withoutScaleGenerators(out[key])
    if (block && typeof block === 'object' && !Array.isArray(block) && !Object.keys(block).length)
      delete out[key]
    else out[key] = block
  }
  return out
}

const _moduleDsCtxCache = new Map() // project.id → { ctx, modCtx, wrappedPages }
const _ensureModuleDsContext = async (ctx, project, root) => {
  if (!project || !project.designSystem) return null
  const hit = _moduleDsCtxCache.get(project.id)
  if (hit && hit.ctx === ctx) return hit.modCtx
  try {
    const { createConfig, pushConfig, popConfig, init, changeGlobalTheme } = await _dsDeps()
    if (typeof createConfig !== 'function' || typeof init !== 'function') {
      // Older published smbls without the scratch re-exports — soft-fail:
      // the module renders with shell tokens, same as before the feature.
      throw new Error('smbls build lacks createConfig/init re-exports')
    }
    const key =
      'wsmod-' +
      String(project.id)
        .replace(/[^a-zA-Z0-9]/g, '')
        .slice(0, 16)
    // NO cleanBase: an in-shell module page composes SHELL components
    // (AppPage/AppSidebar chrome and friends) whose props use SHELL tokens —
    // an isolated clean-base config made those throw at createChildren
    // ("Unresolvable design-system token \"radiusCard\"", observed live) and
    // the whole page subtree stayed blank. Inheriting the ACTIVE (shell)
    // processed config gives every shell token as fallback while the
    // module's own tokens deep-merge OVER it and win on collision.
    // In-shell semantics: the module's OWN app flags (useVariable, useReset,
    // useDocumentTheme, …) describe how it boots STANDALONE and must not run
    // inside the shell — set() spreads the config back OVER init's options,
    // so a truthy flag in the published designSystem blob silently overrides
    // the explicit `false` passed to init below. Observed live (2026-08-12):
    // bellforge's `useVariable: true` re-enabled var injection inside init,
    // and its `[data-theme]` var blocks landed UNSCOPED in the document —
    // overriding the shell's global --theme-* table with var() references
    // that only resolve module-side. Every shell surface reading --theme-*
    // (context menus, dialogs, board widgets) painted transparent, in the
    // module's workspace only.
    const ds = { ...project.designSystem }
    for (const flag of [
      'useVariable',
      'useReset',
      'useFontImport',
      'useIconSprite',
      'useSvgSprite',
      'useDocumentTheme',
      'useDefaultIcons',
      'globalTheme',
      'themeRoot',
      'verbose'
    ])
      delete ds[flag]
    // The SHELL owns the type and spacing SCALE, at every breakpoint
    // (CORE-ORG-CREATE-PROJECT-SEEDS-DESIGN-TOKENS-THAT-OVERRIDE-SHELL-1).
    // A module page renders INSIDE shell chrome, so a letter must mean the
    // same pixel value on both sides of the seam; a module that generates its
    // own sequence renders at a scale the surrounding shell does not share.
    // The common case is not even a deliberate choice: a project created
    // through the org route declares no design system at all, and the server's
    // head read materializes `DESIGNSYSTEM_DEFAULTS` onto the empty namespace
    // (typography base 16 / ratio 1.25, spacing base 16 / ratio 1.618) — so
    // every such module arrived carrying a scale its author never wrote. Live
    // on my.symbols.app/w/shaker/default 2026-10-17 against the brand's
    // base 17 / ratio 1.2; clearing the token groups in the module's own
    // version 1.0.2 was the workaround this strip replaces.
    // Same class as `useVariable` and the theme mode above: dropping the
    // generators lets `createConfig`'s deep merge fill them from the shell
    // base it already inherits. Everything else in the two families —
    // `templates`, `unit`, `h1Matches`, a module's own keys — still belongs to
    // the module.
    for (const family of ['typography', 'spacing'])
      if (ds[family]) ds[family] = _withoutScaleGenerators(ds[family])
    // Media keys come from the HOST config this function holds, not from the
    // ambient one. `createConfig` without cleanBase inherits whatever
    // `getActiveConfig()` returns at call time — the top of the global config
    // stack, else the factory's active entry — and nothing ties that to
    // `ctx.designSystem`, the shell config the loader actually passes in. When
    // the ambient base is the framework default rather than the shell, every
    // default key (`mobileL`, `light`, `dark`) still inherits and only the
    // BRAND-only keys vanish: `appMobile` (`@container app-surface …`, brand
    // designSystem/media.js, absent from default-config) then compiled to a
    // theme selector and the module emitted zero `@container` rules (aihouse
    // admin, 2026-09-22; the module restated `appMobile` in its own media
    // file to get it back). So every host media key the module did not
    // declare is filled here; a module key always wins.
    const hostMedia = ctx && ctx.designSystem && ctx.designSystem.media
    if (hostMedia && typeof hostMedia === 'object') {
      const ownMedia = ds.media && typeof ds.media === 'object' ? ds.media : {}
      ds.media = { ...hostMedia, ...ownMedia }
    }
    const cfg = createConfig(key, ds)
    cfg.__domDocument = globalThis.document
    // Pinned BEFORE init, not only after: createConfig re-seeds the mode
    // flag from the framework default (true), and set() reads the spread
    // config over init's options — the injection gate inside init() only
    // sees false when the config itself carries it.
    //
    // Scheme-keyed tokens ARE supported here since scratch 3.14.778
    // (WS-MODULE-THEME-RULES-EMPTY-ON-DEPLOYED-SHELL-1). Before it, this
    // literal compile stored the scalar `{ var: '--theme-X' }` for every
    // theme name the module context saw — its own AND the shell's, since
    // createConfig runs without cleanBase — and the emitted rule body was
    // the invalid declaration `var: --theme-X`, which browsers drop: every
    // module surface painted with `theme:` rendered transparent. A module
    // may now ship its own `designSystem/theme.js`: its OWN entries inline
    // as `light-dark(...)` (module vars are never injected into the shared
    // document, so a reference would not resolve), and an INHERITED shell
    // entry keeps the host's `var(--theme-X-…)` refs, which the shell does
    // inject. Locked by workspaceProjects.moduleThemeRule.test.js.
    cfg.useVariable = false
    // Thread the HOST's resolved theme mode into the module config — pinned
    // on the config like useVariable above, and for the same reason.
    //
    // The strip list above deletes the module's OWN `globalTheme` (its
    // standalone boot flag must not run in-shell) but that left the module
    // config with NO globalTheme at all, and scratch's set() treats that as
    // 'auto': it stamps `data-theme` on the SHARED documentElement straight
    // from `matchMedia('(prefers-color-scheme: dark)')` and installs a
    // listener. So ~10s into boot, when the module DS initialised, it
    // OVERWROTE the shell's forced theme with the OS value — measured live on
    // app.bellforge.eu: root state light, `data-theme` dark, and even an
    // explicit `?globalTheme=light` ended dark once the module landed.
    //
    // Scratch has an owner-guard for exactly this (a late 'auto' config
    // ADOPTS an already-governed root). That guard now SHIPS: published
    // @symbo.ls/scratch 3.14.778 carries `__smblsThemeRootOwner` beside the
    // auto-stamper `__prefersListener` (measured 2026-09-22 on the published
    // tarball, which is byte-identical to the local dist the dev shell
    // serves). Threading the host mode stays correct regardless: it makes
    // scratch take its forced branch and stamp the same value the shell
    // already set — idempotent, listener-free, right on old and new builds.
    //
    // 'auto' is deliberately NOT threaded: under auto the OS legitimately
    // decides, the module's stamp equals the shell's, and forcing a resolved
    // value would freeze an OS flip mid-session.
    //
    // WS-THEME-BOOT-WRONG-MODE-1 (2026-08-21): this creation-time read is the
    // FAST PATH only — it races the shell's theme seed, so the module config
    // also FOLLOWS the host from here on (`_followHostTheme` below). The mode
    // of record is the LIVE root state (`root.themeMode`, what ThemeToggle /
    // the prefs reconcile / the storage event write); `ctx.state` is smbls'
    // boot-seed SNAPSHOT (prepareState copies it into the root state and
    // never writes back), kept only as the pre-create fallback.
    const hostThemeMode = _hostThemeModeOf(root, ctx)
    if (hostThemeMode === 'light' || hostThemeMode === 'dark') {
      cfg.globalTheme = hostThemeMode
    }
    pushConfig(cfg)
    try {
      // init/set MUTATE the config they operate on and do not reliably
      // return it — `cfg` (pushed AND passed via options.config) is the
      // processed, `_scratchConfig`-marked object; anything else fails
      // pushConfig's marker check downstream and the threading silently
      // no-ops (the first live symptom of exactly that: dsMarked=false).
      init(cfg, {
        key,
        document: globalThis.document,
        config: cfg,
        scopeSelector: `[data-smbls-module="${key}"]`,
        useReset: false,
        useFontImport: false,
        useDocumentTheme: false,
        useVariable: false,
        useIconSprite: false,
        useSvgSprite: false,
        useDefaultConfig: true
      })
    } finally {
      popConfig()
    }
    // Literal-value resolution, enforced ON THE CONFIG: getColor returns
    // `var(--color-X)` whenever CONFIG.useVariable is truthy, and the module
    // config's vars are never injected into the document — the class would
    // paint transparent. The init option alone does not reliably survive
    // onto the config object (observed live: _bg-var--color-navy, computed
    // rgba(0,0,0,0)).
    cfg.useVariable = false
    // ONE context per module: the DS config rides the SAME object that already
    // carries the module's own component/function overlays, so a module page
    // never has to choose between its tokens and its own function names.
    const modCtx = _moduleScopeFor(ctx, project) || Object.create(ctx)
    modCtx.designSystem = cfg
    _moduleDsCtxCache.set(project.id, { ctx, modCtx, root })
    _followHostTheme({ pushConfig, popConfig, changeGlobalTheme })
    return modCtx
  } catch (e) {
    console.warn(
      '[workspaceModule] design-system processing failed — module renders with shell tokens:',
      e && e.message
    )
    return null
  }
}

// WS-THEME-BOOT-WRONG-MODE-1 — the module config FOLLOWS the host theme mode.
//
// Threading the host mode into the module config at creation (the block in
// `_ensureModuleDsContext` above) is necessary but not sufficient: it RACES
// the shell's theme seed. bootShell seeds the mode only after the prefs
// pre-flight resolves, and the module project fetch is fired in the same
// tick as that pre-flight. Measured 2026-08-21 on ONE bundle
// (workspace.f7f0f762.js, identical bytes on dev.my and staging.my):
//   dev.my    — /core/projects/key/bellforge/bellforge-app landed 90 ms
//               BEFORE /core/prefs/user → module config created with no host
//               mode → scratch set() took its 'auto' branch → stamped the OS
//               value (dark) over the shell's forced light and installed its
//               own prefers listener. Live: module config {globalTheme:'auto',
//               resolvedTheme:'dark', __prefersListener:true}, root state
//               themeMode:'light', shell frame dark, module cards light,
//               "54" ink #D9A521 (dark-tone) inline on white — the reported
//               "loads dark, partly light" page.
//   staging.my — prefs first → module config {globalTheme:'light'} → light.
// Same bytes, opposite outcome: a race, not a build. The snapshot ALSO froze
// a module at the mode it was created under — a later ThemeToggle / prefs
// reconcile / storage-event `changeGlobalTheme` moved the shell config only.
//
// Follower: every `data-theme` write on the shared root (the shell's forced
// boot write, a toggle, the reconcile, a storage event, an OS flip) re-reads
// the HOST mode and re-threads every cached module config through scratch's
// own `changeGlobalTheme(mode, cfg)` when config or attribute disagree — the
// same instrument `watchThemeResolved` (shared/functions/themeResolved.js)
// uses for `state.themeResolved`. Idempotent by construction: a re-thread
// writes the attribute the host already holds, so the next observer pass
// finds config and attribute equal and stops. A stale module prefers
// listener (installed by an 'auto' pass on a scratch without the theme-root
// owner guard, smbls 2fd42c892 unpublished) is neutralised the same way: its
// OS write disagrees with a forced host and is re-threaded back.
//
// Mode of record: the LIVE root state (`root.themeMode`). `ctx.state` is the
// boot-seed snapshot (smbls prepareState copies it into the root state and
// never writes back) and is only a pre-create fallback.
const _hostThemeModeOf = (root, ctx) => {
  const live = root && typeof root === 'object' ? root.themeMode : null
  const seed = ctx && ctx.state ? ctx.state.themeMode : null
  const mode = live === 'light' || live === 'dark' || live === 'auto' ? live : seed
  return mode === 'light' || mode === 'dark' || mode === 'auto' ? mode : null
}
let _dsThemeDeps = null
let _hostThemeObserver = null
const _syncOneModuleDsTheme = (hit) => {
  const cfg = hit && hit.modCtx && hit.modCtx.designSystem
  const host = _hostThemeModeOf(hit && hit.root, hit && hit.ctx)
  if (!cfg || !host || !_dsThemeDeps) return false
  const forced = host === 'light' || host === 'dark'
  const doc = globalThis.document
  const el = doc && doc.documentElement
  const attr = el && typeof el.getAttribute === 'function' ? el.getAttribute('data-theme') : null
  if (cfg.globalTheme === host && (!forced || attr === host)) return false
  const { pushConfig, popConfig, changeGlobalTheme } = _dsThemeDeps
  pushConfig(cfg)
  try {
    changeGlobalTheme(host, cfg)
  } finally {
    popConfig()
  }
  return true
}
// Re-thread every cached module config to the host mode. Returns how many
// configs changed. Called by the root observer below; exported so a caller
// that changes the host mode outside the DOM path (tests, a future explicit
// hook) can force the pass.
export const syncModuleDsTheme = () => {
  let n = 0
  for (const hit of _moduleDsCtxCache.values()) {
    try {
      if (_syncOneModuleDsTheme(hit)) n++
    } catch (e) {
      console.warn('[workspaceModule] host theme re-thread failed:', e && e.message)
    }
  }
  return n
}
const _followHostTheme = (deps) => {
  _dsThemeDeps = deps
  if (_hostThemeObserver || typeof MutationObserver === 'undefined') return
  const doc = globalThis.document
  const el = doc && doc.documentElement
  if (!el || typeof el.setAttribute !== 'function') return
  _hostThemeObserver = new MutationObserver(() => {
    syncModuleDsTheme()
  })
  _hostThemeObserver.observe(el, { attributes: true, attributeFilter: ['data-theme'] })
}

// Wrap every module page def with the module DS context — cached alongside
// the context so re-passes hand the SAME objects to every resolver. The
// `context` property is NON-ENUMERABLE on purpose (fingerprint + clone
// safety); element create reads `definition.context` directly, which works
// regardless of enumerability.
//
// IMPORTANT: the wrapped defs can NEVER ride root state — root.update deep-
// clones, and a clone strips the non-enumerable context (and would cycle on
// the scratch config if it were enumerable). The PageSlot's home resolver
// (_appPageFor via root._slotResolve) therefore consults the LIVE map below
// (getModulePageDef) instead of the cloned workspaceProject.pages stash.
let _liveModulePages = null
export const getModulePageDef = (path) => (_liveModulePages && _liveModulePages[path]) || null
// Cross-chunk safety: main.js's home resolver can live in a DIFFERENT bundle
// instance of this module (observed live 2026-08-11: inject ran and ctx.pages
// carried wrapped defs, yet the resolver's imported getModulePageDef saw
// null). The loader therefore ALSO installs a getter CLOSURE on the state
// root each pass — the closure carries THIS instance's map regardless of
// chunking; root.update clones drop functions harmlessly and the next pass
// reinstalls.
const _installLiveGetter = (root) => {
  if (!root) return
  try {
    root._modulePagesLiveGet = () => _liveModulePages
    // Live module-FUNCTION getter — the home board's widget resolver
    // (pages/main.js _allModuleWidgetsFor) evaluates a widget's optional
    // `visibleFn` gate through this, so a module can persona-scope its own
    // home widgets without the shell knowing any persona model. Same
    // cross-chunk closure contract as the two getters around it.
    // `ownerId` (the widget's owning module) resolves the name in THAT
    // module's own scope, own property only — two apps may name the same
    // gate function, and the root bag keeps only the first copy
    // (moduleSwitchHookOwnScope.test.js). No owner, or an owner with no
    // module scope: the root bag, as before.
    root._moduleFnGet = (name, ownerId) => {
      const scoped = _ownerScopeFns(ownerId)
      if (scoped) {
        const own = Object.prototype.hasOwnProperty.call(scoped, name) ? scoped[name] : null
        return typeof own === 'function' ? own : null
      }
      const c = _ctx(root)
      const f = c && c.functions ? c.functions[name] : null
      return typeof f === 'function' ? f : null
    }
    // Same cross-chunk closure trick for the module DS context itself: the
    // home board mounts MODULE WIDGET bodies (`_mountModuleWidgetBody` in
    // pages/main.js) under the SHELL config — module tokens inside a widget
    // silently dropped (the exact failure _ensureModuleDsContext documents
    // for pages, previously unfixed for widgets). The board reads this
    // getter to attach the module context to widget body defs.
    // Also the carrier of the module's own component/function overlays now, so
    // a widget body mounted under it resolves the module's `natEnsureLoaded`
    // rather than whichever same-named app registered into root first. A
    // DS-less module has no entry in the DS cache but always has a scope.
    root._moduleDsCtxGet = (projectId) => {
      if (projectId == null) return null
      const hit = _moduleDsCtxCache.get(projectId) || _moduleScopeCache.get(projectId)
      return (hit && hit.modCtx) || null
    }
    // WS-MODULE-STATE-SCOPE-1 — same cross-chunk closure contract for the
    // module's STATE root. The scope itself travels on the module CONTEXT (a
    // plugin — see moduleStateScope.js), so no mount point needs this; it is
    // for SHELL code that must READ a module's live data without being inside
    // it. Today that is `uiCatalog.buildUiCatalog`, which reports each module's
    // declared stateKeys to the assistant and would otherwise read the shell
    // store and report every loaded slice as empty. Returns null for an
    // unscoped module, and every caller falls back to the shell root.
    root._moduleStateGet = (projectId) => _liveModuleState(projectId, root)
  } catch (e) {
    /* sealed root — resolver falls back to the import path */
  }
}
// Append the composer-latch error plugin to a module context's OWN plugin
// list, once. Inherited plugins (the shell's) stay in front, exactly like
// attachModuleStateScope builds its list.
const _attachComposerErrorPlugin = (modCtx, root) => {
  if (!modCtx || !root) return
  const own = Object.prototype.hasOwnProperty.call(modCtx, 'plugins') ? modCtx.plugins : null
  const list = Array.isArray(own) ? own : Array.isArray(modCtx.plugins) ? [...modCtx.plugins] : []
  if (list.some((p) => p && p.__moduleComposerLatch === root)) return
  // A plugin bound to a replaced shell root (the switch pipeline can swap the
  // root store) is dropped, not kept beside the new one.
  const kept = list.filter((p) => !(p && p.__moduleComposerLatch))
  modCtx.plugins = [...kept, buildModuleComposerErrorPlugin(root)]
}
export const _wrapModulePages = async (ctx, project, root) => {
  const pages = project.pages || {}
  // A module with NO design system still needs its own resolution scope — the
  // cross-app function collision (natEnsureLoaded) has nothing to do with
  // tokens. So the DS context is the preferred carrier when there is one, and
  // the plain scope is the carrier otherwise; both are the same object shape
  // and both are prototype-chained to the shell context.
  const dsCtx = ctx ? await _ensureModuleDsContext(ctx, project, root) : null
  const modCtx = dsCtx || _moduleScopeFor(ctx, project)
  if (!modCtx) return pages
  // WS-MODULE-STATE-SCOPE-1 — the module's STATE root, built on the same
  // awaited engine the DS context uses, and carried into the tree by a plugin
  // on the SAME context object that already carries the module's design system
  // and its function/component overlays. Nothing is written onto the defs: a
  // def property would be shadowed by the extends chain at the two call sites
  // that mount a module surface by name (see moduleStateScope.js). A soft
  // failure here (no engine, a non-store root) leaves every module surface on
  // the shell root, exactly as before this feature.
  const modState = await _ensureModuleStateRoot(project, root)
  if (modState) {
    attachModuleStateScope(modCtx, ctx, modState)
  } else {
    // WS-MODULE-DECLARED-STATE-NOT-SEEDED-IN-SHELL-1 — scoping declined, so
    // this module reads/writes the shell root directly (no isolation, exactly
    // the pre-WS-MODULE-STATE-SCOPE-1 behaviour). Its declared defaults still
    // deserve the same treatment every other un-scoped module key already
    // gets on this root: seed once, never touch a key already defined.
    _seedDeclaredState(project, root)
  }
  // MODULE-SHELL-COMPOSER-1 — a lifecycle error inside a module page that
  // hid the shell composer releases that page's latch (moduleShellComposer.js).
  // Same carrier as the state scope: a plugin on the module context, appended
  // once, never a def property.
  _attachComposerErrorPlugin(modCtx, root)
  const hit = _moduleDsCtxCache.get(project.id) || _moduleScopeCache.get(project.id)
  if (hit && hit.modCtx === modCtx && hit.wrappedPages) {
    // WS-MODULE-PAGE-SCOPE-SNAPSHOT-STALES-A-LATE-HELPER-1 — a page that
    // declares its own `scope:` (below) got a MINT-TIME COPY of
    // modCtx.globalScope merged with its own keys, frozen into this cached
    // wrappedPages entry forever. _moduleScopeFor's cache-hit refresh keeps
    // modCtx.globalScope itself fresh — every OTHER wrapped page already
    // sees that, because its `w.scope` is a LIVE REFERENCE to that same
    // object — but a page with its own scope got a COPY, and a copy never
    // reads modCtx.globalScope again once cached. A key whose usable value
    // arrives after the first wrap (a helper revived from its published
    // string form) then stayed stale forever, only on pages that declare
    // their own scope. Refresh those copies' globalScope-sourced keys the
    // same way modCtx.globalScope itself is refreshed: a key the page's OWN
    // `scope:` declares is never touched, so a page-level override still
    // wins.
    for (const [path, w] of Object.entries(hit.wrappedPages)) {
      const def = pages[path]
      if (!def || typeof def.scope !== 'object' || def.scope === null) continue
      for (const [k, v] of Object.entries(modCtx.globalScope)) {
        if (!Object.prototype.hasOwnProperty.call(def.scope, k)) w.scope[k] = v
      }
    }
    return hit.wrappedPages
  }
  const wrapped = {}
  for (const [path, def] of Object.entries(pages)) {
    const w = { ...def }
    Object.defineProperty(w, 'context', { value: modCtx, enumerable: false })
    // WS-MODULE-CTX-DROPS-APP-GLOBALSCOPE-1 (live leg) — the context carrier
    // alone never reaches `el.scope`: element/create.js bases scope on
    // `ancestorScope || context.globalScope`, and a module page always HAS an
    // ancestor scope (the shell's), so `__scope` reads inside the app's
    // published functions still resolved the SHELL chain (measured live on
    // prod b8b4b820: ctx.globalScope owned NAT_COLLECTIONS, el.scope did not).
    // `scope` on the def makes create.js mint the page's OWN scope store with
    // the app bag as own keys and the ancestor chain as live fallback. It must
    // be ENUMERABLE — the router mounts `{ ...content }`, which drops
    // non-enumerable keys (`context` pays for that with _routeTableDef); the
    // bag is plain data, so def fingerprints (_publishFp) stay stringifiable.
    if (modCtx.globalScope) {
      w.scope =
        def && typeof def.scope === 'object' && def.scope !== null
          ? Object.assign({}, modCtx.globalScope, def.scope)
          : modCtx.globalScope
    }
    wrapped[path] = w
  }
  if (hit) hit.wrappedPages = wrapped
  return wrapped
}

// Build (or refresh) one module's state root, awaiting the SAME `smbls` chunk
// the module design system already awaits — no second import, and therefore no
// second reactive engine introduced by this feature. Whether the engine it
// hands back is the one that OWNS the shell root is not assumed:
// `createModuleStateRoot` proves it and declines loudly when it is not (see
// moduleStateScope.js's one-engine note).
const _ensureModuleStateRoot = async (project, root) => {
  if (!project || project.id == null || !root) return null
  try {
    return _moduleStateFor(project, root, await _dsDeps())
  } catch (e) {
    console.warn(
      '[workspaceModule] state scoping unavailable — module writes stay on the shell root:',
      e && e.message
    )
    return null
  }
}

// MODULE-ROUTE-CTX (tickets/fable.md ~5129): a copy of a wrapped module page
// def SAFE for the smbls auto-router's own merge. `router()`
// (smbls/plugins/router/index.js) matches `element.routes[path]`, then does
// `nextContent = { ...contentStyles, ...content }` before `element.set(
// nextContent, …)` — for EVERY navigation that reaches this table (a hard
// URL load's initial render, a top-navbar tab's `el.router()` SPA nav, and
// popstate). A plain object spread copies only OWN ENUMERABLE properties, so
// `def.context` — deliberately non-enumerable in `_wrapModulePages` above
// (clone/fingerprint defense: these defs can never ride root state, see that
// fn's block comment) — never survives into the router's `nextContent`, and
// the matched module page falls back to the SHELL's scratch config: dark
// shell theme, blue links, unreadable dim title (measured live 2026-08-13,
// bellforge /w/bellforge/theses hard-load + its top-navbar tabs). PageSlot.js
// hits the identical "spread drops non-enumerable props" hazard on ITS OWN
// mount spread and works around it by re-declaring `context: def.context`
// explicitly in the object literal it hands to `host.set({...})` — there is
// no equivalent shell call site inside the framework router to annotate the
// same way, so the copy handed to the ROUTE TABLE instead carries `context`
// as a normal OWN ENUMERABLE property from the start: it then survives ANY
// downstream spread, including the router's. `ctx.pages` (read by PageSlot's
// own resolver via root._slotResolve/_appPageFor) keeps the ORIGINAL
// non-enumerable-context def unchanged — this second, routes-table-only copy
// exists solely to satisfy the router's merge; DS-less modules (def.context
// absent) get back the identical `def` reference, so non-module/non-DS
// routes are byte-for-byte unchanged.
const _routeTableDef = (def) => (def && def.context ? { ...def, context: def.context } : def)

// ── App setup on demand (boot fix step 4b) ──────────────────────────────────
// An installed app's page wrap — its design-system context, state root and
// wrapped page defs — no longer runs inside the boot pass. The pass registers
// the app cheaply (functions, components as lazy entries, grants, navbar) and
// puts a PLACEHOLDER page on each of its paths. The wrap runs when the app is
// first opened (the placeholder renders) or in the app's own idle slice after
// the reveal, whichever comes first, and only once. design-d2's rules for the
// placeholder: it shows only on the first open, reads busy (aria-busy + a
// polite status "Opening {app}…"), routes again to the SAME URL with no new
// history entry and no scroll reset, and on a failed wrap shows the app's
// error, never an endless load. An app's home widgets publish only after its
// wrap: before it they would render with the shell's tokens.
const _appActivations = new Map()
const _appDisplayName = (project, ref) => {
  const nav = project && project.configDefaults && project.configDefaults.navbar
  const label = Array.isArray(nav) && nav[0] && nav[0].label
  return String(label || (project && project.name) || (ref && ref.key) || '')
}
// The opening placeholder's marker. Non-enumerable (see _appOpeningPage): DOMQL
// key iteration, an object spread and root.update's clone never see it. A
// REGISTERED symbol, so the home's page resolver (pages/main.js _appPageFor)
// reads the same marker with no import between the two files: a marked
// placeholder in the context bag beats the project-stash copy of the app's
// page, because RENDERING the placeholder is what sets the app up
// (WS-APP-OPEN-SLOT-PLACEHOLDER-1: a navbar tab opened the stash copy in the
// page slot, against the shell design system, and the app's setup waited for
// its idle slice — +14.1 s after the click, measured live on the Unreal twin).
const _APP_OPENING = Symbol.for('symbols.workspace.appOpening')
export const isAppOpeningPage = (def) => !!(def && def[_APP_OPENING])
// Click-to-content time (`wsopen:<app>:*`), beside the `wsboot:app:*` spans:
// `:intent` when an opening placeholder renders (the member asked for the
// app), `:setup:start` / `:setup:end` around the page wrap, `:reopen` when the
// real page took the placeholder's place, and ONE measure `wsopen:<app>` from
// the first intent to that reopen. An app set up in its idle slice, with no
// open intent, gets the setup marks only. The measure starts and ends exactly
// ON its two marks (their own times), so the span and the marks line up in the
// timeline. Telemetry only — never throws.
const _openMarkAt = (name) => {
  try {
    if (typeof performance === 'undefined' || typeof performance.mark !== 'function') return null
    const mark = performance.mark(name)
    return mark && typeof mark.startTime === 'number' ? mark.startTime : performance.now()
  } catch (_) {
    return null
  }
}
const _openIntent = (entry) => {
  if (entry.openAt != null) return
  const at = _openMarkAt('wsopen:' + entry.spanKey + ':intent')
  entry.openAt = at != null ? at : _spanStart()
}
const _openReopened = (entry) => {
  const end = _openMarkAt('wsopen:' + entry.spanKey + ':reopen')
  const start = entry.openAt
  entry.openAt = null
  if (start == null || end == null) return
  try {
    if (typeof performance.measure === 'function') {
      performance.measure('wsopen:' + entry.spanKey, { start, end })
    }
  } catch (_) {
    /* telemetry only */
  }
}
// The real page takes the placeholder's place wherever a `set` mounted it: a
// page slot pane, a parked slot host, the second pane. `set` on the parent is
// the very call that mounted the placeholder there, so the page lands exactly
// where the placeholder was. Nothing else re-mounts a slot pane: its host is
// keep-alive (canvas components/PageSlot.js), so a re-open of the same path
// only reveals the host that is already there. The ROUTER's page is left to
// the same-URL re-route (_reopenActivatedApp): the router holds that swap for
// the leaving page's exit motion, and a second set here would race it.
//
// The page is mounted with the props the page slot forces on every page it
// mounts (PageSlot.js _mountPane -> apply): `context` explicitly — the wrapped
// def carries it NON-enumerable (_wrapModulePages) and a spread drops it; a
// plain div — a page is a <main>, and a pane must not add a second main
// landmark; no page-grid placement; full height.
// __tests__/appOpenSwapMountParity.test.js holds the two mounts together.
const _swapInAppPage = (entry, path, el) => {
  // A placeholder that was replaced or swept meanwhile is disposed: it has no
  // parent left. One that is still mounted is its parent's `content`.
  const holder = el && el.parent
  if (!holder || holder === entry.routedEl) return false
  if (el.key !== 'content' || holder.content !== el) return false
  if (typeof holder.set !== 'function') return false
  // Disposed meanwhile: dispose() clears __ref (and node).
  if (!el.__ref) return false
  const def = entry.ctx && entry.ctx.pages ? entry.ctx.pages[path] : null
  if (!def || isAppOpeningPage(def)) return false
  const swap = () => {
    // The holder may have moved on before the insertion.
    if (holder.content !== el) return
    holder.set({
      ...def,
      context: def.context,
      tag: 'div',
      gridRow: null,
      gridColumn: null,
      height: '100%',
      minHeight: '100%'
    })
  }
  // NOT connected YET is not gone: smbls 3.14.84x builds an app's first view
  // detached and inserts it whole (firstRender), so a cold deep link to an app
  // that is already set up settles here before the insertion. The old guard
  // (`isConnected === false` → no swap) left that placeholder on "Opening …"
  // forever. el.whenConnected swaps now when connected, else in the task that
  // inserts the node, and never for a disposed element.
  if (typeof el.whenConnected === 'function') el.whenConnected(swap)
  else swap()
  return true
}
// Open the app from its placeholder: the open intent, the setup (single flight,
// _activateWorkspaceApp), then the real page in the placeholder's place — or
// the error with its Retry.
const _openAppPage = (entry, path, el, s) => {
  _openIntent(entry)
  const fail = () => {
    if (s && typeof s.update === 'function') s.update({ failed: true })
  }
  _activateWorkspaceApp(entry).then((ok) => {
    if (ok === false) return fail()
    if (_swapInAppPage(entry, path, el)) _openReopened(entry)
  }, fail)
}
const _appOpeningPage = (entry, path) => {
  const page = {
    tag: 'section',
    flow: 'y',
    align: 'center center',
    gap: 'A',
    minHeight: '100%',
    padding: 'C',
    state: { failed: false },
    ariaBusy: (el, s) => (s && s.failed ? 'false' : 'true'),
    // "Opening Commerce…" — the app by its App Store name, in the session
    // language (Nika 2026-10-23: "app names = what a person sees in the
    // workspace App Store"). `entry.name` is the manifest's first tab
    // (_appDisplayName: "Store"); installedAppName asks THE store resolver
    // with the app's catalog identity and keeps those words for an app the
    // store ships no name for.
    Status: {
      tag: 'p',
      color: 'caption',
      role: 'status',
      ariaLive: 'polite',
      text: (el, s) =>
        s && s.failed
          ? el.call('polyglot', 'workspace.apps.loadFailed')
          : String(el.call('polyglot', 'workspace.apps.opening') || '').replace(
              '{app}',
              el.call('installedAppName', { catalogId: entry.catalogId, label: entry.name }) ||
                entry.name
            )
    },
    // A failed setup offers ONE recovery action — Retry: set the app up again,
    // here, without closing the sheet. The house error-poster action (the
    // brand Button in solid ink, on its own control scale). Its box is reserved
    // while the app opens, so the status line never moves when it appears.
    Button: {
      theme: 'primary',
      text: '{{ common.action.try_again | polyglot }}',
      visibility: (el, s) => (s && s.failed ? 'visible' : 'hidden'),
      pointerEvents: (el, s) => (s && s.failed ? 'auto' : 'none'),
      tabindex: (el, s) => (s && s.failed ? '0' : '-1'),
      aria: { hidden: (el, s) => (s && s.failed ? null : 'true') },
      onClick: (e, el, s) => {
        if (s && typeof s.update === 'function') s.update({ failed: false })
        _openAppPage(entry, path, el.parent, s)
      }
    },
    onRender: (el, s) => _openAppPage(entry, path, el, s)
  }
  // The placeholder names its page: the page's own `metadata` rides on it. The
  // pane header and the tab title read a def's metadata when the pane opens,
  // and nothing reads it again when the real page takes the placeholder's
  // place — without it the pane header would show the raw path for good.
  const own = entry.project && entry.project.pages ? entry.project.pages[path] : null
  if (own && own.metadata !== undefined) page.metadata = own.metadata
  Object.defineProperty(page, _APP_OPENING, { value: true })
  return page
}
// The page on screen is one of this app's paths: show the real page at the
// SAME URL — no history entry, no scroll reset. A page slot needs no re-open
// here: the placeholder it shows takes the real page itself (_swapInAppPage).
// The old slot re-open — `_canvasSlot` written null, then the same path —
// never re-mounted anything: both writes land in ONE effect flush (the signal
// engine defers effects to a microtask and a store update batches), so the
// pane saw the same path and its keep-alive revealed the host it already had.
const _reopenActivatedApp = (entry, appPages) => {
  const { routedEl } = entry
  try {
    const loc = typeof window !== 'undefined' && window.location ? window.location : null
    const pathname = (loc && loc.pathname) || ''
    const routePath = pathname ? shellStripWorkspacePrefix(pathname) : ''
    if (routePath && appPages[routePath] && routedEl && typeof routedEl.router === 'function') {
      const nav = routedEl.router(
        pathname + ((loc && loc.search) || '') + ((loc && loc.hash) || ''),
        routedEl,
        {},
        { pushState: false, scrollToTop: false }
      )
      // The router may hold the swap for the leaving page's exit motion.
      Promise.resolve(nav).then(
        () => _openReopened(entry),
        () => {}
      )
    }
  } catch (e) {
    console.warn('[workspaceApps] re-open after setup failed', e && e.message)
  }
}
// One app's published entry, rebuilt in the list (matched by its catalog id).
// `setupFailed` rides only on an entry whose last setup failed.
const _publishAppEntry = (entry, patch) => {
  const { root, catalogId } = entry
  const list = root && root.workspaceApps
  if (!Array.isArray(list) || !list.some((a) => a && a.appId === catalogId)) return
  root.update({
    workspaceApps: list.map((a) => {
      if (!a || a.appId !== catalogId) return a
      const next = { ...a, ...patch }
      if (!patch.setupFailed) delete next.setupFailed
      return next
    })
  })
}
const _publishActivatedApp = (entry) => {
  const { project } = entry
  if (!project || (!project.widgets && !project.views && !project.forms)) return
  _publishAppEntry(entry, {
    widgets: project.widgets || null,
    pendingWidgets: null,
    views: project.views || null,
    forms: project.forms || null
  })
}
// A FAILED setup releases the home at the source (time to facts): the entry
// stops naming widgets on their way (the home would wait for them forever) and
// says its setup failed — `setupFailed: true`, since `pendingWidgets: null`
// alone reads as "set up" — so the home never asks a provider that has no
// state root and can name the app as failed. Its widgets stay null and its
// placeholder keeps the error; a later open still retries, and a success
// publishes the widgets and drops the flag. An install that is no longer
// current (a newer pass, a teardown) publishes nothing.
const _releaseFailedApp = (entry) => {
  if (_appActivations.get(entry.ledgerId) !== entry) return
  const list = entry.root && entry.root.workspaceApps
  const cur = Array.isArray(list) ? list.find((a) => a && a.appId === entry.catalogId) : null
  if (!cur || (cur.pendingWidgets == null && cur.setupFailed === true)) return
  _publishAppEntry(entry, { pendingWidgets: null, setupFailed: true })
}
// Runs the app's page wrap once (single flight per install). Resolves true
// when the real pages replaced the placeholders, false when the install was
// torn down or superseded meanwhile. A failure clears the flight, so the next
// open tries again.
export const _activateWorkspaceApp = (entry) => {
  if (!entry) return Promise.resolve(false)
  if (entry.done) return Promise.resolve(true)
  if (entry.promise) return entry.promise
  const run = (async () => {
    const { root, ctx, routedEl, project, ledgerId } = entry
    const tWrap = _spanStart()
    _mark('wsopen:' + entry.spanKey + ':setup:start')
    const appPages = await _wrapModulePages(ctx, project, root)
    _span(entry.spanKey, 'wrap', tWrap)
    _mark('wsopen:' + entry.spanKey + ':setup:end')
    if (_appActivations.get(ledgerId) !== entry) return false
    for (const [path, def] of Object.entries(appPages)) {
      const held = entry.placeholders[path]
      if (!held || ctx.pages[path] !== held) continue
      // Read the route table BEFORE writing ctx.pages: in the live shell the
      // router's table IS ctx.pages (one object — measured on dev 2026-10-04,
      // `getRoot().routes === context.pages`), so once ctx.pages held `def`
      // the table no longer read `held` and the context-carrying copy never
      // landed. The router then mounted the app page from `def` through its
      // spread, which drops the non-enumerable `context`: every installed
      // app's ROUTE page ran on the SHELL context — its own icons painted
      // the noIcon placeholder (Storm Validators: valRefresh / valNetwork …)
      // and its own tokens fell back to the shell's
      // (MODULE-PAGE-ROUTE-MOUNTS-ON-SHELL-CONTEXT-1).
      const routeHeld = !!(routedEl && routedEl.routes && routedEl.routes[path] === held)
      ctx.pages[path] = def
      // _routeTableDef: the router's merge spread drops a def's non-enumerable
      // `context` (MODULE-ROUTE-CTX) — the table needs it as an own property.
      if (routeHeld) routedEl.routes[path] = _routeTableDef(def)
    }
    entry.done = true
    _publishActivatedApp(entry)
    _reopenActivatedApp(entry, appPages)
    return true
  })()
  entry.promise = run
  run.catch((e) => {
    if (entry.promise === run) entry.promise = null
    console.warn('[workspaceApps] app setup failed', entry.catalogId, e && e.message)
    try {
      _releaseFailedApp(entry)
    } catch (_) {
      /* the placeholder still shows the error */
    }
  })
  return run
}
// The home surfaces an app feeds (time to facts): 'signals' — it declares a
// homeSignals provider, 'upcoming' — a homeUpcoming one; [] — it never feeds
// the home. Read from the app's revived functions, which are real at the pass
// (only components revive lazily), so every published entry names them before
// its setup and the home waits only for an app that feeds it.
export const _HOME_PROVIDER_FNS = [
  ['signals', 'homeSignals'],
  ['upcoming', 'homeUpcoming']
]
const _homeProvidersOf = (project) => {
  const fns = project && project.functions
  if (!fns || typeof fns !== 'object') return []
  return _HOME_PROVIDER_FNS
    .filter(
      ([, name]) =>
        Object.prototype.hasOwnProperty.call(fns, name) && typeof fns[name] === 'function'
    )
    .map(([kind]) => kind)
}
// After the pass every registered app sets up, one at a time — first the apps
// that feed the home (homeProviders), then apps with a widget on the saved
// board, then the rest in install order. A home feeder takes a NORMAL-priority
// task right after the pass: the home's facts wait for it, and a background
// slice comes late on a busy cold boot. Apps with visible board widgets also
// take a normal task; only the remaining apps wait for a background idle slice.
// The pass and the reveal never wait for either. The root flag
// `_appsSettingUp` stays up until the queue drains, so a saved cell keeps its
// skeleton until its app's widgets publish (no reflow).
// One macrotask that a HIDDEN page does not throttle (2026-10-02: a background
// tab sat on "Opening apps" for 143 s). Chrome clamps a hidden page's timers to
// >= 1 s (once a minute after 5 minutes) and runs no frames, so boot work must
// never wait on setTimeout / requestAnimationFrame to progress. Same order as
// the framework's mountScheduler: scheduler.yield, MessageChannel, setTimeout.
const _yieldTask = () =>
  new Promise((resolve) => {
    const sch = typeof globalThis !== 'undefined' ? globalThis.scheduler : null
    if (sch && typeof sch.yield === 'function') {
      sch.yield().then(resolve, resolve)
      return
    }
    if (typeof MessageChannel === 'function') {
      const ch = new MessageChannel()
      ch.port1.onmessage = () => {
        ch.port1.onmessage = null
        ch.port1.close()
        ch.port2.close()
        resolve()
      }
      ch.port2.postMessage(0)
      return
    }
    setTimeout(resolve, 0)
  })
const _pageHidden = () => typeof document !== 'undefined' && !!document && document.hidden === true
// The pass's yield between app registrations. A VISIBLE page keeps the timer
// task: queued input, paint and the reveal watchdog (a timer) get their turn
// before the next app (workspaceProjects.test.js pins a switch queued that
// way). A HIDDEN page takes a task the browser does not throttle.
const _passYield = () =>
  _pageHidden() ? _yieldTask() : new Promise((resolve) => setTimeout(resolve, 0))
const _taskSlice = () =>
  new Promise((resolve) => {
    const sch = typeof globalThis !== 'undefined' ? globalThis.scheduler : null
    if (sch && typeof sch.postTask === 'function') {
      sch.postTask(() => resolve(), { priority: 'user-visible' }).catch(() => resolve())
    } else if (_pageHidden()) {
      _yieldTask().then(resolve)
    } else {
      setTimeout(resolve, 0)
    }
  })
// A hidden page paints nothing, so an idle slice means nothing there: it takes
// a plain task (an idle callback's 2 s timeout is a throttled timer).
const _idleSlice = () =>
  new Promise((resolve) => {
    const sch = typeof globalThis !== 'undefined' ? globalThis.scheduler : null
    if (_pageHidden()) {
      _yieldTask().then(resolve)
    } else if (sch && typeof sch.postTask === 'function') {
      sch.postTask(() => resolve(), { priority: 'background' }).catch(() => resolve())
    } else if (
      typeof globalThis !== 'undefined' &&
      typeof globalThis.requestIdleCallback === 'function'
    ) {
      globalThis.requestIdleCallback(() => resolve(), { timeout: 2000 })
    } else {
      setTimeout(resolve, 0)
    }
  })
const _homeBoardIds = (root) => {
  const ids = new Set()
  const layout = root && root.userDashboardPrefs && root.userDashboardPrefs.grid_layout
  for (const e of Array.isArray(layout) ? layout : []) if (e && e.id) ids.add(String(e.id))
  const live = root && root._homeLayout
  for (const e of Array.isArray(live) ? live : []) if (e && e.id) ids.add(String(e.id))
  const panels = root && root.workspaceSettings && root.workspaceSettings.home_default_panels
  for (const id of Array.isArray(panels) ? panels : []) if (id) ids.add(String(id))
  // On a fresh board the app's widgets have not published yet. Its pending
  // ids already describe the same default-on set the home will render
  // (homePanelSet.js appDefaultWidgetIds): explicit panels intersect the
  // app's own ids; an omitted panel list means all its widgets.
  const apps = root && root.workspaceApps
  for (const app of Array.isArray(apps) ? apps : []) {
    if (!app) continue
    const defaults = Array.isArray(app.home_default_panels)
      ? new Set(app.home_default_panels)
      : null
    const declared = Array.isArray(app.widgets)
      ? app.widgets.map((w) => w && w.id)
      : Array.isArray(app.pendingWidgets)
        ? app.pendingWidgets
        : []
    for (const id of declared) if (id && (!defaults || defaults.has(id))) ids.add(String(id))
  }
  const hidden = root && root._homeHiddenWidgets
  if (hidden) for (const id of ids) if (hidden[id]) ids.delete(id)
  return ids
}
const _activationRank = (entry, boardIds) => {
  const feeds = Array.isArray(entry.homeProviders)
    ? entry.homeProviders
    : _homeProvidersOf(entry.project)
  if (feeds.length) return 0
  const widgets = Array.isArray(entry.project && entry.project.widgets) ? entry.project.widgets : []
  if (widgets.some((w) => w && boardIds.has(String(w.id)))) return 1
  return 2
}
let _activationQueueSeq = 0
const _runAppActivations = async (root, onDrain) => {
  const seq = ++_activationQueueSeq
  const boardIds = _homeBoardIds(root)
  const pending = [..._appActivations.values()].filter((e) => e.root === root && !e.done)
  pending.sort((a, b) => _activationRank(a, boardIds) - _activationRank(b, boardIds))
  for (const entry of pending) {
    await (_activationRank(entry, boardIds) < 2 ? _taskSlice() : _idleSlice())
    if (seq !== _activationQueueSeq) return
    if (_appActivations.get(entry.ledgerId) !== entry || entry.done) continue
    try {
      await _activateWorkspaceApp(entry)
    } catch (_) {
      /* logged by _activateWorkspaceApp; the placeholder shows the error */
    }
  }
  if (seq === _activationQueueSeq && typeof onDrain === 'function') onDrain()
}
const _activationsPending = (root) =>
  [..._appActivations.values()].some((e) => e.root === root && !e.done)
// Set up every app of this root that is still waiting, now (one round; a
// failed app stays on its placeholder). For callers that need the real pages
// at once — tests, a console check — never the boot path.
export const whenAppsSetUp = async (root) => {
  const pending = [..._appActivations.values()].filter((e) => e.root === root && !e.done)
  await Promise.all(pending.map((e) => _activateWorkspaceApp(e).catch(() => false)))
  return !_activationsPending(root)
}

// ── Inject / teardown ─────────────────────────────────────────────────────────

// Currently-injected module + its context keys, so a switch can remove EXACTLY
// what it added (module-level: one app instance, one active module at a time).
// _injectedStateKeys tracks the root state keys the module seeded (declared
// stateKeys that were empty at inject time) so teardown clears exactly those.
let _activeProjectId = null
// The workspace the active module was loaded FOR — rebound on every successful
// pass. Lets a failing LATER pass tell "the live module's own workspace hit a
// transient blip (keep it)" apart from "we're switching somewhere else (tear it
// down)"; without it every transient scope miss destroyed + re-injected a
// healthy module (2026-07-30 GreenIG duplicate-widgets loop).
let _moduleLoadedWorkspaceId = null
let _injectedComponentKeys = []
let _injectedFunctionKeys = []
let _injectedPageKeys = []
// WS-MODULE-OWN-KEYS-STALE-ACROSS-ORG-SWITCH-1 — the BAGS this registration
// actually wrote into, captured AT INJECT TIME (see _captureBags).
let _injectedBags = null
// The takeover module's translation merge ledger (_mergeModuleTranslations).
let _injectedTranslations = null
let _injectedStateKeys = []
// key → the DECLARED seed value it was injected with (manifest `seeds[key]`,
// or [] when undeclared). WS-SWITCH-RESET-SHAPE (tickets/opus.md workspace-
// switch enumeration, 2026-08-18): _clearState used to reset EVERY tracked
// key to a hardcoded [] — wrong-typed for a key whose declared empty value
// is null or an object: [] is empty to a .length check but TRUTHY to
// `if (!key)`, so emptiness guards saw a survivor after a switch. The reset
// now writes the correctly-typed empty per seed (see _emptyForSeed).
let _injectedStateSeeds = {}

// The takeover module's manifest `actions` + the stash object it wrote to
// root.workspaceProject — cached module-side so loadWorkspaceApps can compose
// the ONE merged action registry / AI-surface stash over takeover + additive
// apps (see _composeAiStash). Cleared with the rest of the takeover teardown.
let _takeoverActions = null
let _takeoverStash = null

// BOOT-RESHUFFLE F4 — identical-republish guards. The activation chain
// (project → apps → config) runs from up to four independent drivers per boot
// (boot chain, auth `_ensureWorkspaceModule`, the switch pipeline, converge
// retries); each publish used to re-render GridContainer + HeaderTabs + every
// moduleRoutes subscriber even when the content was byte-identical. A
// stringify fingerprint of the outgoing payload (functions drop out — a
// module version pins them, so data equality implies function equality)
// makes the repeat passes cheap no-ops. Publish sites additionally require
// the live root value to be PRESENT — the switch registry wipes them to
// null/sentinel, and a wiped root must republish regardless of fingerprint.
let _lastTakeoverFp = null
let _lastAppsFp = null
const _publishFp = (parts) => {
  try {
    return JSON.stringify(parts)
  } catch (_) {
    return null // unfingerprintable → always republish
  }
}

// ── Additive workspace-apps (plural) ────────────────────────────────────────
// The single-module machinery above owns the ONE full-takeover module
// (`settings.workspaceModule`): skin + home panels + navbar-replace. That stays
// single by nature — one <html> design system, one home layout.
//
// `settings.workspaceApps[]` is the ADDITIVE plural: any number of apps that
// each contribute navbar entries / pages / components / a launcher tile WITHOUT
// taking the workspace over. They load in PARALLEL to the takeover module via
// loadWorkspaceApps() and keep their own per-app injection ledger so removing
// one app tears down EXACTLY its keys and leaves the others (and the shell, and
// the takeover module) intact. Keyed by appId (`owner/key`).
const _injectedByApp = new Map() // appId → { components:[], functions:[], pages:[] }
let _appsLoadSeq = 0
// Outcome of the last COMPLETED loadWorkspaceApps pass — the additive-apps
// mirror of `_lastLoadOutcome` above. The takeover loader always had a
// transient-vs-definitive verdict + a converge retry; the apps loader silently
// settled EVERY pass, so a cold boot whose scope hadn't converged yet (org ↔
// workspace mismatch while a cross-org /w/<slug> URL reconciles, or a failed
// workspace-record fetch) published `workspaceApps: []` + latched
// `_appRoutesSettled` and NEVER retried — 0 of N installed apps for the whole
// session (natali 0-of-13, 2026-08-06; measured live: record eventually holds
// 13 refs, zero /core/projects fetches ever issued).
//   'idle'       — never ran (nothing expected; definitive for converge)
//   'active'     — every installed app loaded (definitive)
//   'partial'    — some apps loaded, others failed — retry without removing ready apps
//   'empty'      — settings resolved for this org's workspace and carry no app
//                  refs (definitive)
//   'unresolved' — scope not determinable this pass (no workspace, org guard
//                  blocking mid-convergence, record fetch failed) — transient
//   'error'      — refs exist but EVERY app failed to load — transient
let _lastAppsOutcome = 'idle'
export const getAppsLoadOutcome = () => _lastAppsOutcome
// Synthetic ledger id for the shared capability bridges (moduleData / …), which
// are built over the UNION of all apps' grants rather than per-app so multiple
// record-using apps coexist. Tracked in _injectedByApp so teardown is uniform.
const _CAPS_LEDGER = '__apps_caps__'

// WS-MODULE-OWN-KEYS-STALE-ACROSS-ORG-SWITCH-1 — the bags a registration wrote
// into, captured at INJECT time and read back at teardown.
//
// Teardown must never RE-RESOLVE the context to find where its keys live:
// `_ctx(root)` reads `root.__element.context`, and the root STATE is replaced
// under the loader on an org switch (scopes.js PHASE-1 `root.replace`) — the
// `__element` link is transiently absent, so `_ctx` returned null. The old
// teardown then deleted nothing, cleared its key list ANYWAY, and the keys were
// orphaned in the live bag with no owner: the module's next registration saw
// them as a "key collision skipped", dropped its own fresh copies, and the
// PREVIOUS tenant's module code stayed bound across the tenant boundary (25
// such warns per org return — every module-own component + function).
//
// Same mechanism as the 6a7cdafd3 caps-ledger eviction, one layer earlier: a
// key is owned by the registration that wrote it, and ownership is released
// only by really deleting it — never by losing track of the bag.
const _captureBags = (ctx, routedEl) => ({
  components: (ctx && ctx.components) || null,
  functions: (ctx && ctx.functions) || null,
  pages: (ctx && ctx.pages) || null,
  routes: (routedEl && routedEl.routes) || null
})

// The bag to delete from: the one written at inject, else the live context's
// (an older ledger, or a registration made before the bags were captured).
const _bagOr = (bags, name, fallback) => (bags && bags[name]) || fallback || null

const _moduleTeardown = (root) => {
  const ctx = _ctx(root)
  const comps = _bagOr(_injectedBags, 'components', ctx && ctx.components)
  const fns = _bagOr(_injectedBags, 'functions', ctx && ctx.functions)
  const pages = _bagOr(_injectedBags, 'pages', ctx && ctx.pages)
  if (comps) for (const k of _injectedComponentKeys) delete comps[k]
  if (fns) for (const k of _injectedFunctionKeys) delete fns[k]
  if (pages) for (const k of _injectedPageKeys) delete pages[k]
  _liveModulePages = null
  if (root && typeof root._modulePagesLiveGet === 'function') root._modulePagesLiveGet = null
  if (root && typeof root._moduleFnGet === 'function') root._moduleFnGet = null
  // Pull the module routes back out of the live router map so other workspaces
  // never resolve /policies, /claims, … (matches the components/functions teardown).
  const routedEl = _routedEl(root)
  const routes = _bagOr(_injectedBags, 'routes', routedEl && routedEl.routes)
  if (routes) for (const k of _injectedPageKeys) delete routes[k]
  _unmergeModuleTranslations(_injectedTranslations)
  _injectedTranslations = null
  _injectedComponentKeys = []
  _injectedFunctionKeys = []
  _injectedPageKeys = []
  _injectedBags = null
  _activeProjectId = null
  _moduleLoadedWorkspaceId = null
  _takeoverActions = null
}

// Clear the module's state footprint: the stash, any parked approval action,
// and EXACTLY the state keys this module seeded (tracked at inject) — never a
// hardcoded list, so a module's domain state can't leak across workspaces and
// shell-owned keys are never touched. Each key resets to the correctly-TYPED
// empty for its declared seed — [] for array seeds, null otherwise — NOT the
// seed itself (a seed can carry demo content, which must not survive into a
// module-less workspace) and NOT a hardcoded [] (wrong-typed for object/null
// seeds: [] is empty to .length but TRUTHY to `if (!key)`, so emptiness
// guards saw a survivor — see _injectedStateSeeds).
const _emptyForSeed = (v) => (v === undefined || Array.isArray(v) ? [] : null)

// WS-MODULE-STATE-SCOPE-1 — reset + evict ONE owner's module state store.
// Elements that still hold a reference to the outgoing store (a subtree that
// has not unmounted yet) read the declared empties instead of the previous
// tenant's rows; the eviction makes the next inject start from nothing.
// `ownerId` keys the scoped-keys REGISTRY (the takeover's project id, or an
// additive app's tenant-scoped ledger id); `projectId` keys the store (always
// the module project id). They differ for an additive app.
const _clearModuleStatePlane = (ownerId, root, projectId) => {
  if (ownerId == null) return
  const storeKey = projectId === undefined ? ownerId : projectId
  const store = _liveModuleState(storeKey, root)
  if (store) {
    try {
      const patch = moduleScopedWipeFor(ownerId, store)
      if (Object.keys(patch).length) store.update(patch)
    } catch (e) {
      console.warn('[workspaceModule] module state wipe failed:', ownerId, e && e.message)
    }
  }
  if (storeKey != null) _moduleStateCache.delete(storeKey)
}

const _clearState = (root) => {
  const patch = { workspaceProject: null, _aiAction: null }
  for (const k of _injectedStateKeys) patch[k] = _emptyForSeed(_injectedStateSeeds[k])
  // WS-MODULE-SCOPED-KEYS-CONTRACT-1: the module's DECLARED scoped keys reset
  // too — regardless of the "only seed when empty" gate above (a declaration
  // means "mine"), typed-empty per live value or the declared `empty`. Same
  // patch the PHASE-1 switch wipe already spread (scopes.js) — idempotent.
  Object.assign(patch, moduleScopedWipeFor(_activeProjectId, root))
  // WS-MODULE-STATE-SCOPE-1 — the module's OWN state plane resets at the same
  // beat. Its rows live on its module store now, so the declared-key patch is
  // computed against THAT bag, and the store is then evicted so the next inject
  // builds a fresh one. Eviction is what makes the tenant boundary total: a
  // module that declares no `scopedKeys` used to leak its rows across a switch
  // on the shell root and would otherwise leak them on its own store instead.
  _clearModuleStatePlane(_activeProjectId, root)
  unregisterModuleScopedKeys(_activeProjectId)
  _injectedStateKeys = []
  _injectedStateSeeds = {}
  _takeoverStash = null
  // Torn-down module ⇒ the next publish is never an "identical republish".
  _lastTakeoverFp = null
  _lastAppsFp = null
  root.update(patch)
}

// ── Ordered module teardown (BF-MODULE-UNREGISTERED-COMPONENTS-CONSOLE-1) ────
// The module's rail and home widgets are mounted by the SHELL (app.js
// `ModuleRailHost.Body.__mountModuleRail`, pages/main.js `ModuleWidgets`) but
// their factories call MODULE functions (`el.call('bfPersona', …)`). Teardown
// used to run `_moduleTeardown(root)` (delete the injected ctx.functions keys) and
// only THEN `_clearState(root)`, whose `root.update({ workspaceProject: null })`
// re-renders every `subscribeTo: ['workspaceProject']` subscriber — with the
// rail still mounted (the `__railSig` guard mounts but never unmounts). Every
// such factory then called a name deleted the same tick: measured on a
// cross-org switch away from Bellforge, 8 `is not registered` warnings, 6
// `factory threw` errors and the `reading 'accent'` TypeError (an unregistered
// call returns undefined) — 28 console errors in one pass.
//
// The order is the fix, at the layer that owns the mount: unmount the
// module-owned subtrees, publish the null state so every remaining subscriber
// re-renders module-less WHILE the functions still resolve, and only then
// delete the injected entries. Bellforge (and every other module) needs no
// defensive guard in its own call sites.
//
// `root.update` renders synchronously, so the publish has settled by the time
// `_moduleTeardown` runs — there is no tick to await.
const _unmountModuleSurfaces = (root) => {
  const elx = root && (root.__element || (root.getRoot && root.getRoot()))
  const body = elx && elx.ModuleRailHost && elx.ModuleRailHost.Body
  // Only a BUILT rail host can hold a mounted rail. The shell root's onCreate
  // kicks the first module load before the root renders its children (app.js,
  // "Kick the private-data load NOW"), and that first load tears down before it
  // injects — so on a cold boot `ModuleRailHost.Body` can still be the child
  // DEFINITION from app.js: a plain object with no node and no element methods.
  // Its `set({})` fallback threw `set is not a function` on every cold load of
  // a module workspace, and the signature write below landed on the definition
  // (functions/workspaceProjects.railUnmountBoot.test.js).
  if (!body || !body.node) return
  // Clear the mount signature FIRST: the remount on switch-back is driven by
  // `__railSig === rail` being false, and an unmount that left the old
  // signature behind would keep the rail permanently empty.
  body.__railSig = null
  try {
    if (typeof body.removeContent === 'function') body.removeContent()
    else body.set({})
  } catch (err) {
    console.warn('[workspaceModule] rail unmount failed:', err && err.message)
  }
}

// The ONE teardown entry point for the takeover module — every switch-away
// path calls this, never `_moduleTeardown` + `_clearState` by hand (three call sites
// used to repeat the pair, and each repeat was a chance to get the order
// wrong).
const _teardownModule = (root) => {
  // WS-MODULE-SCOPED-KEYS-CONTRACT-1: the module's `hooks.onWorkspaceSwitch`
  // fires FIRST — its functions still resolve and its surfaces are still
  // mounted, so a late-write fence can arm before anything is torn down.
  _runModuleSwitchHook(root, _activeProjectId)
  _unmountModuleSurfaces(root)
  _clearState(root)
  _moduleTeardown(root)
}

// Resolve a hook NAME for its owner. A module owner (the takeover's project
// id, or an additive install's ledger id) resolves through ITS OWN module
// scope, own property only: two apps may name the same hook function, and the
// root bag keeps only the first copy (_injectEntries skips the rest), so a
// root lookup ran app A's release code on app B's switch
// (moduleSwitchHookOwnScope.test.js). Only an owner with no module scope
// (shell-owned) falls back to the root bag.
// The OWN function bag of a module owner — a project id (takeover, or an
// additive app's `workspaceApps[].id`) or an additive install's ledger id —
// or null for an owner with no module scope (shell-owned).
const _ownerScopeFns = (ownerId) => {
  if (ownerId == null) return null
  // An additive install's owner id IS its tenant-scoped ledger id.
  const ledgerId = ownerId
  const led = _injectedByApp.get(ledgerId)
  const projectId = led && led.projectId != null ? led.projectId : ownerId
  const hit = _moduleDsCtxCache.get(projectId) || _moduleScopeCache.get(projectId)
  return (hit && hit.modCtx && hit.modCtx.functions) || null
}
export const _switchHookResolver = (root, ownerId) => {
  const ctx = _ctx(root)
  const scoped = _ownerScopeFns(ownerId)
  if (scoped) {
    return (name) => (Object.prototype.hasOwnProperty.call(scoped, name) ? scoped[name] : undefined)
  }
  return (name) => (ctx && ctx.functions ? ctx.functions[name] : undefined)
}

// Resolve + run one owner's tenant-boundary hook (see _switchHookResolver),
// with the root ELEMENT as `this` when one exists and the root STATE as the
// first arg.
const _runModuleSwitchHook = (root, ownerId, reason = 'teardown') => {
  if (!ownerId) return false
  const elx = (root && (root.__element || (root.getRoot && root.getRoot()))) || null
  return runModuleSwitchHook(ownerId, {
    resolveFn: _switchHookResolver(root, ownerId),
    thisArg: elx,
    root,
    reason
  })
}

// Guarded per-bag injection with the SKIP-EXISTING-WITH-WARN collision policy:
// a module can never shadow a shell key, and only actually-set keys enter the
// injected-key arrays — so teardown is provably exact.
// Who already holds this key in the root bags? Derived from the ledgers that
// already exist — no second bookkeeping structure to keep in sync, and no
// module-scope memo to survive a tenant switch. `kind` is 'components' or
// 'functions'.
const _rootKeyOwner = (kind, key) => {
  const takeover = kind === 'components' ? _injectedComponentKeys : _injectedFunctionKeys
  if (takeover && takeover.indexOf(key) !== -1) return String(_activeProjectId || 'workspaceModule')
  try {
    for (const [ledgerId, led] of _injectedByApp.entries()) {
      const list = led && led[kind]
      if (Array.isArray(list) && list.indexOf(key) !== -1) return String(ledgerId)
    }
  } catch (_) {
    /* ledger shape is best-effort diagnostics only */
  }
  return 'shell'
}

// Guarded per-bag injection with the SKIP-EXISTING-WITH-WARN collision policy
// for the ROOT bags. Since WS-MODULE-FN-NAME-COLLISION-CROSS-APP the skip is
// no longer a silent mis-binding — the module keeps its own copy in its own
// scope (see _moduleScopeFor) — so this warn is a DIAGNOSTIC, and it now names
// both sides: which module wanted the key and which one already holds it. The
// anonymous form cost a live customer a working Scheduling app because nobody
// could tell "crm and scheduling both define natEnsureLoaded" from "a module
// tried to shadow a shell key".
const _injectEntries = (bag, entries, injectedKeys, kind, owner) => {
  for (const k of Object.keys(entries)) {
    if (k in bag) {
      // The SAME binding is not a collision: the takeover bridge hands the
      // module the shell's own deep-link readers (readPageParam, …) that the
      // root already registers, and the module scope still gets them through
      // _moduleScopeFor. Read through the descriptor so a lazy root accessor
      // is never forced here. (moduleBridgeSameBindingNoCollision.test.js)
      // A shared library function that two modules link is one binding too:
      // the revive binds one library text to one function (_bindLibraryFnTexts).
      const held = Object.getOwnPropertyDescriptor(bag, k)
      const offered = Object.getOwnPropertyDescriptor(entries, k)
      if (
        held &&
        offered &&
        'value' in held &&
        'value' in offered &&
        held.value === offered.value
      ) {
        continue
      }
      // A per-app CONTRACT function (HOME_SIGNALS_CONTRACT v1: every home
      // feeder exports `homeSignals` / `homeUpcoming` under that exact name)
      // is read only from each app's OWN module scope (homeSignals.js,
      // homeNextUp.js — hasOwnProperty on the app's functions), never from
      // the root bag, so a second app's copy is by design, not a collision.
      // The names come from the contract list itself (_HOME_PROVIDER_FNS).
      if (kind === 'functions' && _HOME_PROVIDER_FNS.some(([, name]) => name === k)) {
        continue
      }
      if (kind && owner) {
        console.warn(
          '[workspaceModule] key collision skipped:',
          k,
          `— ${owner} also defines it; root keeps ${_rootKeyOwner(kind, k)}'s copy` +
            ` (${owner} resolves its own inside its module scope)`
        )
      } else {
        console.warn('[workspaceModule] key collision skipped:', k)
      }
      continue
    }
    // A lazy entry forwards through its own accessor (boot fix step 4): the
    // root bag reads the one revived value on first use, never at boot.
    const d = Object.getOwnPropertyDescriptor(entries, k)
    if (d && typeof d.get === 'function') {
      Object.defineProperty(bag, k, {
        get: d.get,
        set: d.set,
        enumerable: true,
        configurable: true
      })
    } else {
      bag[k] = entries[k]
    }
    injectedKeys.push(k)
  }
}

// ── Module translations → the shell's polyglot map ──────────────────────────
//
// A module ships its copy as `designSystem.lang` — `{ en: {...}, ka: {...} }`,
// flat undotted keys. The loader injected the module's components, functions,
// pages and design system, but nothing carried that bundle into
// `context.polyglot.translations`, the ONE map `el.call('polyglot', key)` and
// `{{ key | polyglot }}` read (smbls plugins/polyglot `translate`). Every module
// therefore seeded the map itself (aihouse/admin `ahSeedLang`, 2026-09-23), and
// a module that did not rendered its raw keys. The merge belongs here, once per
// module load, beside the other injections.
//
// The module context is `Object.create(ctx)` (see _moduleScopeFor), so its
// `polyglot` IS the shell's object — one write reaches module pages, module
// widget bodies AND the shell chrome that shows a module string (a widget title
// in the home-board header renders under the SHELL context).
//
// NON-DESTRUCTIVE: a key the host already holds is never overwritten. Shell
// keys are dotted and module keys are not, so they should never meet — the
// guard holds even when they do. EXACT TEARDOWN: every key this pass wrote is
// ledgered with its value and removed on switch-away, but only while the map
// still holds that same value; a language map the merge had to create goes
// again when it is empty (an empty `ka` map would otherwise add a language to
// `getLanguages()`). The ledger keeps the `translations` OBJECT, not the
// per-language maps: the shell's setLang/boot REPLACE `translations[lang]`
// with a spread copy, so a held per-language map would go stale.
const _isTranslationMap = (v) => !!v && typeof v === 'object' && !Array.isArray(v)
export const _mergeModuleTranslations = (ctx, project) => {
  const bundle = project && project.designSystem && project.designSystem.lang
  const poly = ctx && ctx.polyglot
  if (!_isTranslationMap(bundle) || !poly || typeof poly !== 'object') return null
  if (!_isTranslationMap(poly.translations)) poly.translations = {}
  const translations = poly.translations
  const added = []
  const createdLangs = []
  for (const lang of Object.keys(bundle)) {
    const src = bundle[lang]
    if (!_isTranslationMap(src)) continue
    if (!_isTranslationMap(translations[lang])) {
      translations[lang] = {}
      createdLangs.push(lang)
    }
    const target = translations[lang]
    for (const key of Object.keys(src)) {
      const value = src[key]
      if (value === undefined || value === null || typeof value === 'function') continue
      if (target[key] !== undefined) continue
      target[key] = value
      added.push([lang, key, value])
    }
  }
  return { translations, added, createdLangs }
}
// Step 3a: the server `nav.lang` merges per installed app, keyed by the tenant
// ledger id, with its content hash (a changed copy re-merges).
const _navLangLedgers = new Map()
const _dropNavLang = (ledgerId) => {
  const held = _navLangLedgers.get(ledgerId)
  _navLangLedgers.delete(ledgerId)
  if (held) _unmergeModuleTranslations(held.ledger)
}
export const _unmergeModuleTranslations = (ledger) => {
  if (!ledger || !_isTranslationMap(ledger.translations)) return 0
  const translations = ledger.translations
  let removed = 0
  for (const [lang, key, value] of ledger.added || []) {
    const map = translations[lang]
    if (_isTranslationMap(map) && map[key] === value) {
      delete map[key]
      removed++
    }
  }
  for (const lang of ledger.createdLangs || []) {
    const map = translations[lang]
    if (_isTranslationMap(map) && !Object.keys(map).length) delete translations[lang]
  }
  return removed
}

// Monotonic load token — every loadWorkspaceProject entry claims the next seq;
// after EVERY await the call re-checks it and bails if a newer load started
// (mid-flight workspace switch race: the loser must not inject/stash over the
// winner).
let _loadSeq = 0

// ── Single-flight per loader (BOOT-RESHUFFLE F4) ────────────────────────────
// Boot fires up to four entrants at the same scope within moments of each
// other (_projectChain, auth's _ensureWorkspaceModule, onWorkspaceChange,
// converge retries) and each used to claim a fresh _loadSeq — aborting the
// pass already in flight and re-running the whole project→apps fetch chain.
// Same key + same root while a pass is IN FLIGHT → share its promise WITHOUT
// bumping the seq (no abort, no rerun). A different key (real switch, or an
// install/uninstall that patched the local settings bag first —
// workspaceApps.js, moduleConsole.js) starts a fresh pass and takes over the
// slot, so the seq/isStale supersession semantics are untouched. Single SLOT
// per loader, deliberately not a map: under the supersede model only the
// LATEST pass is ever shareable — a map would hand a doomed stale promise to
// an A→B→A switch. Slot clears on settle: this is dedupe, never a cache.
let _projectFlightKey = null
let _projectFlight = null
let _projectFlightRoot = null
let _appsFlightKey = null
let _appsFlight = null
let _appsFlightRoot = null
// BOOT single-flight across SEQUENTIAL entrants. The slots above share a pass
// only while it runs. A cold boot has three entrants (auth.js post-sign-in
// chain, loadPrivateData's full-record converge, loadPrivateData's boot
// chain), and on a slow boot a later entrant arrives AFTER the first pass
// settled: it then ran a second full pass, re-sent every app's channel
// resolution (getProjectByKey + listEnvironments) and flipped the loader back
// to "Opening apps…" (Unreal dev twin 2026-09-29: pass A 38.7-46.1 s, pass B
// 55.4-57.1 s, six duplicate requests). A boot entrant (`{ boot: true }`)
// reuses a DEFINITIVE settled pass for the same scope key and root. Every
// other caller (install, uninstall, console reload, converge retry) runs a
// fresh pass, and any fresh pass start clears the slot.
let _appsSettled = null
let _projectSettled = null
const _loaderFlightKey = (root) => {
  const ws = (root && root.workspace) || {}
  const wsId = String(ws.id || ws._id || '')
  const orgId = _lc(String((root && root.org && (root.org.id || root.org._id)) || ''))
  // Lean-claims boots carry no settings bag — every boot entrant keys '' for
  // both sigs and shares one chain. Flows that patch the bag locally before
  // reloading key differently on purpose: they MUST supersede.
  //
  // DECISION site (spec §5), and the one place readiness has to enter the KEY
  // itself. The sigs below cannot distinguish "no settings because the record
  // hasn't arrived" from "settings arrived and carry no module/apps" — both
  // produce `''|''`. With the dedup slot keyed on the sigs alone, the
  // pre-arrival flight and the post-arrival flight (the §6 late-arrival
  // re-render, which re-enters these loaders precisely BECAUSE the record just
  // landed) collapse into one: the second call is handed the first's promise
  // and the full record is never read. `readySig` is the disambiguator — every
  // lean entrant still shares one chain ('lean'), and the moment the record
  // completes the key flips to 'full' and supersedes it. Load-bearing for the
  // late-arrival re-render; do not fold it back into the sigs.
  //
  // The sigs themselves deliberately keep reading the RAW held bag rather than
  // the accessor's (which is null whenever !ready): the key is a fingerprint of
  // what THIS pass would see, and the local-patch-then-reload flows named above
  // must keep superseding an in-flight pass on either shape. Readiness is the
  // accessor's job; the fingerprint is not.
  const ready = shellWorkspaceSettings(root).ready
  const readySig = ready ? 'full' : 'lean'
  const settings = (ws && ws.settings) || null
  const ref = settings ? _publishedRef(settings) : null
  const moduleSig = ref ? `${_appId(ref)}@${ref.version || ref.channel || ''}` : ''
  const appsSig = settings ? _appRefs(settings).map(_appId).join(',') : ''
  return `${orgId}|${wsId}|${readySig}|${moduleSig}|${appsSig}`
}

// Why the most recent module load REFUSED (sandboxed tier / failed audit),
// captured at the refusal points in _loadPublishedModule / _fetchAndBuildModule
// and surfaced by loadWorkspaceProject. Without this a configured-but-unloadable
// module is a silent no-op — the app just never appears, no user-facing signal
// (tickets/workspace.md WS-APPS bug #4). Reset per loadWorkspaceProject entry.
let _lastRefusal = null

// Whether the most recent null module resolution is a DEFINITIVE content
// verdict (the payload arrived and was rejected: manifest missing, id
// mismatch — set where _normalizeManifest returns null on fetched data) as
// opposed to a TRANSIENT failure (fetch/parse/channel error) that the
// converge retry below should re-attempt. Reset per loadWorkspaceProject
// entry alongside _lastRefusal.
let _lastNullDefinitive = false

// Outcome of the most recent COMPLETED loadWorkspaceProject pass — separates a
// DEFINITIVE result from a transient one:
//   'active'     — a module is injected and live
//   'no-module'  — the settings were RESOLVED and carry no module reference
//                  (a legitimately plain workspace — final, never retried)
//   'refused'    — the loader refused for a known policy reason (sandboxed
//                  tier / failed publish-time audit — final, never retried)
//   'error'      — a module IS referenced but its load failed (fetch/parse/
//                  channel resolution) — transient, retried by the converge
//                  loop below
//   'unresolved' — the workspace SCOPE couldn't be determined on this pass
//                  (no workspace resolved yet, org↔workspace mismatch while an
//                  org switch settles, workspace-record fetch failed) —
//                  transient, retried
// Read by auth.js's _ensureWorkspaceModule so its per-workspace latch only
// sticks on definitive outcomes. Stale (superseded-seq) passes leave it alone.
let _lastLoadOutcome = 'idle'
export const getModuleLoadOutcome = () => _lastLoadOutcome

// ── Cold-boot convergence retry ──────────────────────────────────────────────
// On a COLD boot the module load races the auth/scope convergence:
// root.workspace and root.org settle over SEVERAL async ticks (bootShell's
// eager fetch → auth claims tick re-lean → SDK workspaces-list fallback →
// cross-org drift heal), while loadPrivateData's boot chain runs
// loadWorkspaceProject exactly ONCE against whatever tuple happens to be
// current. Every transient miss (stale ?ws= pointer workspace, org guard
// blocking mid-convergence, a failed workspace-record fetch on token churn)
// used to settle SILENTLY into a plain workspace with no retry — while
// applyWorkspaceConfig, re-run later by other triggers, still painted the
// workspace's theme + home panels. Net effect: full module chrome, zero module
// code/data — the GreenIG "widgets render empty on sign-in" bug.
//
// This loop is the self-heal: scheduled ONLY when a pass ends transient
// ('unresolved'/'error'), it re-runs the full activation chain (project →
// apps → config) on a short backoff until the scope converges, a DEFINITIVE
// outcome lands ('active'/'no-module'/'refused'), or attempts exhaust.
// Re-entrancy safe: loadWorkspaceProject's _loadSeq + _activeProjectId dedup
// make a retry racing a genuine switch load idempotent, and the singleton
// guard collapses overlapping schedules to one loop.
let _convergeGen = 0
let _convergeRunning = null
let _convergeOwner = null
const _moduleRootAlive = (root, element) =>
  !!root &&
  (root.__element || null) === element &&
  !element?.__destroyed &&
  element?.__ref !== null &&
  (!element?.state || (element.state.root || element.state) === root)
const _moduleOwnerCurrent = (owner) =>
  !!owner && owner === _convergeOwner && _moduleRootAlive(owner.root, owner.element)

// Root replacement, not a workspace change on one root, transfers retry
// ownership. Claim before either loader awaits so an older retry cannot
// advance a sequence over the replacement's current pass.
const _claimModuleOwner = (root, sdk) => {
  if (!root) return null
  const element = root.__element || null
  if (!_moduleRootAlive(root, element)) return null
  if (_convergeOwner?.root !== root || _convergeOwner.element !== element) {
    _convergeOwner = { root, element, sdk }
    _convergeGen++
    _loadSeq++
    _appsLoadSeq++
    // Do not let A -> B -> A share A's cancelled promise. Payload caches
    // remain valid; only the two active pass slots lose their old owner.
    _projectFlight = null
    _projectFlightKey = null
    _projectFlightRoot = null
    _appsFlight = null
    _appsFlightKey = null
    _appsFlightRoot = null
    _appsSettled = null
    _projectSettled = null
  } else if (sdk) {
    _convergeOwner.sdk = sdk
  }
  return _convergeOwner
}
// First retry fires fast — the scope usually settles within the first second
// of boot (the very next auth tick), so a quick first attempt closes the
// visible empty-chrome window; later attempts back off for slow convergence
// (token re-mint, workspaces-list fallback). ~15s total bound.
let _convergeDelays = [400, 1000, 2000, 4000, 8000]
export const __setModuleConvergeDelaysForTests = (delays) => {
  _convergeDelays = delays
}

// Browser-first sleep; unref'd under node so a pending retry never holds a
// test process open.
const _sleep = (ms) =>
  new Promise((resolve) => {
    const t = setTimeout(resolve, ms)
    if (t && typeof t.unref === 'function') t.unref()
  })

const _scheduleModuleConverge = (root) => {
  const owner = _convergeOwner
  if (!_moduleOwnerCurrent(owner) || owner.root !== root || _convergeRunning === owner) return
  _convergeRunning = owner
  const gen = _convergeGen
  const current = () =>
    gen === _convergeGen && _moduleOwnerCurrent(owner) && root.signedIn !== false
  ;(async () => {
    try {
      // The APPS loader has its own transient outcomes ('unresolved'/'error')
      // and MUST converge independently of the takeover module: a workspace
      // running only additive apps (no takeover module) ends every module
      // pass on the DEFINITIVE 'no-module' — bailing on that alone meant the
      // apps loader was never retried, permanently stranding an app-only
      // workspace whose boot pass raced the org/scope convergence at 0 loaded
      // apps (natali 0-of-13, 2026-08-06).
      // 'blocked' is deliberately ABSENT: it means every unloaded app carries a
      // content verdict, so each further pass would re-fetch the same payload
      // and refuse it again. Adding it here re-arms five wasted reload passes.
      const appsPending = () => ['unresolved', 'error', 'partial'].includes(_lastAppsOutcome)
      for (const delay of _convergeDelays) {
        await _sleep(delay)
        if (!current()) return
        // A module became active (this workspace's or a switched-to one) or a
        // definitive outcome landed meanwhile — converged, stop (unless the
        // apps side is still owed a successful pass).
        if (_activeProjectId && root.workspaceProject && !appsPending()) return
        if (
          (_lastLoadOutcome === 'no-module' || _lastLoadOutcome === 'refused') &&
          !appsPending()
        ) {
          return
        }
        // A real workspace switch owns the loaders while it runs — stand down
        // for this attempt; the next delay re-checks the settled state.
        if (root._wsSwitching) continue
        const project = await loadWorkspaceProject(root, owner.sdk)
        if (!current()) return
        const moduleDone =
          !!project ||
          (_activeProjectId && root.workspaceProject) ||
          _lastLoadOutcome === 'no-module' ||
          _lastLoadOutcome === 'refused'
        if (project || (moduleDone && appsPending())) {
          // Late activation: complete the same chain every activation path
          // runs so widgets/navbar/apps resolve from the freshly-injected
          // module (loadWorkspaceApps also re-runs its own settings read).
          await loadWorkspaceApps(root, owner.sdk)
          if (!current()) return
          try {
            applyWorkspaceConfig(root)
          } catch (e) {
            console.warn('[workspaceProject] converge applyWorkspaceConfig failed', e?.message || e)
          }
          // Converged only when the apps side is definitive too — otherwise
          // keep backing off (the module memo makes the re-runs cheap).
          if (!appsPending()) return
          continue
        }
        if (moduleDone && !appsPending()) return
      }
      if (!current()) return
      console.warn(
        '[workspaceProject] module scope never converged — giving up until the next trigger'
      )
      if (_lastLoadOutcome === 'unresolved' || _lastLoadOutcome === 'error') {
        updateWorkspaceActivation(root, 'module', { state: 'error' })
      }
      if (_lastAppsOutcome === 'unresolved') {
        updateWorkspaceActivation(root, 'apps', { state: 'error' })
      }
      // Exhausted with a transient outcome still standing: the loaders above
      // deliberately did NOT latch `_appRoutesSettled` on those passes, and
      // nothing else will — settle it now so the '/*' catch-all's 404
      // suppression (projectRedirect.js) doesn't hide genuinely nonexistent
      // paths behind a loader forever.
      try {
        root?.update?.({ _appRoutesSettled: true }, { preventFetch: true })
      } catch (_) {
        /* settle is best-effort */
      }
    } catch (e) {
      console.warn('[workspaceProject] module converge retry failed', e?.message || e)
    } finally {
      if (_convergeRunning === owner) _convergeRunning = null
    }
  })()
}

// Apply the active workspace's module to the live context. Async (fetch/import).
// Call BEFORE applyWorkspaceConfig so the config + pages resolve from the freshly
// applied module. Returns the normalized module (or null).
// ?page= recovery: a module page opened in the home PageSlot rides
// ?page=<route> in the URL (pages/main.js). The home page's own restore runs at
// page onCreate — usually BEFORE module pages inject (they aren't in the shared
// route map) — so re-apply it once the pages exist. Never overrides an
// already-open sheet.
//
// Called from BOTH loader paths. It used to live inline in
// `loadWorkspaceProject` only, so a workspace running additive apps (the
// `settings.workspaceApps` shape — no takeover module) never restored the
// param: every ?page=/warehouse-style deep link landed on Home and needed a
// nav click.
// Captured at bundle evaluation — BEFORE the boot router runs. The boot
// org-reconcile/normalize replaces the URL WITHOUT the query string, so by the
// time either loader below reaches _restorePageParam on a COLD deep link, the
// live location no longer carries ?page= and the restore silently no-oped
// (every cold ?page=/discover landed on Home; warm nav worked). One-shot: a
// later workspace switch must not resurrect a stale boot param.
let _bootPageParam = null
let _bootViewAsParam = null

// Capture is idempotent and NEVER clears an earlier capture: an empty query
// string means "nothing to read here" (the boot reconcile already rewrote
// the URL, or this is frank's node-side serialization pass), not "the param
// is absent". That rule replaces the module-level IIFE consts this file used
// to hold: an IIFE result is not statically evaluable, so the emitted
// `_restorePageParam` / `_restoreViewAsParam` read bindings the published
// payload never assigned and threw on every call
// (WORKSPACE-SHELL-FRANK-GATE-BLOCKS-REPUBLISH-1). A plain module `let` DOES
// lift into the emitted globalScope, so both loaders keep working there too:
// module-level code never runs in a published payload, hence the second
// capture call at the top of `_restorePageParam`.
const _captureBootParams = () => {
  try {
    const search =
      (typeof window !== 'undefined' && window.location && window.location.search) || ''
    if (!search) return
    const q = new URLSearchParams(search)
    _bootPageParam = q.get('page') ?? _bootPageParam
    _bootViewAsParam = q.get('viewAs') ?? _bootViewAsParam
  } catch (_) {}
}
_captureBootParams()
let _bootPageParamUsed = false

// ?viewAs= recovery — same shape as ?page= above, for a module's own View-as
// override (`chrome.viewAsKey`, e.g. bellforge's `bfPersonaOverride`). The
// override is a plain in-memory root-state key — it is deliberately NOT in
// WORKSPACE_SCOPED_KEYS (scopes.js), so it is not a tenant-boundary value —
// which means nothing re-seeds it after a hard reload, and any in-session
// workspace-change re-run can leave it looking reset for the brief window
// before the module's chrome republishes. Persisting the override's VALUE in
// the URL (generic — keyed by `chrome.viewAsKey`, not a module-specific
// name) makes View-as durable across BOTH a hard reload and an in-session
// re-hydration: whichever one happens, restoring here re-applies it the
// moment the module's chrome is resolvable again. The WRITE side lives in
// the shell (app.js `_syncShellRoute`) — this is the read/restore half,
// symmetric with the ?page= recovery beside it (RAIL-TRANSITION,
// tickets/fable.md).
let _bootViewAsParamUsed = false

const _restoreViewAsParam = (root) => {
  try {
    const key = moduleViewAsKey(root)
    if (!key) return
    // A live in-memory value (the user just clicked a persona chip this
    // session) always wins over a stale/boot URL param — never clobber it.
    if (root[key]) return
    const search =
      (typeof window !== 'undefined' && window.location && window.location.search) || ''
    const liveParam = search ? new URLSearchParams(search).get('viewAs') : null
    const value = liveParam || (!_bootViewAsParamUsed ? _bootViewAsParam : null)
    if (!value || value === 'admin') return
    _bootViewAsParamUsed = true
    root.update({ [key]: value })
  } catch (err) {
    console.warn('[workspaceProject] ?viewAs= restore failed', err)
  }
}

// tickets/sonnet.md MODULE-TEARDOWN-1/ROOT-CALL-REF-1 — one notification per
// DISTINCT missed page param, not per re-call (this function is called from
// both loader paths and re-runs on every workspace-module publish, so a
// naive check-every-time would toast on every retry).
let _lastPageParamMissNotified = null

export const _restorePageParam = (root, pages) => {
  try {
    _captureBootParams()
    const search =
      (typeof window !== 'undefined' && window.location && window.location.search) || ''
    const liveParam = search ? new URLSearchParams(search).get('page') : null
    const pageParam = liveParam || (!_bootPageParamUsed ? _bootPageParam : null)
    // A1-06 — resolve the PAGE, restore the ROUTE. A module deep link carries
    // the params that identify the record it opened
    // (`?page=/vault-rooms?item=<id>`), and an exact `pages[pageParam]` lookup
    // never matches one — so a reload of a shared deep link fell through to
    // the "page not available" branch and dropped the user back on the grid.
    // Same query/hash normalization PageSlot's `_defFor` and openAppPage
    // already use; `_canvasSlot` still receives the FULL route.
    const pageParamPath = pageParam ? String(pageParam).split('?')[0].split('#')[0] : null
    _restoreViewAsParam(root)
    if (pageParam && pages && (pages[pageParam] || pages[pageParamPath]) && !root._canvasSlot) {
      _bootPageParamUsed = true
      _lastPageParamMissNotified = null
      root.update({ _canvasSlot: pageParam })
      return
    }
    // Immersive landing: a member the module's chrome manifest puts in
    // immersive mode never sees the bare home board — no ?page= to restore
    // means they land on the module's own landing page (which carries the
    // module rail), on '/' only (a direct module ROUTE load keeps its page).
    if (!root._canvasSlot && moduleImmersiveActive(root)) {
      const landing = moduleImmersiveLanding(root)
      if (landing && pages && pages[landing]) {
        root.update({ _canvasSlot: landing })
        return
      }
    }
    // A ?page= that never matches ANY installed route used to fail here
    // completely silently — no error, no notice, the caller's own default
    // (Home) renders with zero explanation (measured live: a bare load on
    // `?page=/theses` picked a workspace with 0 installed apps and landed on
    // Home with nothing telling the user why). `root._appRoutesSettled` is
    // the "route injection for this workspace is DEFINITELY complete" latch
    // (workspaceApps.js/auth.js) — checking it here is load-bearing, not
    // decoration: without it, a page that simply hasn't injected YET (the
    // exact race this function's per-loader recall already exists to
    // handle) would wrongly report "not available" before its own retry
    // ever got a chance to resolve it.
    if (
      pageParam &&
      root &&
      root._appRoutesSettled === true &&
      !root._canvasSlot &&
      _lastPageParamMissNotified !== pageParam
    ) {
      _lastPageParamMissNotified = pageParam
      try {
        root.call?.('openNotification', {
          title: root.call?.('polyglot', 'workspace.apps.pageMissing'),
          message: (root.call?.('polyglot', 'workspace.apps.pageMissingBody') || '').replace(
            '{page}',
            pageParam
          ),
          type: 'error'
        })
      } catch (e) {
        /* the notification surface isn't reachable from the loader context */
      }
    }
  } catch (err) {
    console.warn('[workspaceProject] ?page= restore failed', err)
  }
}

// ── Immersive chrome (module manifest `chrome`) ─────────────────────────────
// `chrome: { immersive: 'non-admin' | true, landing: '/route' }` in a module
// manifest hides the shell's top/bottom chrome (AppHeader, prompt composer,
// presence dock) for matched members and lands them on the module's own page
// instead of the home board. Admins ALWAYS keep the full shell chrome — they
// need the console/customize surfaces regardless of the manifest value.
// Consumers (the chrome hide gates) must be reactive on `workspaceProject`.
// Chrome manifest resolution — WORKSPACE SETTINGS OVERRIDE THE MODULE
// DEFAULT (2026-08-12 per Nika "personal workspace should be able to
// override generic changes"): the module manifest's `chrome` is the generic
// default for every workspace running the module; a workspace's own
// `settings.chrome` (stash `config.chrome`, same settings ▸ configDefaults
// precedence as navbar/panels) replaces it wholesale — e.g.
// `settings.chrome = { immersive: false }` restores the full shell chrome
// for that one workspace. Absent/cleared settings fall through to the
// manifest.
const _moduleChrome = (root) => {
  const wp = root?.workspaceProject
  if (!wp) return null
  const cfg = wp.config
  if (cfg && cfg.chrome !== undefined) return cfg.chrome
  return wp.chrome || null
}

// The root-state key the active module's chrome manifest names for its own
// View-as override (`chrome.viewAsKey`, e.g. bellforge's
// `bfPersonaOverride`). Exported (not just the local read inline in
// moduleImmersiveActive below) so the shell's URL-persistence sync
// (app.js `_syncShellRoute`) and the load-pipeline restore
// (`_restorePageParam` below) can read/write the SAME generic key without
// hand-coding a module-specific name — a module never needs a shell-side
// special case to get reload-durable View-as (RAIL-TRANSITION,
// tickets/fable.md).
export const moduleViewAsKey = (root) => {
  const chrome = _moduleChrome(root)
  return chrome && typeof chrome.viewAsKey === 'string' ? chrome.viewAsKey : null
}

export const moduleImmersiveActive = (root) => {
  const chrome = _moduleChrome(root)
  if (!chrome || !chrome.immersive) return false
  try {
    // Item 11 route gate: immersive (no shell chrome) only holds on the
    // module surface. On /studio, /infra and other own-sidebar routes the
    // rail hides — without this gate a member/preview there would have NO
    // navigation at all (no rail, no navbar).
    if (!moduleSurfaceRouteActive(root)) return false
    if (!currentUserIsAdmin(root)) return true
    // View-as: the manifest may name a root-state key (chrome.viewAsKey)
    // that the module's own persona switcher writes (e.g. bellforge's
    // bfPersonaOverride). An admin previewing a non-admin persona gets the
    // REAL immersive chrome — navbars and composer disappear — so the
    // preview is truthful (Nika 2026-08-13: "after switching to non-admin
    // personas navbars should dissapear"). The key is read on every admin
    // pass, so the factory tracks it and chrome re-resolves on switch.
    const key = moduleViewAsKey(root)
    if (key) {
      const v = root ? root[key] : null
      if (typeof v === 'string' && v && v !== 'admin') return true
    }
    // Nika 2026-08-16: a rail workspace is immersive for ADMINS by default
    // too — top navbar hidden even with the pageslot closed — unless this
    // admin toggled the shell chrome back (persisted per workspace). The
    // root mirror is read FIRST (reactive after a toggle); storage covers
    // the first render of a fresh boot.
    if (moduleRailActive(root)) {
      const mirror = root ? root._moduleChromePref : undefined
      const pref = mirror !== undefined ? mirror : readModuleChromePref(root)
      if (pref !== 'shell') return true
    }
    return false
  } catch (_) {
    return false
  }
}

// TRUE only for an ADMIN previewing a non-admin persona via the module's
// chrome.viewAsKey — i.e. immersive-by-preview, never a real member's
// immersive. The PageSlot CloseBtn branches on this: a real immersive
// member's whole view IS the slot (X routes to the landing), but a
// previewing admin still has the shell behind the sheet, so X must REALLY
// close (Nika 2026-08-15: "after choosing different non-admin role — cant
// close pageslot" — X routed to the landing and, once there, was a
// same-value no-op with no exit).
export const moduleViewAsActive = (root) => {
  try {
    if (!_moduleChrome(root)) return false
    if (!currentUserIsAdmin(root)) return false
    const key = moduleViewAsKey(root)
    if (!key) return false
    const v = root ? root[key] : null
    return typeof v === 'string' && !!v && v !== 'admin'
  } catch (_) {
    return false
  }
}

// BF-MOCKUP-AUDIT-1 item 11 (Nika 2026-08-16: "sidebar covers other
// sidebars … fix it for all pages, for example /studio and /infra") — the
// module rail is chrome for the WORKSPACE SURFACE ('/', where the module
// board + PageSlot live). Any other route (/studio boards, /infra, feature
// pages) carries its OWN sidebar/chrome, and the fixed rail (z1290) sat on
// top of it (measured live: elementsFromPoint on /studio/bellforge stacked
// ModuleRailHost OVER AppSidebar). TRUE only on the module surface.
// Reads root.currentPath (the router's reactive key; consumers must
// subscribeTo it) with a location fallback; the ws prefix is stripped
// inline so both '/w/<org>/<ws>' and top-level forms classify the same.
export const moduleSurfaceRouteActive = (root) => {
  try {
    const raw =
      (root && root.currentPath) ||
      (typeof window !== 'undefined' ? window.location.pathname : '/') ||
      '/'
    const inner = String(raw).replace(/^\/w\/[^/]+\/[^/]+/, '') || '/'
    const p = inner.split(/[?#]/)[0]
    return p === '/' || p === ''
  } catch (_) {
    return true
  }
}

// The module rail (chrome.rail) is ALWAYS-ON for every member once declared
// (Nika 2026-08-13: "sidebar always on, pageslot opening right to it… only
// for bellforge"). Distinct from immersive: immersive hides the shell's
// top/bottom chrome for matched members; the rail is persistent for
// EVERYONE in the workspace, admins included — ON THE WORKSPACE SURFACE.
// Routes that mount their own sidebars (/studio, /infra, boards) are out
// of the rail's territory (item 11 above).
export const moduleRailActive = (root) => {
  const chrome = _moduleChrome(root)
  if (!(chrome && typeof chrome.rail === 'string' && chrome.rail)) return false
  return moduleSurfaceRouteActive(root)
}

// Manifest opt-in: `chrome: { railEdge: true }` puts the rail's EDGE
// (immersive/fullscreen) state flush on the window edges — no Z top/bottom
// offsets, no left gutter (Nika 2026-09-02 via the bellforge lane: "put
// navbar to the edge" — bellforge ships a flat full-height band, and a
// floating inset under it reads as a broken gap). Modules that do NOT set
// it keep the 2026-08-16 "do not put on the edge" offsets — that ruling
// stays the default; this flag is the per-module reversal, not a repeal.
// The chrome-inset band (--slot-top/--slot-bottom, the 2026-08-14
// same-height contract) still wins whenever the shell chrome is live —
// railEdge governs only the edge state.
export const moduleRailEdge = (root) => {
  const chrome = _moduleChrome(root)
  return !!(chrome && chrome.railEdge === true)
}

// ── Admin chrome preference (Nika 2026-08-16: "admin has expanded by
// default so top navbar should not be visible … only if admin toggles
// back", "expanded even if pageslot is closed") ─────────────────────────────
//
// In a rail workspace the ADMIN's default presentation is the IMMERSIVE one
// too — no shell top/bottom chrome, slot edge-to-edge, full-bleed home
// board. The escape hatch is a persisted per-workspace preference:
// 'shell' = the admin toggled the chrome back on. Stored in localStorage
// (survives reload); mirrored on root._moduleChromePref by the toggle so
// factories re-run (the storage read alone is not reactive).
const _moduleChromePrefKey = (root) => {
  const wsId =
    root?.activeWorkspaceId || root?.workspace?.id || root?.jwtClaims?.active_workspace_id || null
  return wsId ? 'moduleChromePref.' + String(wsId) : null
}

export const readModuleChromePref = (root) => {
  try {
    const key = _moduleChromePrefKey(root)
    const raw = key && globalThis.localStorage ? globalThis.localStorage.getItem(key) : null
    return raw === 'shell' ? 'shell' : null
  } catch (_) {
    return null
  }
}

export const writeModuleChromePref = (root, value) => {
  try {
    const key = _moduleChromePrefKey(root)
    if (!key || !globalThis.localStorage) return
    if (value === 'shell') globalThis.localStorage.setItem(key, 'shell')
    else globalThis.localStorage.removeItem(key)
  } catch (_) {
    /* best-effort */
  }
}

// ── Slot fullscreen resolution (B1, tickets/bellforge.md) ──────────────────
//
// `root._slotFullscreen` is a TRI-STATE: true/false record an EXPLICIT user
// choice (the header toggle); null/undefined mean "no choice this session".
// Close/minimize/route-away paths reset to null — a close is not an opinion
// about the next open. With no explicit choice, a persisted per-workspace
// preference wins; with neither, a module workspace (chrome.rail — the
// admin's windowed module view) defaults EXPANDED (Nika: "the workspace
// admin view opens collapsed; it should be EXPANDED by default"), while
// plain workspaces keep the floating card.
//
// localStorage, not sessionStorage: the acceptance is "a fresh load shows
// the expanded state" with "persisted user preference still winning" — the
// preference must survive the reload boundary. Per-workspace key, same
// tenant-scoping shape as minimizedSlots'.
const _slotFsPrefKey = (root) => {
  const wsId =
    root?.activeWorkspaceId || root?.workspace?.id || root?.jwtClaims?.active_workspace_id || null
  return wsId ? 'slotFullscreenPref.' + String(wsId) : null
}

// A persisted "false" is a B1-era artefact and is HEALED here (2026-08-16,
// BF-EXPANDED-DEFAULT-AND-INSET-1): before c99d7ef93 the header toggle wrote
// explicit `false` on every un-expand click, and the tri-state puts a
// persisted pref ABOVE the module default — so one past click pinned a rail
// workspace WINDOWED forever ("admin view should be expanded by default" —
// Nika, reporting the already-shipped default as broken). The post-fix
// toggle writes ONLY `true` (explicit edge-to-edge) or clears; no live code
// path can produce "false", and the toggle has no windowed position (windowed
// is reached only by closing) — so a stored "false" carries no user intent
// the UI can still express. It is therefore mapped to null AND removed on
// first read, per workspace key, instead of being special-cased alive.
export const readSlotFullscreenPref = (root) => {
  try {
    const key = _slotFsPrefKey(root)
    const raw = key && globalThis.localStorage ? globalThis.localStorage.getItem(key) : null
    if (raw === 'false') {
      globalThis.localStorage.removeItem(key)
      return null
    }
    return raw === 'true' ? true : null
  } catch (_) {
    return null
  }
}

export const writeSlotFullscreenPref = (root, value) => {
  try {
    const key = _slotFsPrefKey(root)
    if (!key || !globalThis.localStorage) return
    // Only `true` persists (explicit edge-to-edge). `false`/null both CLEAR:
    // a stored "false" is exactly the B1-era pin the reader above heals, so
    // the writer must not be able to re-create it.
    if (value === true) globalThis.localStorage.setItem(key, 'true')
    else globalThis.localStorage.removeItem(key)
  } catch (_) {
    /* best-effort — quota, privacy mode */
  }
}

// THE one resolver every fullscreen consumer reads (PageSlot's classes +
// header toggle, the rail's chrome-inset predicate, the AI page context).
// Split view always wins (a split has no fullscreen), immersive members are
// always edge-to-edge, then explicit choice → persisted pref → module
// default. Reads `_slotFullscreen` unconditionally first so every factory
// that calls this force-tracks the key (FA515 discipline).
export const slotFullscreenActive = (root) => {
  if (!root) return false
  const explicit = root._slotFullscreen
  if (root._canvasSlotSecond || root._slotSplitIntent) return false
  if (moduleImmersiveActive(root)) return true
  if (explicit === true || explicit === false) return explicit
  const pref = readSlotFullscreenPref(root)
  if (pref === true || pref === false) return pref
  return moduleRailActive(root)
}

// Per-module stage colour (chrome.stage) — the shell paints this behind
// every in-flow surface while the module's workspace is active: the strip
// behind the floating module rail AND the shell pages (home, studio,
// tickets, route mounts). One key, one paint site (shellApp.background),
// so "change the bg per workspace" is a one-line module-config edit
// (2026-08-16 per Nika: "make it consistent so we change bg per
// workspace"). The value crosses the module→shell boundary as a literal
// CSS colour string — the shell cannot resolve a module's design-system
// tokens, same contract shape as chrome.rail carrying a component name.
// Theme-aware chrome VALUE picker — shared by chrome.stage and chrome.card
// (BF-WIDGET-INK-1, Nika's Q2 "desk pair"): a literal CSS colour string, or a
// `{ light: '#…', dark: '#…' }` pair resolved by the shell's LIVE theme.
// Force-track BOTH theme keys UNCONDITIONALLY (FA515 discipline) — the
// value must re-resolve when the user flips theme AND when the OS scheme
// flips under 'auto'. themeMode stays the string 'auto' across an OS
// flip, so tracking it alone never repaints (BF-THEME-SURFACES-1,
// measured by brand 2026-08-16: opaque rgb(37,34,28) across both
// data-theme flips); themeResolved is the shell's live resolution.
// Resolution order for a pair: the shell's LIVE resolution (themeResolved,
// which already folds the user choice + OS preference) → explicit
// themeMode → matchMedia, so 'auto' follows the OS on a fresh load AND
// on a mid-session scheme flip. The pair is resolved HERE, in JS, and
// written as ONE colour — a CSS `light-dark()` value would follow the OS
// `color-scheme`, never the forced `[data-theme]`, and a module
// design-system array pair does not re-resolve per forced theme on the
// app-module render path of the shell's pinned runtime.
const _pickThemedChromeValue = (root, value) => {
  const mode = root ? root.themeMode : null
  const resolved = root ? root.themeResolved : null
  if (typeof value === 'string' && value.trim()) return value.trim()
  if (value && typeof value === 'object') {
    let dark
    if (resolved === 'dark' || resolved === 'light') {
      dark = resolved === 'dark'
    } else if (mode === 'dark' || mode === 'light') {
      dark = mode === 'dark'
    } else {
      dark = typeof matchMedia === 'function' && matchMedia('(prefers-color-scheme: dark)').matches
    }
    const pick = dark ? value.dark || value.light : value.light || value.dark
    return typeof pick === 'string' && pick.trim() ? pick.trim() : null
  }
  return null
}

export const moduleStageColor = (root) => {
  const chrome = _moduleChrome(root)
  if (!chrome) return null
  // Theme-aware form: chrome.stage = { light: '#…', dark: '#…' } — light
  // beige stages want darker companions in dark mode (2026-08-16 per Nika:
  // "on light mode is light beige … on dark mode choose a bit darker tones
  // too"). Resolution: _pickThemedChromeValue above.
  return _pickThemedChromeValue(root, chrome.stage)
}

// Per-module home-widget CARD colour (chrome.card) — the second half of the
// module's desk pair (BF-WIDGET-INK-1, Nika 2026-08-18 Q2 = B: the widget
// cards join the desk family in BOTH themes — white cards in light, a
// designed dark card in dark — not the shell's neutral glass). SAME
// contract shape as chrome.stage: a literal CSS colour string or a
// `{ light, dark }` pair carried across the module→shell boundary as
// values (the shell cannot resolve a module's design-system tokens). The
// shell paints it through the card's EXISTING per-workspace override hook,
// `--home-card-bg` (pages/main.js `_NEUTRAL_CARD_BG`, one value for both
// schemes), so the card surface itself is untouched: a workspace with no
// chrome.card keeps the brand `card` theme fill.
export const HOME_CARD_BG_VAR = '--home-card-bg'
export const moduleCardColor = (root) => {
  const chrome = _moduleChrome(root)
  if (!chrome) return null
  return _pickThemedChromeValue(root, chrome.card)
}

// Variable-level stage repair — the module-chrome writer of `--stage-bg`,
// callable without an element. `_setStageVar` (app.js) delegates its var half
// here; settle points whose repairing re-render a no-op guard suppresses (the
// takeover-publish swallow in loadWorkspaceProject) call it directly.
// VARIABLE level only: the inline `var(--stage-bg, var(--color-stage))`
// chains on the root/content nodes are declared CONSUMERS on 3+ mounted
// nodes — repairing any single node's background instead of the var would
// repaint one consumer and strand the rest (BF-STAGE-ACTIVATION-FLAKE-1).
// OWNERSHIP: `--stage-bg` is ALSO a workspace-record hook — workspaceConfigs
// `_applyDesignSystem` writes `settings.designSystem.vars['--stage-bg']` to
// the SAME inline property (the per-workspace ground for workspaces WITHOUT
// a module desk). Same contract as applyModuleCardVar below: this writer
// removes the property only when it set it itself, so a module that declares
// no stage never strips a record-authored value on a rail render
// (WS-DESIGN-SYSTEM-STAGE-BG-CLOBBERED-BY-MODULE-CHROME-1 — the record's
// ground survived only on `/` and was gone on every module route).
let _moduleStageVarOwned = false
export const applyStageVar = (root) => {
  if (typeof document === 'undefined') return
  const stage = moduleStageColor(root)
  const docEl = document.documentElement
  if (stage) {
    if (docEl.style.getPropertyValue('--stage-bg') !== stage) {
      docEl.style.setProperty('--stage-bg', stage)
    }
    _moduleStageVarOwned = true
  } else if (_moduleStageVarOwned) {
    docEl.style.removeProperty('--stage-bg')
    _moduleStageVarOwned = false
  }
  // The card half rides the SAME writer so stage and card can never
  // disagree (one resolver, one render path, one theme read).
  applyModuleCardVar(root)
}

// Module-owned card var writer (chrome.card → --home-card-bg on <html>).
// Unlike `--stage-bg`, `--home-card-bg` is ALSO a workspace-record hook
// (workspaceConfigs._applyDesignSystem writes `settings.designSystem.vars`
// inline on <html> — GreenIG's green glass rides it), so this writer
// tracks OWNERSHIP: it removes the property only when it set it itself,
// and a module that declares no card never strips a record's value. While
// a module that declares a card is active, the module's value wins — this
// writer runs on every ModuleRailHost render/update (after any record
// re-apply), exactly as the stage var does; the module's own desk is the
// authority for its own cards, a record var is the per-workspace override
// for workspaces WITHOUT a module desk.
let _moduleCardVarOwned = false
export const applyModuleCardVar = (root) => {
  if (typeof document === 'undefined') return
  const card = moduleCardColor(root)
  const docEl = document.documentElement
  if (card) {
    if (docEl.style.getPropertyValue(HOME_CARD_BG_VAR) !== card) {
      docEl.style.setProperty(HOME_CARD_BG_VAR, card)
    }
    _moduleCardVarOwned = true
  } else if (_moduleCardVarOwned) {
    docEl.style.removeProperty(HOME_CARD_BG_VAR)
    _moduleCardVarOwned = false
  }
}

export const moduleImmersiveLanding = (root) => {
  const chrome = _moduleChrome(root)
  const landing = chrome && typeof chrome.landing === 'string' ? chrome.landing : null
  return landing && landing.startsWith('/') ? landing : null
}

export const loadWorkspaceProject = async (root, sdk, opts = {}) => {
  if (!root) return null
  // A comm host (comm.symbols.app, shared commHost.js) loads no workspace
  // module: it is chat, calls and AI only (docs/COMM_HOST.md). Every entrant
  // ends here — the boot chain and a workspace switch (loadPrivateData.js),
  // the post-sign-in re-run (auth.js _ensureWorkspaceModule) and the config
  // converge — and the outcome stays 'idle', which is definitive: no latch
  // waits for it and no converge retries it.
  if (shellIsCommHost()) return null
  const owner = _claimModuleOwner(root, sdk)
  if (!owner) return null
  const flightKey = _loaderFlightKey(root)
  if (_projectFlight && _projectFlightKey === flightKey && _projectFlightRoot === root) {
    return _projectFlight
  }
  // Boot reuse (see `_projectSettled`): only an ACTIVE module that is still
  // live on this root. The switch registry wipes root.workspaceProject to
  // null, and a wiped root must be republished by a real pass.
  const held = _projectSettled
  if (
    opts.boot === true &&
    held &&
    held.key === flightKey &&
    held.root === root &&
    root.workspaceProject
  ) {
    return held.result
  }
  _projectSettled = null
  const run = _loadWorkspaceProjectPass(root, sdk, owner, !!opts.quiet)
  if (!_moduleOwnerCurrent(owner)) return run
  _projectFlightKey = flightKey
  _projectFlightRoot = root
  _projectFlight = run
  const clear = () => {
    if (_projectFlight !== run) return
    _projectFlight = null
    _projectFlightKey = null
    _projectFlightRoot = null
  }
  run.then(clear, clear)
  return run
}

const _loadWorkspaceProjectPass = async (root, sdk, owner, quiet = false) => {
  const seq = ++_loadSeq
  const flightKey = _loaderFlightKey(root)
  const isStale = () =>
    seq !== _loadSeq || _loaderFlightKey(root) !== flightKey || !_moduleOwnerCurrent(owner)
  // A QUIET pass (R2c-m2, the reload after the person's own apply) is no
  // reason for the retry strip; its outcome (ready / error) settles without
  // `quiet`, so a failure still shows. Same rule as the apps pass (BLD-28).
  updateWorkspaceActivation(
    root,
    'module',
    quiet ? { state: 'loading', quiet: true } : { state: 'loading' }
  )
  // Fresh attempt — clear any prior refusal reason + stale surfaced error.
  _lastRefusal = null
  _lastNullDefinitive = false
  if (root._workspaceModuleError) root.update?.({ _workspaceModuleError: null })
  if (isStale()) return null

  const ws = root.workspace || {}
  const wsId = ws.id || ws._id
  // Which module (if any) this workspace runs is SERVER-driven: the workspace
  // record's `settings.workspaceModule` reference (or legacy `settings
  // .projectModule` string). No workspace id is matched here — a workspace gets
  // a module ONLY because its server record says so.
  //
  // root.workspace is built from lean JWT claims at boot (auth.js maps it to
  // {id,name,slug,type,org_id,role} — no `settings` bag) and is re-leaned on every
  // claims tick. The selector + config live in `settings`, so fetch the FULL record
  // from the server when it's absent. Resolving it here (rather than mutating
  // root.workspace, which auth would clobber) gives one resolution point for boot +
  // switch. Org-guarded against the cross-org stale-workspace leak.
  // DECISION site (spec §5) — the branch it takes is "do I already hold an
  // authoritative settings bag, or must I go and fetch the record?", and
  // getting it wrong is not cosmetic: `settingsResolved` is what makes the
  // no-module verdict DEFINITIVE (it suppresses the converge retry). The old
  // `!!ws.settings` was a hand-rolled `ready` that any settings-shaped property
  // on a LIST record satisfied — including the optimistic mirrors other flows
  // write back onto `root.workspace` (moduleConsole.js, workspaceApps.js),
  // which publish a bag holding only the one key they just changed. Reading a
  // partial bag as resolved settles a configured workspace as a plain one.
  // Provenance answers it instead: ready ⇒ authoritative (a full record with
  // `{}` IS a real "no overrides" answer and correctly skips the fetch).
  const { settings: heldSettings, ready: heldReady } = shellWorkspaceSettings(root)
  let settings = heldReady ? heldSettings : null
  // Whether this pass POSITIVELY read the workspace's settings (off the record
  // on root, or by fetching the record — a fetched record WITHOUT a settings
  // bag is still a definitive read: the workspace simply has none). Anything
  // less means the scope is unresolved and the plain-workspace settle below is
  // provisional, not final.
  // (Seeded from the accessor's `ready` — same meaning, one implementation.
  // The FETCH fallback below stays exactly as it was: the accessor reports what
  // is held right now and deliberately never fetches, so it can only narrow the
  // initial read, never replace the completion path.)
  let settingsResolved = heldReady
  if (
    !settings &&
    wsId &&
    _belongsToActiveOrg(root) &&
    sdk &&
    typeof sdk.getWorkspace === 'function'
  ) {
    try {
      const full = await _waitForModuleRequest(getWorkspaceCached(sdk, wsId))
      // Org-verify the FETCHED record too (mirrors onWorkspaceChange's guard in
      // loadPrivateData.js): root.workspace can still be a stale pointer's
      // foreign-org workspace mid-boot — its settings must neither load a
      // foreign module here nor count as this org's definitive "no module".
      const orgId = _lc((root.org && (root.org.id || root.org._id)) || '')
      const recOrg = _lc((full && (full.organization || full.organizationId || full.org)) || '')
      if (full && orgId && recOrg && recOrg !== orgId) {
        console.warn(
          '[workspaceProject] fetched workspace belongs to another org — module load deferred'
        )
      } else if (full) {
        settings = full.settings || null
        settingsResolved = true
      }
    } catch (e) {
      console.warn('[workspaceProject] full workspace fetch failed', e)
    }
    if (isStale()) return null
  }

  const belongs = _belongsToActiveOrg(root)
  const loader = belongs ? _resolveModuleLoader(settings, sdk, wsId) : null

  if (!loader) {
    const definitive = !!(wsId && belongs && settingsResolved)
    // A TRANSIENT miss (scope not readable this pass) while a module is
    // ALREADY LIVE for this same workspace — or for a pass that couldn't even
    // name one — must NOT tear the live module down: destroying + re-injecting
    // a healthy module on every blip (a failed workspace-record refetch, an
    // auth-tick re-lean) looped the whole widget surface through teardown/
    // re-inject and left duplicate grid registrations behind (2026-07-30
    // GreenIG). A DIFFERENT workspace's transient pass still tears down below
    // (never leak a module across a real switch) and a definitive "this
    // workspace has no module" verdict always does.
    if (!definitive && _activeProjectId && (!wsId || String(wsId) === _moduleLoadedWorkspaceId)) {
      _lastLoadOutcome = 'unresolved'
      console.warn('[workspaceProject] scope unresolved on this pass — keeping live module', {
        wsId: wsId || null,
        belongs,
        settingsResolved
      })
      if (!root.workspaceProject) _scheduleModuleConverge(root)
      return null
    }
    _teardownModule(root)
    // Settle as a plain workspace — but only DEFINITIVELY when the settings
    // were actually resolved for a workspace of the active org and carry no
    // module reference. Everything else (no workspace resolved yet, org guard
    // blocking mid-switch, record fetch failed) is transient boot noise: the
    // module may well be configured, this pass just couldn't know. Cold boots
    // hit exactly that window — the old silent settle here left a configured
    // module unloaded for the whole session (theme + legacy widget chrome
    // still painted via applyWorkspaceConfig's own later re-runs), the
    // GreenIG "widgets empty on sign-in" bug. Schedule the converge retry.
    if (definitive) {
      _lastLoadOutcome = 'no-module'
      updateWorkspaceActivation(root, 'module', { state: 'ready' })
    } else {
      _lastLoadOutcome = 'unresolved'
      console.warn('[workspaceProject] module scope unresolved on this pass — will retry', {
        wsId: wsId || null,
        belongs,
        settingsResolved
      })
      _scheduleModuleConverge(root)
    }
    return null
  }

  let project
  try {
    project = await loader()
  } catch (e) {
    console.warn('[workspaceProject] module load failed', e)
    project = null
  }
  if (isStale()) return null
  if (!project) {
    updateWorkspaceActivation(root, 'module', { state: 'error' })
    // A module WAS referenced (a loader existed) but resolved to null. If the
    // loader refused it for a KNOWN reason (sandboxed tier / failed audit),
    // surface it — a configured app silently never appearing is a support
    // black hole (tickets/workspace.md WS-APPS bug #4). Generic/transient fetch
    // nulls stay quiet (no _lastRefusal set).
    if (_lastRefusal) {
      root.update?.({ _workspaceModuleError: _lastRefusal })
      try {
        root.call?.('openNotification', {
          title: root.call?.('polyglot', 'workspace.apps.loadFailed'),
          message: _lastRefusal,
          type: 'error'
        })
      } catch (e) {
        /* the notification surface isn't reachable from the loader context */
      }
    }
    // Same live-module protection as the unresolved branch above: a TRANSIENT
    // re-load failure for the workspace whose module is already live keeps the
    // module (one bad round-trip must not destroy the session's widget
    // surface). Refusals and definitive verdicts still tear down, as does any
    // failure while switching to a DIFFERENT workspace.
    if (
      !_lastRefusal &&
      !_lastNullDefinitive &&
      _activeProjectId &&
      (!wsId || String(wsId) === _moduleLoadedWorkspaceId)
    ) {
      _lastLoadOutcome = 'error'
      console.warn('[workspaceProject] module re-load failed — keeping live module')
      if (!root.workspaceProject) _scheduleModuleConverge(root)
      return null
    }
    _teardownModule(root)
    // A refusal (sandboxed tier / failed audit) or a definitive content
    // verdict (manifest missing / id mismatch on a fetched payload) is final.
    // A TRANSIENT null (fetch/parse/channel failure on a REFERENCED module)
    // is retried — else one bad round-trip costs the whole session's module
    // (failures are never memoized, so the refetch is real).
    if (_lastRefusal || _lastNullDefinitive) {
      _lastLoadOutcome = 'refused'
    } else {
      _lastLoadOutcome = 'error'
      _scheduleModuleConverge(root)
    }
    return null
  }

  // Inject components + functions into the live context — but only when switching
  // INTO this module (re-entrancy: onWorkspaceChange can fire twice). A different
  // module first tears down the previous one's keys + state.
  //
  // The gate is (module id × WORKSPACE): two workspaces can run the SAME
  // module, and a module-id-only gate skipped teardown on that switch — so
  // workspace A's seeded rows survived (the registry wipe doesn't know module
  // state keys, and the seed loop below skips non-empty keys as "someone
  // else's data") and rendered as workspace B's (2026-07-30 isolation audit
  // #3). Same-workspace re-passes stay idempotent.
  if (_activeProjectId !== project.id || String(wsId || '') !== _moduleLoadedWorkspaceId) {
    _teardownModule(root)
    const ctx = _ctx(root)
    if (ctx) {
      ctx.components = ctx.components || {}
      ctx.functions = ctx.functions || {}
      ctx.pages = ctx.pages || {}
      // WS-MODULE-OWN-KEYS-STALE-ACROSS-ORG-SWITCH-1 — hold the bags this pass
      // writes into, so the NEXT teardown deletes from them even if the root
      // state was replaced under us in between (see _captureBags). The routes
      // map is added below, where the routed element is resolved.
      _injectedBags = _captureBags(ctx, null)

      _injectEntries(
        ctx.components,
        project.components || {},
        _injectedComponentKeys,
        'components',
        String(project.id)
      )
      // The module's own copy reaches the shell's polyglot map — once per
      // load, non-destructively, ledgered for _moduleTeardown.
      _injectedTranslations = _mergeModuleTranslations(ctx, project)

      // Engine-synthesized fns ride along with the module's own: a capability
      // grant gets the scoped `moduleData` bridge (moduleDataBridge.js). The
      // approval-gated action TRIO is NOT injected here — it is built ONCE by
      // loadWorkspaceApps over the union of this module's actions and every
      // additive app's (a per-module trio would collide on the shared fn names
      // and only the first registry would be reachable — the "Unknown action"
      // bug with multiple apps installed). loadWorkspaceApps always runs right
      // after this loader on every activation path (auth boot, module console,
      // install/uninstall), so the trio is present by the time anything can
      // propose an action.
      const fns = { ...(project.functions || {}) }
      // Injected UNCONDITIONALLY — same contract as moduleAiChat below. The
      // bridge live-resolves the grant on EVERY call (union of the build-time
      // snapshot and the workspace record's stored ref) and fails closed, so
      // registration must not depend on `project.capabilities` having resolved
      // by loader time: a late settings bag (or an unaudited development-
      // channel load, where the ref reaches the loader before the grant) left
      // `moduleData` unregistered for the whole session and every records call
      // returned undefined — measured live on gita/backoffice 2026-08-29, the
      // same frozen-at-registration shape the bridge itself was taught to heal
      // (bellforge 2026-08-13/16).
      fns.moduleData = buildModuleDataBridge(
        root,
        project.capabilities || {},
        _moduleDataBridgeOpts()
      )
      // The scoped server-routed completion bridge (moduleAiBridge.js) —
      // el.call('moduleAiChat', prompt, opts). Injected UNCONDITIONALLY: the
      // bridge resolves the live `ai` grant per call (cold-boot heal — a
      // load-time grant check froze "no grant" forever when settings resolved
      // late, the exact race moduleDataBridge heals for records; measured
      // live 2026-08-13: granted workspace answered offline-draft only).
      // Logo-home bridge (Nika 2026-08-16: "clicking logo should close
      // pageslot and navigate to / home"): ONE sanctioned close+home the
      // module can call — clears both panes and the split intent, arms the
      // one-shot reopen suppression (_slotOnClose's immersive landing
      // reopen would otherwise undo the close 11ms later — the measured
      // second-writer), and routes to '/' when the tab sits on a deep
      // shell route. The slot's own close choreography handles the ?page=
      // URL sync and title restore.
      fns.moduleGoHome = () => {
        try {
          root._slotSkipReopenOnce = true
          root.update({
            _canvasSlot: null,
            _canvasSlotSecond: null,
            _slotSplitIntent: false,
            // null, not false — a close is not an explicit windowed choice
            // (tri-state contract at slotFullscreenActive above).
            _slotFullscreen: null
          })
          const path = shellStripWorkspacePrefix(String(root.currentPath || '/'))
          if (path && path !== '/') {
            const rel = _routedEl(root)
            if (rel && typeof rel.router === 'function') {
              rel.router('/', typeof rel.getRoot === 'function' ? rel.getRoot() : rel)
            }
          }
        } catch (e) {
          console.warn('[workspaceModule] moduleGoHome failed:', e && e.message)
        }
        return true
      }
      fns.moduleAiChat = buildModuleAiBridge(root, project.capabilities || {})
      // The streamed sibling over the SAME grant and the same live check —
      // el.call('moduleAiStream', prompt, { …, onText }) (moduleAiBridge.js).
      fns.moduleAiStream = buildModuleAiStreamBridge(root, project.capabilities || {})
      // Admin ops over the same grant — usage counter + settings.ai write
      // (el.call('moduleAiAdmin', 'usage'|'settings', args), B7).
      fns.moduleAiAdmin = buildModuleAiAdminBridge(root, project.capabilities || {})
      // External company-registry lookups (moduleRegistryBridge.js, D2-1) —
      // el.call('moduleRegistryLookup', 'search'|'company'|'status', args).
      // Injected unconditionally for the same cold-boot reason as the ai
      // bridge: the `registry` grant is resolved live per call.
      fns.moduleRegistryLookup = buildModuleRegistryBridge(root, project.capabilities || {})
      // A `files` grant gets the scoped upload bridge (moduleFileBridge.js) —
      // el.call('moduleFileUpload', file, opts).
      if (project.capabilities && project.capabilities.files) {
        fns.moduleFileUpload = buildModuleFileBridge(root, project.capabilities)
        // Long-form media over the SAME grant: direct-to-storage, chunked,
        // resumable, cancellable, the workspace's tier limit
        // (el.call('moduleFileUploadLarge', file, { onProgress, signal })).
        fns.moduleFileUploadLarge = buildModuleFileUploadLargeBridge(root, project.capabilities)
        // Read side of the SAME grant — upload without read left the module
        // able to write files it could never open again.
        fns.moduleFileList = buildModuleFileListBridge(root, project.capabilities)
        fns.moduleFileRead = buildModuleFileReadBridge(root, project.capabilities)
        // Server-side docx/pdf → text extraction over the same grant
        // (el.call('moduleFileExtractText', ref)) — F16.2b.
        fns.moduleFileExtractText = buildModuleFileExtractTextBridge(root, project.capabilities)
        // Write side: save an edit BACK to the file the app was handed. Without
        // it an app opened from /files could only ever mint a second copy.
        fns.moduleFileUpdate = buildModuleFileUpdateBridge(root, project.capabilities)
        // The create half — a fresh save lands ON the /files desktop, visibly.
        fns.moduleFileCreate = buildModuleFileCreateBridge(root, project.capabilities)
      }
      // Declared vendor libraries get the import bridge (moduleImportBridge.js)
      // — el.call('moduleImport', pkg) / el.call('moduleAssetUrl', pkg, path).
      // Manifest-driven (not a server grant): it exposes no workspace data,
      // only which public npm packages the shell will fetch for this app.
      if (project.libraries) {
        fns.moduleImport = buildModuleImportBridge(project.libraries)
        fns.moduleAssetUrl = buildModuleAssetUrlBridge(project.libraries)
        fns.moduleMount = buildModuleMountBridge(project.libraries)
        fns.moduleUnmount = buildModuleUnmountBridge()
        fns.moduleApi = buildModuleApiBridge()
        fns.moduleToken = buildModuleTokenBridge()
        fns.moduleTheme = buildModuleThemeBridge()
      }
      // A `calendar` grant chains the module to the NATIVE workspace calendar
      // (moduleCalendarBridge.js) — el.call('moduleCalendarCreate', {...}),
      // el.call('moduleCalendarUpdate', {id,...}), el.call('moduleCalendarRemove',
      // {id}) and el.call('moduleCalendarList', {from,to}). Without it a module's events
      // live only in its own records plane, where the Calendar app, freebusy
      // and reminders can never see them.
      if (project.capabilities && project.capabilities.calendar) {
        const cal = buildModuleCalendarBridge(root, project.capabilities)
        fns.moduleCalendarCreate = cal.moduleCalendarCreate
        // The edit/remove half of the SAME grant (WS-MODULE-CALENDAR-BRIDGE-
        // UPDATE-REMOVE-1). Injected here too, or a module that chained an
        // event could create it natively but never move or retire it: the
        // module row drifts from its native twin and a deleted row leaves an
        // orphan event plus a live meet room.
        fns.moduleCalendarUpdate = cal.moduleCalendarUpdate
        fns.moduleCalendarRemove = cal.moduleCalendarRemove
        fns.moduleCalendarList = cal.moduleCalendarList
      }
      // Page-open bridge (moduleOpenBridge.js) — module code opens its own
      // registered pages in the PageSlot: el.call('moduleOpenPage', '/route').
      // No capability gate: it can only reach the module's own pages.
      fns.moduleOpenPage = buildModuleOpenBridge(root)
      // Same-page record opens do NOT navigate (a second mount of the page
      // the module is already showing was the doubled-sheet bug), so the
      // module has no route change to carry `?item=<id>`. This is the seam
      // that writes it: query-only, replaceState, no new page instance. See
      // moduleOpenBridge.js for the full contract.
      fns.setModuleRouteQuery = buildModuleRouteQueryBridge(root)
      // The page asks the shell to hide its global composer while it shows
      // its own (moduleShellComposer.js, MODULE-SHELL-COMPOSER-1) — latched
      // to the caller's own page, released on leave; the header stays.
      fns.setShellComposer = buildModuleShellComposerBridge(root, {
        moduleIdOf: moduleIdOfContext
      })
      // The READ half of the same seam (D2 §9 "open = exact spot"). A module
      // page must be able to read the param it (or a room's Open) just wrote,
      // AT MOUNT — `el.call('readPageParam', 'node')`. Same reader the five
      // shell apps use (functions/pageParam.js), so a module's `?node=` and
      // mail's `?thread=` resolve through ONE implementation: the pane's own
      // route first, the address bar second. Without the bridge a module page
      // mounted in the PageSlot could only see `location.search`, which never
      // carries the SECOND pane's route.
      fns.readPageParam = readPageParam
      fns.readPageParams = readPageParams
      fns.readPageRoute = readPageRoute
      // The share-link seam (WS-SHELL-MODULE-BRIDGE-NO-CURRENT-URL-1): a
      // module had no bridge function for its own absolute URL, so a "Copy
      // link" composed one from this trio plus the inherited `workspaceUrl`
      // — which answers the canonical channel-shell origin, not THIS host,
      // so a custom-domain copy pointed at my.symbols.app. One call now
      // answers the pane's real address, current host included.
      fns.readPageUrl = readPageUrl
      // NOTE: the workspace-notification bridge (`moduleNotify`) is NOT
      // injected here — like the approval-action trio it is a shared fn name
      // built ONCE by loadWorkspaceApps over the union of this module's id
      // and every additive app's (a per-plane copy would collide and only
      // the first would be reachable). loadWorkspaceApps always runs right
      // after this loader on every activation path.
      // Platform bridges WIN over same-named module functions. A module may
      // ship its own `moduleOpenPage` (a standalone-router fallback — the
      // bridge doesn't exist outside the shell, and el.call on an
      // unregistered name is a SILENT no-op, so standalone CTAs were dead
      // clicks). _injectEntries skips collisions, which would let that
      // module fallback shadow the real bridge in-shell — evict the
      // module's copy first so the platform implementation lands.
      //
      // WS-MODULEDATA-GRANT-LOST-ON-ORG-RETURN-1 — the same eviction applies to
      // a copy held by the synthetic caps ledger (`__apps_caps__`). That
      // ledger is rebuilt by EVERY loadWorkspaceApps pass, and loadWorkspaceApps
      // runs AFTER this loader — so on an in-session org return the previous
      // tenant's apps-pass `moduleData` was still in the bag here, this
      // injection skipped the fresh takeover bridge as a "collision", and the
      // apps pass then tore the stale ledger down (deleting moduleData) and
      // never rebuilt it for a takeover-only workspace: every
      // `el.call('moduleData', …)` returned undefined until a full reload
      // ("module capability grant missing"). A platform-bridge name is
      // OWNED by the latest loader that builds it — evict the stale copy
      // AND drop it from that ledger's list, so the later caps teardown
      // cannot delete the fresh bridge.
      const capsLed = _injectedByApp.get(_CAPS_LEDGER)
      for (const k of Object.keys(fns)) {
        if (!(k in ctx.functions)) continue
        const i = _injectedFunctionKeys.indexOf(k)
        if (i !== -1) {
          delete ctx.functions[k]
          _injectedFunctionKeys.splice(i, 1)
          continue
        }
        const j = capsLed ? capsLed.functions.indexOf(k) : -1
        if (j !== -1) {
          delete ctx.functions[k]
          capsLed.functions.splice(j, 1)
        }
      }
      _injectEntries(ctx.functions, fns, _injectedFunctionKeys, 'functions', String(project.id))

      // WS-MODULE-FN-NAME-COLLISION-CROSS-APP — build this module's own
      // resolution scope from the SAME bag that went to root (its functions
      // plus its capability bridges), so the two planes can never disagree.
      // Root keeps every key it had; the scope only decides which copy the
      // module's own pages and widgets see when another app took the name.
      _moduleScopeFor(ctx, project, fns)

      // Register the module's pages into both the context map (read by the home
      // split-pane mount via el.context.pages) AND the live router map on the
      // routed element, so a nav item can open them as a real full shell page
      // (browser-back works) — not only inside the home PageSlot overlay. Removed
      // on switch-away by _moduleTeardown, so the routes stay isolated to this module.
      const routedEl = _routedEl(root)
      if (_injectedBags) _injectedBags.routes = (routedEl && routedEl.routes) || null
      // Module design-system context — see _ensureModuleDsContext /
      // _wrapModulePages. The SAME wrapped defs must reach every resolver
      // (ctx.pages, the live router map, AND the workspaceProject stash
      // below) — PageSlot's _defFor prefers root._slotResolve, which reads
      // the stash, so wrapping only the context map left the slot mounting
      // the bare def with shell tokens.
      const wrappedPages = await _wrapModulePages(ctx, project, root)
      if (isStale()) return null
      _liveModulePages = wrappedPages
      _installLiveGetter(root)
      for (const [path, def] of Object.entries(wrappedPages)) {
        const taken =
          path in ctx.pages || !!(routedEl && routedEl.routes && path in routedEl.routes)
        if (taken) {
          console.warn('[workspaceModule] key collision skipped:', path)
          continue
        }
        ctx.pages[path] = def
        // _routeTableDef: the router's own merge spread drops the def's
        // non-enumerable module DS `context` — see that fn's comment
        // (MODULE-ROUTE-CTX). ctx.pages keeps the original `def`.
        if (routedEl && routedEl.routes) routedEl.routes[path] = _routeTableDef(def)
        _injectedPageKeys.push(path)
      }
    }
    _activeProjectId = project.id
    // WS-MODULE-SCOPED-KEYS-CONTRACT-1: publish the module's declared scoped
    // keys + boundary hook to the registry the PHASE-1 switch wipe reads.
    registerModuleScopedKeys(project.id, {
      keys: project.scopedKeys || [],
      hook: project.hooks && project.hooks.onWorkspaceSwitch
    })
  }

  // Keep the live map fresh on re-passes too (the inject block above is
  // skipped when the module is unchanged, but a prior teardown from a
  // failed pass may have nulled it).
  if (!_liveModulePages) {
    const wrappedPages = await _wrapModulePages(_ctx(root), project, root)
    if (isStale()) return null
    _liveModulePages = wrappedPages
  }
  _installLiveGetter(root)

  // AI-drivable fn set = manifest-declared + the engine-synthesized PROPOSE
  // (never approve / reject — those are the member's clicks).
  const aiFunctions = project.actions
    ? [...new Set([...(project.aiFunctions || []), ...MODULE_ACTION_AI_FNS])]
    : project.aiFunctions || []

  // Cache the takeover module's action registry for the merged trio built by
  // loadWorkspaceApps (idempotent — re-runs of this loader refresh it).
  _takeoverActions = project.actions || null

  // Seed the module's declared state keys — ONLY where the current value is
  // empty/absent (a non-empty key is someone else's data: hands off, untracked,
  // never cleared on teardown). Tracked keys are reset to the correctly-typed
  // empty for their declared seed by _clearState (never a hardcoded [] — see
  // _injectedStateSeeds / _emptyForSeed).
  const seedPatch = {}
  for (const key of project.stateKeys || []) {
    const cur = root[key]
    const empty = cur == null || (Array.isArray(cur) && !cur.length)
    if (!empty) continue
    seedPatch[key] = project.seeds && project.seeds[key] !== undefined ? project.seeds[key] : []
    _injectedStateSeeds[key] = seedPatch[key]
    if (!_injectedStateKeys.includes(key)) _injectedStateKeys.push(key)
  }

  const takeoverStash = {
    id: project.id,
    type: project.type,
    manifestVersion: project.manifestVersion,
    // The active workspace's SERVER `settings` (designSystem/navbar/modules/apps/
    // home_default_panels) cached as the resolved config — falling back to the
    // manifest's configDefaults (server settings ▸ configDefaults ▸ shell
    // defaults). A STABLE slot: resolveWorkspaceConfig (#1) reads it, and unlike
    // root.workspace.settings it survives the auth claims-tick that re-leans
    // root.workspace — so the skin + panels don't flicker away after boot.
    // Server-sourced, never a frontend hardcode.
    //
    // `settings` is null-stripped before the spread (_stripClearedSettings,
    // which also drops an empty-array navbar — see its comment): a
    // workspace whose personalization was RESET carries an EXPLICIT
    // `settings.<key>:null` for the cleared key(s) (mcResetNavbar/
    // mcRemoveModule in moduleConsole.js; resetWorkspaceNavbar/
    // uninstallWorkspaceApp in workspaceApps.js) — that used to WIN this
    // spread and blank the key instead of falling through to the module's
    // configDefaults. Absent and explicit-null now behave identically here
    // (matching _normalizeSavedRow's per-key `!= null` handling below in
    // workspaceConfigs.js); only a REAL settings value still overrides
    // configDefaults — tickets/workspace.md "settings.navbar:null
    // suppresses an assigned module's configDefaults.navbar".
    config:
      settings || project.configDefaults
        ? { ...(project.configDefaults || {}), ..._stripClearedSettings(settings || {}) }
        : null,
    pages: project.pages,
    widgets: project.widgets,
    // Presentation-mode manifest (immersive chrome + landing) — consumed by
    // the shell chrome gates (moduleImmersiveActive) and the home landing
    // redirect. Rides the stash so the gates are reactive on
    // root.workspaceProject like every other module surface.
    chrome: project.chrome || null,
    // Its canvas pages (manifest `canvasRoutes`) — shared moduleRoutes.js
    // isModuleCanvasRoute reads them here (takeover) and off each
    // root.workspaceApps[] entry (additive apps).
    canvasRoutes: project.canvasRoutes || null,
    // Formats this module opens (config.js `opens[]`) — the /files handler
    // registry merges these the same way it merges the additive apps'.
    opens: project.opens || null,
    // Where a row of a declared collection OPENS (config.js `collections[]`)
    // — functions/moduleRecordRoutes.js unions these with the additive apps',
    // and shared `entityRefHref` reads the union off this stash. Same shape,
    // same publish site and same "or null" as `opens` right above.
    recordRoutes: project.recordRoutes || null,
    // Which module component draws one row of a declared collection
    // (config.js `collections[].row`) — functions/moduleRecordRows.js unions
    // these with the additive apps'. Same publish site as recordRoutes.
    recordRows: project.recordRows || null,
    // The module's read-only views for AI answers (config.js `views[]`) —
    // functions/assistantAppViews.js reads them off this stash.
    views: project.views || null,
    // …and its own forms (config.js `forms[]`) — functions/assistantAppForms.js.
    forms: project.forms || null,
    aiFunctions,
    // How each of those verbs is CALLED, for the assistant's catalog. Carried
    // beside the names, never merged into them: the names are the call GATE,
    // the docs are for the model.
    aiFunctionDocs: project.aiFunctionDocs || {},
    // The approval-gated actions this module declares — the keys the
    // assistant may PROPOSE (uiCatalog `moduleActions`), and their own words.
    actionKeys: moduleActionKeys(project.actions),
    actionDocs: moduleActionDocs(project.actions),
    chatStarters: project.chatStarters,
    // Catalog metadata for the AI's workspace knowledge layer (uiCatalog.js):
    // the module's injected component names (its UI vocabulary — what
    // render_answer_ui sources may extend), its live root state keys (where
    // its loaded data lives), the discovered loader functions (imperative
    // data paths the AI can drive via el.call), and any declared data
    // recipes. Names/descriptors only — the defs stay in context.
    components: Object.keys(project.components || {}),
    stateKeys: (project.stateKeys || []).slice(),
    loaders: _discoverLoaders(project.functions),
    dataRecipes: (project.dataRecipes || []).slice()
  }
  // Cache the takeover-only stash so loadWorkspaceApps can recompose the
  // published root.workspaceProject over it + the additive apps' union without
  // compounding earlier compositions (the base must stay takeover-pure).
  _takeoverStash = takeoverStash
  _lastLoadOutcome = 'active'
  updateWorkspaceActivation(root, 'module', { state: 'ready' })
  // Rebound on EVERY successful pass (not only inside the inject block): the
  // same module can serve two workspaces, and the live-module guards above key
  // on which workspace the module is currently active FOR.
  _moduleLoadedWorkspaceId = String(wsId || '')
  // BOOT-RESHUFFLE F4: publishing an IDENTICAL stash is a no-op. The
  // activation chain runs from up to four drivers per boot (boot chain, auth
  // ensure, switch pipeline, converge retries) and every republish
  // re-rendered GridContainer + HeaderTabs + every moduleRoutes subscriber.
  // Skip only when the live value is still present — the switch registry
  // wipes root.workspaceProject to null, and a wiped root MUST be
  // republished even when the content fingerprint is unchanged.
  const _fp = _publishFp(['takeover', _moduleLoadedWorkspaceId, takeoverStash])
  const _hasSeeds = Object.keys(seedPatch || {}).length > 0
  if (_fp && _fp === _lastTakeoverFp && root.workspaceProject && !_hasSeeds) {
    // identical republish — swallow. The swallow also swallows the rail
    // re-render that used to repair `--stage-bg` after a transient removal
    // (the accidental-repair regression, BF-STAGE-ACTIVATION-FLAKE-1) — so
    // repair EXPLICITLY, at the variable level, before swallowing.
    // ⚠ REACHABILITY DEPENDENCY: applyStageVar REMOVES the var on a falsy
    // moduleStageColor, and this call stays safe for TRANSIENTLY
    // chrome-less roots only because (a) _composeAiStash preserves takeover
    // chrome and (b) loadWorkspaceApps' transitional-chrome hold carries
    // chrome forward onto a chrome-less composed stash. The fingerprint
    // does NOT protect this: _lastTakeoverFp moves only on the takeover
    // branch, while the composed republish replaces root.workspaceProject
    // without touching it — root content and fp CAN diverge. Do not
    // re-derive safety from fp identity.
    applyStageVar(root)
  } else {
    _lastTakeoverFp = _fp
    root.update({ workspaceProject: takeoverStash, ...seedPatch })
  }

  _rerouteIfStranded(root, project.pages, 'workspaceProject')

  _restorePageParam(root, project.pages)

  if (!isStale()) _projectSettled = { key: flightKey, root, result: project }
  return project
}

// Reload recovery for injected module/app routes.
//
// A HARD load directly on a module route (refreshing while on /policies, or
// opening /w/icca/exams from a link) hits the router at BOOT — before the
// workspace module or app has activated and injected its pages — so the boot
// pass finds no matching route and falls through. Now that the routes exist,
// re-dispatch the current URL so the real page renders.
//
// Only fires when the URL is one of the just-injected routes AND the boot pass
// actually stranded, so it never disturbs normal in-app navigation. Shared by
// BOTH loaders: the takeover module had this recovery, the ADDITIVE app loader
// did not — which is why a refresh on an installed app's page (the Manifest v2
// path every workspace app uses) rendered an empty pane while its navbar and
// data loaded fine.
const _rerouteIfStranded = (root, pages, tag) => {
  try {
    if (!pages) return
    const pathname =
      (typeof window !== 'undefined' && window.location && window.location.pathname) || ''
    if (!pathname) return
    const routePath = shellStripWorkspacePrefix(pathname)
    if (!pages[routePath]) return
    const routedEl = _routedEl(root)
    if (!routedEl || typeof routedEl.router !== 'function') return
    const matched = root.route
    // The original guard only asked whether ROUTING STATE looked wrong
    // (`'/*'` / null / a path mismatch). That misses the case this helper
    // exists for: a module injects its pages AFTER the router already
    // resolved, so `root.route` has since been updated to the module's path
    // and `currentPath` matches — yet the VIEW is still the not-found page.
    // Every cold load of an installed app's route rendered "Not found" until
    // something re-routed by hand (2026-08-08).
    //
    // Routing state cannot answer "is the right page on screen", so ask the
    // DOM instead: if the routed element is not currently showing the element
    // for `routePath`, re-route. Cheap, and it makes the helper correct for
    // both the stranded-state case and the late-injection case.
    const stateStranded = matched === '/*' || matched == null || root.currentPath !== pathname
    let viewStranded = false
    try {
      const node = routedEl.node
      // A rendered module page carries its own route key; a not-found page
      // does not match it. Comparing the KEY avoids depending on any single
      // page's markup.
      const shown =
        node && node.firstElementChild && node.firstElementChild.getAttribute
          ? node.firstElementChild.getAttribute('data-key')
          : null
      const expected = pages[routePath] && pages[routePath].key
      viewStranded = !!(expected && shown && shown !== expected)
      // No key to compare (most pages) — fall back to "the routed element has
      // no children at all", which is unambiguous.
      if (!expected && node) viewStranded = viewStranded || !node.firstElementChild
    } catch (err) {
      viewStranded = false
    }
    if (stateStranded || viewStranded) {
      // Carry the QUERY (and hash) through the re-dispatch. This helper fires
      // on the COLD load of a module route — the module injects its pages
      // after the router already resolved — and it used to hand the router a
      // bare pathname, so mwtRouter pushed a query-less URL and the record
      // the link named was gone before the page ever mounted. Measured live:
      // `/w/vault/default/vault-rooms?item=…&tenant=…` became
      // `/w/vault/default/vault-rooms` in a single pushState from here, which
      // is why a shared record link dead-ended on the module's generic list
      // on a cold mount while working perfectly warm (A1-06 / V1-4).
      const search = (typeof window !== 'undefined' && window.location.search) || ''
      const hash = (typeof window !== 'undefined' && window.location.hash) || ''
      routedEl.router(pathname + search + hash, routedEl)
    }
  } catch (err) {
    console.warn('[' + tag + '] reload re-route failed', err)
  }
}

// Remove EXACTLY one additive app's injected keys (components / functions /
// pages + live routes), leaving the shell, the takeover module, and every other
// app untouched. The per-app ledger makes the teardown provably exact.
const _teardownApp = (root, ledgerId) => {
  _appActivations.delete(ledgerId)
  const led = _injectedByApp.get(ledgerId)
  if (!led) return
  // WS-MODULE-SCOPED-KEYS-CONTRACT-1: the app's boundary hook fires while its
  // functions still resolve; its declared scoped keys reset; then the ledger
  // goes. (The synthetic caps ledger declares nothing — all three are no-ops.)
  _runModuleSwitchHook(root, ledgerId)
  const scopedPatch = moduleScopedWipeFor(ledgerId, root)
  // WS-MODULE-STATE-SCOPE-1 — the app's own state plane resets and is evicted
  // at the same beat (see _clearModuleStatePlane). Must run BEFORE the
  // unregister below: the wipe patch is built from this owner's registry entry,
  // which is keyed by LEDGER id while the store is keyed by PROJECT id.
  _clearModuleStatePlane(ledgerId, root, led.projectId)
  unregisterModuleScopedKeys(ledgerId)
  if (Object.keys(scopedPatch).length && root && typeof root.update === 'function') {
    root.update(scopedPatch)
  }
  // WS-MODULE-OWN-KEYS-STALE-ACROSS-ORG-SWITCH-1 — delete from the bags this
  // ledger was INJECTED into, not from a context re-resolved now (which is
  // null while the root state is being replaced mid-switch). Dropping the
  // ledger without deleting its keys orphans them: the next install of the
  // same app is then skipped as a "collision" and the previous tenant's copy
  // stays bound. Fallback keeps ledgers written before this capture working.
  const ctx = _ctx(root)
  const comps = _bagOr(led.bags, 'components', ctx && ctx.components)
  const fns = _bagOr(led.bags, 'functions', ctx && ctx.functions)
  const pages = _bagOr(led.bags, 'pages', ctx && ctx.pages)
  if (comps) for (const k of led.components) delete comps[k]
  if (fns) for (const k of led.functions) delete fns[k]
  if (pages) for (const k of led.pages) delete pages[k]
  const routedEl = _routedEl(root)
  const routes = _bagOr(led.bags, 'routes', routedEl && routedEl.routes)
  if (routes) for (const k of led.pages) delete routes[k]
  _unmergeModuleTranslations(led.translations)
  _injectedByApp.delete(ledgerId)
}

// Compose the published `root.workspaceProject` stash over the takeover
// module's own stash (may be null) + the loaded additive apps. The stash is
// the assistant's whole client-side AI surface: `aiFunctions` gates which fns
// the AI transport may run (simoneAiTransport._moduleAiFns), `chatStarters`
// feeds the composer's starter bubbles (shared Chat.js), and pages/components/
// stateKeys/loaders/dataRecipes feed the per-turn workspace UI catalog
// (uiCatalog.js). Recomputed from scratch on every apps load — uninstalling an
// app shrinks the surface — and always from the PURE takeover base, never a
// previous composition. Identity fields (id/config/widgets) pass through from
// the takeover only: app-only workspaces keep a null id so takeover-only
// consumers (uninstallWorkspaceApp's activeId check, /add-app gating,
// resolveWorkspaceConfig's project.config branch) behave exactly as before.
const _composeAiStash = (takeover, apps, mergedActions = {}) => {
  if (!apps.length) return takeover || null
  const uniq = (lists) => [...new Set(lists.flat().filter(Boolean))]

  const aiFunctions = uniq([
    (takeover && takeover.aiFunctions) || [],
    ...apps.map((p) => p.aiFunctions || [])
  ])
  if (Object.keys(mergedActions).length) {
    for (const n of MODULE_ACTION_AI_FNS) if (!aiFunctions.includes(n)) aiFunctions.push(n)
  }

  // A verb runs read-only only when EVERY source that declares it marks it so
  // (MODULE-FN-READONLY-ENFORCED-1) — on a name collision either app's
  // function could be the one that runs, and an unmarked one may write.
  const readOnlyAiFunctions = aiFunctions.filter((name) => {
    const declarers = [takeover, ...apps].filter(
      (src) => src && Array.isArray(src.aiFunctions) && src.aiFunctions.includes(name)
    )
    return (
      declarers.length > 0 &&
      declarers.every(
        (src) => Array.isArray(src.readOnlyAiFunctions) && src.readOnlyAiFunctions.includes(name)
      )
    )
  })

  // Union the call docs the same way the names are unioned — without this the
  // merged stash carried the verbs but dropped how to call them, so every
  // workspace with an additive app fell back to an assistant guessing argument
  // shapes. FIRST declaration wins on a collision, matching this loader's
  // policy for page paths and merged actions.
  const aiFunctionDocs = {}
  for (const src of [takeover, ...apps]) {
    const docs = src && src.aiFunctionDocs
    if (!docs || typeof docs !== 'object') continue
    for (const [name, text] of Object.entries(docs)) {
      if (!(name in aiFunctionDocs)) aiFunctionDocs[name] = text
    }
  }

  // Chat starters: round-robin across sources (takeover first, then each app)
  // so every installed app is represented before any single app's long list
  // fills the cap — six apps × 4-5 starters each would otherwise overflow the
  // composer with one app's prompts.
  const MAX_STARTERS = 12
  // A starter that names its app's OWN FORM (`{ text, form }`) carries the
  // app it came from (`app`), so the merged list still knows whose form it
  // opens (assistantStarters.js moduleStarters → assistantStarterForm).
  const ownStarters = (src) =>
    (Array.isArray(src && src.chatStarters) ? src.chatStarters : []).map((it) =>
      it && typeof it === 'object' && typeof it.form === 'string' && !it.app && src.id
        ? { ...it, app: String(src.id) }
        : it
    )
  const starterLists = [ownStarters(takeover), ...apps.map(ownStarters)].filter(
    (l) => Array.isArray(l) && l.length
  )
  const chatStarters = []
  for (let i = 0; chatStarters.length < MAX_STARTERS; i++) {
    let took = false
    for (const list of starterLists) {
      if (i >= list.length || chatStarters.length >= MAX_STARTERS) continue
      if (!chatStarters.includes(list[i])) chatStarters.push(list[i])
      took = true
    }
    if (!took) break
  }

  const pages = { ...((takeover && takeover.pages) || {}) }
  for (const p of apps) Object.assign(pages, p.pages || {})
  const components = uniq([
    (takeover && takeover.components) || [],
    ...apps.map((p) => Object.keys(p.components || {}))
  ])
  const stateKeys = uniq([
    (takeover && takeover.stateKeys) || [],
    ...apps.map((p) => p.stateKeys || [])
  ])
  const loaders = { ...((takeover && takeover.loaders) || {}) }
  for (const p of apps) Object.assign(loaders, _discoverLoaders(p.functions))
  const dataRecipes = [
    ...((takeover && takeover.dataRecipes) || []),
    ...apps.flatMap((p) => p.dataRecipes || [])
  ]

  return {
    id: (takeover && takeover.id) || null,
    type: (takeover && takeover.type) || 'workspace',
    manifestVersion: (takeover && takeover.manifestVersion) || 2,
    config: (takeover && takeover.config) || null,
    widgets: (takeover && takeover.widgets) || null,
    // Takeover-only, like config/widgets: the immersive-chrome gates read
    // root.workspaceProject.chrome — this composed republish (any workspace
    // with an additive app installed) silently DROPPED it, so immersive
    // never activated exactly where additive apps were present (found live
    // on bellforge 2026-08-12: manifest chrome published, stash null).
    chrome: (takeover && takeover.chrome) || null,
    // Takeover-only like chrome: the additive apps' canvas pages are read off
    // their own `root.workspaceApps[]` entries (isModuleCanvasRoute).
    canvasRoutes: (takeover && takeover.canvasRoutes) || null,
    // Takeover-only: each additive app's own `opens` is read off its
    // `root.workspaceApps[]` entry, so unioning them here would double-count.
    opens: (takeover && takeover.opens) || null,
    // Takeover-only for the same reason, and moduleRecordRoutes reads the
    // apps' rows straight off `root.workspaceApps[]`.
    recordRoutes: (takeover && takeover.recordRoutes) || null,
    // Takeover-only too: moduleRecordRows reads the apps' rows off
    // `root.workspaceApps[]`.
    recordRows: (takeover && takeover.recordRows) || null,
    // Takeover-only too: assistantAppViews reads the apps' views off
    // `root.workspaceApps[]`.
    views: (takeover && takeover.views) || null,
    forms: (takeover && takeover.forms) || null,
    pages,
    aiFunctions,
    aiFunctionDocs,
    readOnlyAiFunctions,
    // Every installed app's approval-gated action keys (the merged registry
    // the one propose / approve / reject trio resolves) and their own words.
    actionKeys: Object.keys(mergedActions),
    actionDocs: moduleActionDocs(mergedActions),
    chatStarters,
    components,
    stateKeys,
    loaders,
    dataRecipes
  }
}

// ── Boot reveal route gate (boot fix step 2) ────────────────────────────────
// The boot loader used to wait for the WHOLE module-apps pass (or a 7 s timer
// that a busy main thread starved: Natali 2026-09-29, due ~13.3 s, fired at
// 52.5 s). A page needs at most ONE app: the one whose page the URL names.
// `whenAppRouteReady(root, pathname)` resolves when that path is routable —
// a shell route or takeover page at once, an app page as soon as its app
// registers — or when the running pass settles (after which no app can still
// claim the path, so a genuine 404 is right). No pass in flight: at once.
const _routeWaiters = new Set()
const _pathRoutable = (root, routePath) => {
  const ctx = _ctx(root)
  if (ctx && ctx.pages && Object.prototype.hasOwnProperty.call(ctx.pages, routePath)) return true
  const routedEl = _routedEl(root)
  const routes = routedEl && routedEl.routes
  return !!(routes && routePath !== '/*' && Object.prototype.hasOwnProperty.call(routes, routePath))
}
const _notifyRouteWaiters = (root, settled = false) => {
  for (const waiter of [..._routeWaiters]) {
    if (waiter.root !== root) continue
    if (settled || _pathRoutable(root, waiter.routePath)) {
      _routeWaiters.delete(waiter)
      waiter.resolve(true)
    }
  }
}
export const whenAppRouteReady = (root, pathname) => {
  const raw =
    pathname ||
    (typeof window !== 'undefined' && window.location && window.location.pathname) ||
    '/'
  const routePath = shellStripWorkspacePrefix(String(raw).split('?')[0]) || '/'
  if (!root || !_appsFlight || _appsFlightRoot !== root || _pathRoutable(root, routePath)) {
    return Promise.resolve(true)
  }
  return new Promise((resolve) => _routeWaiters.add({ root, routePath, resolve }))
}

// Load the workspace's ADDITIVE apps (`settings.workspaceApps[]`) into the live
// context, in parallel to the single takeover module (loadWorkspaceProject).
// Diff-based: apps dropped from the list are torn down, new apps are injected,
// apps already active are left in place (no re-fetch — _loadPublishedModule is
// memoized). Sets `root.workspaceApps` to the loaded manifests' surface (id +
// navbar + launcher apps + widgets) for resolveWorkspaceConfig to compose and
// the UI to list. Returns that array. Never throws — a bad app is skipped so
// the rest (and the shell) stay up. Call alongside loadWorkspaceProject, BEFORE
// applyWorkspaceConfig.
export const loadWorkspaceApps = async (root, sdk, opts = {}) => {
  if (!root) return []
  // A comm host loads no apps either (see loadWorkspaceProject).
  if (shellIsCommHost()) return []
  const owner = _claimModuleOwner(root, sdk)
  if (!owner) return []
  // Boot reuse (see `_appsSettled`) — checked BEFORE the activating flag goes
  // up, so a reused pass never shows "Opening apps…" again. The surface
  // identity check refuses a root whose app list was wiped or replaced since.
  if (opts.boot === true && !_appsFlight) {
    const held = _appsSettled
    if (
      held &&
      held.key === _loaderFlightKey(root) &&
      held.root === root &&
      Array.isArray(root.workspaceApps) &&
      root.workspaceApps === held.surface
    ) {
      return held.result
    }
  }
  // Apps inject their pages asynchronously; anything that renders in the
  // meantime (notably the not-found page) must be able to tell "still
  // activating" from "genuinely unresolvable" — otherwise a cold load of an
  // installed app's route flashes "Not found" in the tab title.
  try {
    if (root && typeof root.update === 'function') root.update({ _wsAppsActivating: true })
  } catch (flagErr) {
    /* a state that cannot take the flag simply gets the old behaviour */
  }
  // update() can synchronously hand the shell to another root.
  if (!_moduleOwnerCurrent(owner)) return []
  const flightKey = _loaderFlightKey(root)
  if (_appsFlight && _appsFlightKey === flightKey && _appsFlightRoot === root) {
    return _appsFlight
  }
  const run = _loadWorkspaceAppsPass(root, sdk, owner, !!opts.quiet)
  if (!_moduleOwnerCurrent(owner)) return run
  _appsFlightKey = flightKey
  _appsFlightRoot = root
  _appsFlight = run
  const clear = () => {
    if (_appsFlight !== run) return
    if (_loaderFlightKey(root) === flightKey) root.update?.({ _wsAppsActivating: false })
    _appsFlight = null
    _appsFlightKey = null
    _appsFlightRoot = null
    // The pass settled: a path still unclaimed now is not an app page.
    _notifyRouteWaiters(root, true)
  }
  run.then(clear, clear)
  return run
}

// Sub-phase telemetry for BF-MODULE-APPS-LOADER-INTERMITTENT-SLOW-1 — the
// wsboot:module-apps span (loadPrivateData.js) swung 755ms-13,578ms across
// identical cold boots with no code/deploy change between them. These marks
// localize the swing to one of the three candidates named in that ticket:
// the getWorkspaceCached full-record fetch, the capped per-app prefetch, or
// a _scheduleModuleConverge retry firing mid-pass. Guarded no-op like
// loadPrivateData.js's wsboot:* marks.
const _mark = (name) => {
  try {
    if (typeof performance !== 'undefined') performance.mark(name)
  } catch (_) {
    /* telemetry only */
  }
}
const _measure = (name, from, to) => {
  try {
    if (typeof performance !== 'undefined') performance.measure(name, from, to)
  } catch (_) {
    /* telemetry only */
  }
}

const _loadWorkspaceAppsPass = async (root, sdk, owner, quiet = false) => {
  const seq = ++_appsLoadSeq
  _appsSettled = null
  const flightKey = _loaderFlightKey(root)
  const isStale = () =>
    seq !== _appsLoadSeq || _loaderFlightKey(root) !== flightKey || !_moduleOwnerCurrent(owner)
  updateWorkspaceActivation(
    root,
    'apps',
    quiet ? { state: 'loading', quiet: true } : { state: 'loading' }
  )
  _mark('wsboot:module-apps:pass-start')

  const ws = root.workspace || {}
  const wsId = ws.id || ws._id
  // DECISION site (spec §5) — same branch, same stakes as loadWorkspaceProject's
  // read above, and here the wrong answer is the measured one: a list-shaped
  // record read as "resolved" makes `_appRefs(null)` an authoritative EMPTY app
  // list, which publishes `workspaceApps: []` and latches the surface (the
  // natali 0-of-13 failure mode described below). Provenance, not bag presence.
  const { settings: heldSettings, ready: heldReady } = shellWorkspaceSettings(root)
  let settings = heldReady ? heldSettings : null
  // Whether this pass POSITIVELY read the workspace's settings (off the record
  // on root, or by fetching the record — a fetched record WITHOUT a settings
  // bag is still a definitive read). Mirrors loadWorkspaceProject's
  // `settingsResolved`: anything less means the scope is unresolved and this
  // pass is PROVISIONAL — it must not publish a definitive empty app list,
  // must not latch `_appRoutesSettled`, and must leave a converge retry armed.
  // (Seeded from the accessor's `ready`; the fetch fallback below is untouched —
  // the accessor never fetches, so it narrows the initial read only.)
  let settingsResolved = heldReady
  if (
    !settings &&
    wsId &&
    _belongsToActiveOrg(root) &&
    sdk &&
    typeof sdk.getWorkspace === 'function'
  ) {
    _mark('wsboot:module-apps:ws-fetch:start')
    try {
      const full = await _waitForModuleRequest(getWorkspaceCached(sdk, wsId))
      // Org-verify the FETCHED record too (same guard as loadWorkspaceProject):
      // a stale pointer's foreign-org workspace must neither load foreign apps
      // nor count as this org's definitive "no apps".
      const orgId = _lc((root.org && (root.org.id || root.org._id)) || '')
      const recOrg = _lc((full && (full.organization || full.organizationId || full.org)) || '')
      if (full && orgId && recOrg && recOrg !== orgId) {
        console.warn('[workspaceApps] fetched workspace belongs to another org — apps deferred')
      } else if (full) {
        settings = full.settings || null
        settingsResolved = true
      }
    } catch (e) {
      console.warn('[workspaceApps] full workspace fetch failed', e && e.message)
    }
    _mark('wsboot:module-apps:ws-fetch:end')
    _measure(
      'wsboot:module-apps:ws-fetch',
      'wsboot:module-apps:ws-fetch:start',
      'wsboot:module-apps:ws-fetch:end'
    )
    if (isStale()) return null
  }

  const belongs = _belongsToActiveOrg(root)
  // Transient scope miss (no workspace resolved yet, org guard blocking while
  // a cross-org /w/<slug> boot converges, record fetch failed) with the SAME
  // workspace's apps possibly already live: do NOT tear down, do NOT publish
  // an empty list, do NOT settle — retry once the scope lands. This is the
  // natali 0-of-13 root cause: boot ran this loader while root.org still held
  // the server-side active org (URL named a different org), refs resolved [],
  // and the unconditional publish+settle below suppressed every later retry
  // (the converge loop bailed on the takeover module's own definitive
  // 'no-module' without ever re-running the apps loader).
  if (!wsId || !settingsResolved) {
    _lastAppsOutcome = 'unresolved'
    console.warn('[workspaceApps] scope unresolved on this pass — will retry', {
      wsId: wsId || null,
      belongs,
      settingsResolved
    })
    _scheduleModuleConverge(root)
    _mark('wsboot:module-apps:converge-scheduled')
    return null
  }
  if (!belongs) {
    // A workspace from ANOTHER org on root — either a real org switch-away
    // (onWorkspaceChange re-runs this loader for the new scope) or a boot
    // whose org hasn't converged yet. Tear the foreign apps down NOW (cross-
    // org isolation must never wait), publish the cleared surface, but treat
    // the pass as transient: no `_appRoutesSettled` latch, converge retry
    // armed so the converged scope re-loads its own apps.
    for (const ledgerId of [..._injectedByApp.keys()]) _teardownApp(root, ledgerId)
    for (const ledgerId of [..._navLangLedgers.keys()]) _dropNavLang(ledgerId)
    _lastAppsOutcome = 'unresolved'
    console.warn('[workspaceApps] workspace not in active org on this pass — will retry')
    root.update({ workspaceApps: [] })
    _scheduleModuleConverge(root)
    _mark('wsboot:module-apps:converge-scheduled')
    return null
  }

  const refs = _appRefs(settings)
  const desiredIds = new Set(refs.map((r) => _ledgerKey(wsId, r)))

  // Teardown apps no longer in the list (uninstalled, org switched away, or
  // belonging to the workspace we just switched OFF — see `_ledgerKey`).
  for (const ledgerId of [..._injectedByApp.keys()]) {
    if (!desiredIds.has(ledgerId)) _teardownApp(root, ledgerId)
  }
  // Boot fix step 3a: each entry's server `nav.lang` (the words its nav rows
  // use) merges before any app loads, the way _mergeModuleTranslations merges
  // an app's own bundle (never over an existing key). Keyed by the tenant
  // ledger id: an app of another workspace leaves with its keys.
  for (const ledgerId of [..._navLangLedgers.keys()]) {
    if (!desiredIds.has(ledgerId)) _dropNavLang(ledgerId)
  }
  const _navCtx = _ctx(root)
  for (const r of refs) {
    const nav = r && r.nav
    if (!nav || nav.v !== 1 || !nav.lang || typeof nav.lang !== 'object') continue
    const ledgerId = _ledgerKey(wsId, r)
    const held = _navLangLedgers.get(ledgerId)
    if (held && held.hash === nav.hash) continue
    if (held) _dropNavLang(ledgerId)
    const ledger = _mergeModuleTranslations(_navCtx, { designSystem: { lang: nav.lang } })
    if (ledger) _navLangLedgers.set(ledgerId, { hash: nav.hash, ledger })
  }

  // Capability bridges (moduleData / moduleAiChat / moduleFileUpload) are shared
  // fn names hardcoded by every app's page code, so they can't be per-app —
  // instead we build ONE bridge set over the UNION of all apps' grants (below,
  // after the load loop) and re-derive it on every load. Rebuild each time: the
  // union changes as apps add/remove, so tear the old synthetic ledger down first.
  _teardownApp(root, _CAPS_LEDGER)
  const mergedCollections = new Set()
  const mergedOps = new Set()
  // capabilities.integrations (tickets/workspace.md "module-plane
  // `integrations` capability — make 'Data' an installable catalog app",
  // DECIDED 2026-08-06) is a GRANT SET like records.collections — the union
  // of every installed app's granted kinds/capabilities determines what the
  // one shared moduleData bridge may reach, same reasoning as
  // mergedCollections/mergedOps above (unlike ai/files' single CONFIG object,
  // which genuinely can't be unioned — see the note below). A wildcard `'*'`
  // from ANY app wins outright (widest declared grant).
  const mergedIntegrationKinds = new Set()
  const mergedIntegrationCapabilities = new Set()
  let integrationKindsWildcard = false
  let integrationCapabilitiesWildcard = false
  // The actual ai/files grant CONFIG (not a boolean) — the bridges read
  // capabilities.ai / capabilities.files as objects, which can't be unioned
  // like the collections set above. COMPATIBLE configs merge (deep-equal
  // collapses, files.mimeTypes unions widest — see the checks in the load
  // loop below); only genuinely DIVERGENT configs keep the first app's grant
  // and warn, because one shared bridge cannot serve two different configs —
  // that day needs per-app namespaced bridges. The `…By` trackers carry which
  // app's grant currently holds the slot, so a divergence warn names both
  // sides instead of just the loser.
  let aiCap = null
  let aiCapBy = null
  let filesCap = null
  let filesCapBy = null
  // pkg → exact version, unioned over every installed app (moduleImportBridge).
  const mergedDependencies = new Map()

  const ctx = _ctx(root)
  const routedEl = _routedEl(root)
  const loaded = []
  // Full normalized module objects for the loaded module-apps (not extension
  // refs) — feed the merged action registry + the composed AI-surface stash.
  const appProjects = []

  // BOUNDED parallel prefetch of every module-app's published payload — the fetches
  // are independent network work (env resolve + project fetch, ~2s each on a
  // busy backend) and were paid SERIALLY by the loop below: measured live at
  // 4.7s of a 5.7s boot-loader window with two apps (wsboot:module-apps,
  // 2026-07-28). The loop stays serial for its ORDER-dependent side effects
  // (first-wins ai/files capability grants, page-collision checks, injection
  // ledger) and just consumes settled results. _loadPublishedModule has its
  // own memo + in-flight dedup, so concurrent starts are safe.
  //
  // …but UNBOUNDED parallelism starved the page the user is actually looking
  // at. Each app costs TWO round-trips (getProjectByKey + listEnvironments on
  // a cold env-config cache), so a workspace with 8 apps opened ~16 sockets
  // at once and consumed the browser's whole per-host HTTP/1.1 budget (~6).
  // Measured live on /natali/shop (2026-08-08 per Nika "does not render until
  // i pan"): app mounted at 5.3s, then six `system/*-app` fetches ALL started
  // at 5371ms taking ~2.7-2.9s each, and the canvas project's own data — plus
  // therefore every artboard tile — did not land until 8.5s (17.9s on a
  // busier run). Panning only *appeared* to fix it: by then the queue had
  // drained. Capping in-flight prefetches leaves sockets free for the route's
  // own data while keeping most of the parallel win over the old serial loop.
  // Deliberately NOT 1 (that reinstates the 4.7s serial window this replaced).
  // SELF-CONTAINED on purpose. This runs on the boot path
  // (applyWorkspaceConfig → loadWorkspaceApps), and reaching OUT to a
  // module-scope binding from here threw
  // `ReferenceError: _concurrencyGate is not defined` on the deployed dev
  // bundle — every installed workspace app's routes 404'd because
  // applyWorkspaceConfig aborted before composing them (2026-08-08, P0;
  // prod, which predates the helper, was unaffected). The declaration and
  // the export were both correct in source: the reference simply did not
  // resolve once bundled. A boot-critical helper must therefore not depend
  // on cross-scope resolution at all — build it here, from nothing but its
  // own arguments. `_concurrencyGate` stays exported as the canonical
  // implementation the colocated node --test suite pins.
  const _startPrefetch = ((limit) => {
    let active = 0
    const queued = []
    const pump = () => {
      while (active < limit && queued.length) {
        active++
        queued.shift()()
      }
    }
    return (fn) =>
      new Promise((resolve, reject) => {
        queued.push(() => {
          Promise.resolve()
            .then(fn)
            .then(resolve, reject)
            .finally(() => {
              active--
              pump()
            })
        })
        pump()
      })
  })(3) // see the module-const note above for why 3, and why it is inlined

  // AI-built extension refs resolve against the workspace extension store, not
  // the published-module pipeline — ONE listExtensions round-trip covers every
  // extension ref in the list. Rows carry name/source/pages, and the privacy
  // filter is server-side (an added app is shared, so every member sees it).
  // null until the LIST itself answers: a missing row is a verdict about the
  // extension only when the list came back, never when the call failed.
  // The extension list and published payloads are independent reads. Keep them
  // under one limit so a slow extension does not block all module downloads.
  // Queue it first; registration below still waits for its rows and follows
  // install order. A superseded queued task must not change the AI scope.
  const extensionRowsPromise = refs.some((r) => r && r.type === 'extension')
    ? _startPrefetch(async () => {
        if (isStale()) return null
        try {
          const ai = shellGetAiService()
          if (ai && typeof ai.listExtensions === 'function' && wsId) {
            if (typeof ai.updateContext === 'function') {
              ai.updateContext({ activeWorkspaceId: String(wsId) })
            }
            const res = await _waitForModuleRequest(
              ai.listExtensions({ workspaceId: String(wsId) })
            )
            return Array.isArray(res) ? res : null
          }
        } catch (e) {
          console.warn('[workspaceApps] extension list failed', e && e.message)
          return null
        }
        return null
      })
    : Promise.resolve(null)
  // ROUTE-FIRST ORDERING. The gate above starts prefetches in INSTALL order,
  // so on a workspace with a dozen apps the one the user actually navigated to
  // could sit in the last batch of 3 — measured ~60s to first paint on
  // /w/natali/…/excalidraw, with the route parked on the not-found page the
  // whole time while crm / hr / scheduling loaded ahead of it. Nothing was
  // broken; the app the URL names was simply last in line.
  //
  // So the ref whose key matches the current route's first segment goes first.
  // Pure ordering — it changes no outcome, only which app the user waits for.
  // The match is a heuristic (an app's key is its route segment by
  // convention); when it misses, the order is exactly what it was before.
  // `sort` is stable, so everything else keeps its install order.
  // Match against EVERY segment of the path, not the first one. The workspace
  // prefix helper strips `/w/<org>` but leaves the workspace slug, so
  // `/w/natali/ws_X/excalidraw` starts at `ws_X`, not the app; and an app's own
  // nested route (`…/excalidraw/board/1`) puts the key mid-path. A segment set
  // is right for all of those shapes and costs nothing.
  const _routeSegments = (() => {
    try {
      const location = typeof window !== 'undefined' && window.location
      if (!location) return null
      // PageSlot deep links name the requested app in ?page=, while the
      // pathname can still point to Home or to another installed app.
      const page = new URLSearchParams(location.search || '').get('page')
      const segs = String(page || location.pathname || '')
        .split('?')[0]
        .split('#')[0]
        .split('/')
        .filter(Boolean)
        .map((seg) => seg.toLowerCase())
      return segs.length ? new Set(segs) : null
    } catch (segErr) {
      return null
    }
  })()
  const _refRouteKey = (r) => {
    const id = _appId(r) || ''
    const parts = String(id).split('/')
    return (parts[parts.length - 1] || '').toLowerCase()
  }
  const _prefetchOrder = refs.filter((r) => r && r.type !== 'extension')
  if (_routeSegments) {
    const matchesRoute = (ref) => {
      const key = _refRouteKey(ref)
      // Published app keys conventionally add -app to their public route.
      // Keep exact matches too, including projects whose route uses -app.
      return _routeSegments.has(key) || _routeSegments.has(key.replace(/-app$/, ''))
    }
    _prefetchOrder.sort((a, b) => (matchesRoute(a) ? 0 : 1) - (matchesRoute(b) ? 0 : 1))
  }
  _mark('wsboot:module-apps:prefetch:start')
  // CHANNEL READS START TOGETHER (2026-10-03). Each app's channel resolution
  // (getProjectByKey + /core/projects/:id/environments) is small metadata; it
  // used to sit INSIDE the gated task below, so on dev 1061f366e the additive
  // apps' environments reads ran a few at a time (3453 / 3702 / 3702 / 3835 ms
  // on a cold /w/unreal/default). Both API hosts serve HTTP/2. Start every
  // app's channel read now, ungated; _loadPublishedModule picks the same
  // promise up from the warm holder. The payload downloads keep the 3-slot
  // gate (the /natali/shop starvation note above is about those).
  // Self-contained on purpose (see the gate's own note): no module-scope helper.
  try {
    const warm = (globalThis.__wsEnvWarm = globalThis.__wsEnvWarm || new Map())
    const now = Date.now()
    for (const r of _prefetchOrder) {
      if (!r || r.type !== 'project' || !r.owner || !r.key || !r.channel || r.version) continue
      const k =
        String(r.owner) + '/' + String(r.key) + '@' + String(r.channel) + '#' + String(wsId || '')
      // The takeover pass (or the boot prefetch) just resolved this exact
      // channel and handed it over (_channelHandoff): _loadPublishedModule
      // takes that flight, so warming here would re-send getProjectByKey +
      // listEnvironments for nothing (measured twice per cold boot on
      // /w/vault/default). Read inline, like `_pipeline` above.
      const handed = _channelHandoff.get(k)
      if (handed && Date.now() - handed.at < 30000) continue
      // Every pass warms afresh (an explicit reload must re-resolve every
      // channel); _loadPublishedModule CONSUMES the entry, so it is one read.
      const prom = Promise.resolve()
        .then(() =>
          _pipeline.resolveEnv({ owner: r.owner, key: r.key }, r.channel, { workspaceId: wsId })
        )
        .catch(() => null)
      warm.set(k, { p: prom, at: now })
    }
  } catch (e) {
    console.warn('[workspaceApps] channel warm-up skipped', e && e.message)
  }
  const _prefetched = new Map(
    _prefetchOrder.map((r) => [
      _appId(r),
      // devOverride:false — the dev override targets THE WORKSPACE MODULE,
      // not the apps: with it on, the override's {owner,key} would merge
      // over EVERY app ref in this loop, collapsing all of them onto one
      // project. wsId still rides so the server's workspace-module grant
      // can authorize the read (see _loadPublishedModule's own note).
      _startPrefetch(() =>
        isStale()
          ? null
          : _loadPublishedModule(r, sdk, wsId, { devOverride: false, lazyParts: ['components'] })
      ).catch((e) => {
        console.warn('[workspaceApps] module load failed', _appId(r), e && e.message)
        return null
      })
    ])
  )

  const extensionRows = await extensionRowsPromise
  if (isStale()) return null

  // ── Extension app pages (type 'extension') — a page + navbar/launcher entry
  // over the SAME isolated iframe pipeline /add-app renders with (ExtensionEmbed
  // → renderExtensionInPane audit/parse/scoped-bridge). Never an in-realm mount
  // of AI-authored source, so the module tier gate below doesn't apply. The
  // page needs its list row and nothing else: it registers once per install
  // (the ledger guards a second call), here or in the loop below.
  const extensionRowOf = (ref) =>
    (extensionRows || []).find((x) => x && String(x.id) === String(ref.projectId)) || null
  // ONE builder with the record's `nav` copy (workspaceConfigs.js), so a row
  // painted at boot does not move when the extension loads.
  const extensionRowsFor = (ref, row, name) =>
    extensionAppRows({ key: row.key, projectId: ref.projectId, name })
  const registerExtensionPage = (ref, row) => {
    const ledgerId = _ledgerKey(wsId, ref)
    const path = extensionRowsFor(ref, row, '').navbar[0].path
    // A nameless extension row: the bundle word through the routed element,
    // or — with no element to resolve through — the KEY itself, which the
    // navbar tab and the launcher tile resolve through polyglot at render.
    const appName =
      row.name ||
      (routedEl && typeof routedEl.call === 'function'
        ? routedEl.call('polyglot', 'workspace.apps.nameFallback')
        : 'workspace.apps.nameFallback')
    if (!_injectedByApp.has(ledgerId) && ctx) {
      const led = { components: [], functions: [], pages: [] }
      ctx.pages = ctx.pages || {}
      // WS-MODULE-OWN-KEYS-STALE-ACROSS-ORG-SWITCH-1 — see _captureBags.
      led.bags = _captureBags(ctx, routedEl)
      const taken = path in ctx.pages || !!(routedEl && routedEl.routes && path in routedEl.routes)
      if (taken) {
        console.warn('[workspaceApps] extension page collision skipped:', path)
      } else if (routedEl && typeof routedEl.call === 'function') {
        // IN-SHELL page def (no iframe — 2026-07-25 per Nika): the installed
        // app renders as a native workspace view through the same audited +
        // entity-scoped builder the /add-app canvas and preview use.
        // `crmLeads` (the row's `{ enabled }`, server listExtensions) turns on
        // the visitor line under a sign-up form. The row is read once per
        // install, so a toggle change shows after the next shell reload.
        const built =
          row.appKind === 'multipage'
            ? routedEl.call('buildInlineMultiPageDef', {
                name: appName,
                pages: row.pages || [],
                crmLeads: row.crmLeads
              })
            : routedEl.call('buildInlineExtensionDef', {
                name: appName,
                source: row.source || '',
                crmLeads: row.crmLeads
              })
        // A contained refusal (generatedAppContainment.js) registers the
        // blocked-feature page in place of the app: the route still opens,
        // none of the app's own code runs.
        if (built && built.def && (built.ok !== false || built.blocked)) {
          const pageDef = {
            ...built.def,
            metadata: { ...(built.def.metadata || {}), title: appName }
          }
          ctx.pages[path] = pageDef
          if (routedEl.routes) routedEl.routes[path] = pageDef
          led.pages.push(path)
        } else {
          console.warn('[workspaceApps] extension def build failed:', path, built && built.error)
        }
      }
      _injectedByApp.set(ledgerId, led)
    }
    return { appName, path }
  }

  // A ?page= deep link to an extension opens its window as soon as the list
  // answers. The restore at the pass end waited for every module payload
  // installed ahead of the extension: persona round ai-r1 (2026-10-28)
  // reloaded `?page=/x/restock-board` twice and saw no window at the ~9 s
  // look. A MODULE page stays at the pass end: it calls the shared capability
  // bridges, which exist only after the loop. Install order still decides the
  // published list below. Same param read as _restorePageParam.
  {
    const search =
      (typeof window !== 'undefined' && window.location && window.location.search) || ''
    const liveParam = search ? new URLSearchParams(search).get('page') : null
    const pageParam = liveParam || (!_bootPageParamUsed ? _bootPageParam : null)
    const pagePath = pageParam ? String(pageParam).split('?')[0].split('#')[0] : null
    const target =
      pagePath &&
      refs.find((ref) => {
        const row = ref && ref.type === 'extension' ? extensionRowOf(ref) : null
        return !!row && extensionRowsFor(ref, row, '').navbar[0].path === pagePath
      })
    if (target) {
      registerExtensionPage(target, extensionRowOf(target))
      if (ctx && ctx.pages && ctx.pages[pagePath]) _restorePageParam(root, ctx.pages)
    }
  }

  for (const [index, ref] of refs.entries()) {
    // A resolved-payload loop otherwise remains entirely in microtasks. Give
    // input, paint and the reveal watchdog a task between registrations, then
    // verify ownership again before touching the next app's registry.
    // A hidden tab throttles timers: there the yield is a plain task (_passYield).
    if (index > 0) await _passYield()
    if (isStale()) return null
    const appId = _appId(ref)
    // Catalog identity above (published on `loaded`, keys `_prefetched`);
    // tenant-scoped identity below for the injection ledger ONLY.
    const ledgerId = _ledgerKey(wsId, ref)

    // An extension app: its page registers above (registerExtensionPage).
    if (ref.type === 'extension') {
      const row = extensionRowOf(ref)
      if (!row) {
        console.warn('[workspaceApps] extension ref unresolved:', ref.projectId)
        if (extensionRows !== null) _refusals.set(appId, { code: 'missing', message: null })
        continue
      }
      const { appName, path } = registerExtensionPage(ref, row)
      const extRowsFor = (name) => extensionRowsFor(ref, row, name)
      loaded.push({
        id: String(ref.projectId),
        appId,
        manifestVersion: null,
        navbar: extRowsFor(appName).navbar,
        // Launcher tiles follow the { title, caption, icon, iconBg, route }
        // contract (what SearchAppTile reads) — the old { label, path } shape
        // rendered a nameless, glyphless, unclickable tile in the apps
        // dropdown (the natali blank-tiles bug, 2026-07-30). Emit the real
        // meta an extension has (name + route); icon/iconBg fall back at the
        // canonical spot (launcherTiles.js normalizeLauncherTile).
        //
        // `projectId` + `kind` ride along as the tile's IDENTITY (not display
        // meta): they mark this as a custom, AI-built app so the launcher's
        // right-click menu can offer "Edit" and route back into /add-app for
        // exactly this extension (2026-08-02 per Nika "on custom made apps you
        // can add context menu edit"). normalizeLauncherTile spreads the tile,
        // so both survive normalization. House apps carry neither.
        apps: extRowsFor(appName).apps,
        widgets: null,
        // An extension is a page: it never feeds the home.
        homeProviders: [],
        pages: [path]
      })
      continue
    }

    // Consume the parallel prefetch (falls back to a direct load for safety —
    // e.g. a ref shape the prefetch filter didn't cover). Errors were already
    // caught + null'd inside the prefetch.
    let project = null
    const _spanKey = String((ref && ref.key) || appId)
    const tWait = _spanStart()
    try {
      // devOverride:false — see the prefetch above: apps stay override-free,
      // but wsId rides for the server's workspace-module grant.
      project = await (_prefetched.get(appId) ??
        _loadPublishedModule(ref, sdk, wsId, { devOverride: false, lazyParts: ['components'] }))
      _span(_spanKey, 'wait', tWait)
    } catch (e) {
      console.warn('[workspaceApps] module load failed', appId, e && e.message)
      project = null
    }
    if (isStale()) return null
    if (!project) continue
    appProjects.push(project)
    // The home surfaces this app feeds — known now, before its setup.
    const homeProviders = _homeProvidersOf(project)

    // WS-MODULE-FN-NAME-COLLISION-CROSS-APP — every installed app gets its own
    // resolution scope, UNCONDITIONALLY: before the injection guard, so a
    // re-pass (already injected) and a takeover-duplicated app (injection
    // skipped) both still have one. Without it the second app to declare a
    // name would keep resolving the first app's implementation — measured on
    // my.symbols.app/w/natali, where scheduling's pages ran CRM's
    // `natEnsureLoaded` and its own `natFetchAll` never loaded a single
    // collection.
    if (ctx) _moduleScopeFor(ctx, project, { ...(project.functions || {}) })

    // Accumulate this app's capability grants into the union (used for the
    // single merged bridge built after the loop) — even for an already-injected
    // app, so a reload rebuilds the union correctly.
    const recs = project.capabilities && project.capabilities.records
    if (recs) {
      // Entries may be strings or `{ key, read, write }` objects (F1) — the
      // union normalizes to KEYS: the merged client bridge gates membership
      // only, per-collection levels stay server-enforced per caller.
      for (const c of Array.isArray(recs.collections) ? recs.collections : []) {
        const k = recordsEntryKey(c)
        if (k) mergedCollections.add(k)
      }
      for (const o of Array.isArray(recs.ops) ? recs.ops : []) mergedOps.add(o)
    }
    const integrations = project.capabilities && project.capabilities.integrations
    if (integrations) {
      if (integrations.kinds === '*') integrationKindsWildcard = true
      else
        for (const k of Array.isArray(integrations.kinds) ? integrations.kinds : []) {
          mergedIntegrationKinds.add(k)
        }
      if (integrations.capabilities === '*') integrationCapabilitiesWildcard = true
      else
        for (const c of Array.isArray(integrations.capabilities) ? integrations.capabilities : []) {
          mergedIntegrationCapabilities.add(c)
        }
    }
    // ai/files grants are CONFIG objects (system prompt, modelMode,
    // visibility), not a collections set — two DIFFERENT configs can't share
    // one moduleAiChat/moduleFileUpload fn (that day needs per-app namespaced
    // bridges; today's manifest generation hardcodes the plain fn names onto
    // every app's page code, so it's a bigger design change). But IDENTICAL
    // configs are not a collision at all: jobs-app and excalidraw both grant
    // `files:{visibility:'private'}`, and the old first-wins-with-warning
    // path fired on every boot over a distinction without a difference. So:
    // compatible configs MERGE — deep-equal collapses silently, and
    // files.mimeTypes unions to the widest grant (absent already means the
    // server's own allowlist and stays absent). Only genuine divergence keeps
    // the first app's grant and warns, naming BOTH apps.
    if (project.capabilities && project.capabilities.ai) {
      const nextAi = project.capabilities.ai
      if (!aiCap) {
        aiCap = nextAi
        aiCapBy = project.id
      } else if (!_capConfigsEqual(aiCap, nextAi)) {
        console.warn(
          '[workspaceApps] ai capability divergence — keeping',
          `${aiCapBy},`,
          'skipping',
          project.id
        )
      }
    }
    if (project.capabilities && project.capabilities.files) {
      const nextFiles = project.capabilities.files
      if (!filesCap) {
        filesCap = nextFiles
        filesCapBy = project.id
      } else {
        const merged = _mergeFilesCaps(filesCap, nextFiles)
        if (merged) filesCap = merged
        else {
          console.warn(
            '[workspaceApps] files capability divergence — keeping',
            `${filesCapBy},`,
            'skipping',
            project.id
          )
        }
      }
    }
    // Declared vendor libraries union across installed apps. Unlike ai/files
    // (single config objects) this is a per-package map, so two apps CAN each
    // declare their own. A same-package/different-version collision keeps the
    // first and warns — one page can only hold one copy of a library, and
    // silently swapping versions under an app is the worse failure.
    if (project.libraries && typeof project.libraries === 'object') {
      for (const [pkg, version] of Object.entries(project.libraries)) {
        const prev = mergedDependencies.get(pkg)
        if (prev && prev !== version) {
          console.warn(
            '[workspaceApps] dependency version collision — keeping',
            `${pkg}@${prev},`,
            'skipping',
            `${pkg}@${version}`,
            'from',
            project.id
          )
          continue
        }
        mergedDependencies.set(pkg, version)
      }
    }

    // WS-MODULE-OWN-KEYS-STALE-ACROSS-ORG-SWITCH-1 — a workspace can BOTH set
    // a module as its takeover (`settings.workspaceModule`) AND carry that same
    // module in its installed-apps list. `loadWorkspaceProject` injects the
    // takeover's components/functions/pages first (it always runs immediately
    // before this loader on every activation path), so by the time this loop
    // reaches the same project every one of its keys is already in the bags —
    // owned by the TAKEOVER ledger. Re-injecting them here cannot succeed: the
    // skip-existing collision policy drops every fresh copy and logs one warn
    // per key.
    //
    // MEASURED on a PLAIN boot with NO org switch (dev.my.symbols.app,
    // /w/bellforge/ws_UYAOhQyNjm, bundle workspace.09e57f4d.js, 2026-08-28,
    // console.warn hooked with stacks): 26 `[workspaceModule] key collision
    // skipped` — 2 at t=10.5 s from the takeover pass (AppPage, TooltipHidden,
    // both genuine shell keys a module may not shadow) and 23 at t=12.1 s from
    // THIS loop (PipelineBoard, PipelineCard, PipelineStageColumn + 20
    // `pipeline*` functions). The two bursts carry different call stacks into
    // the same `_injectEntries`, which is what proves they are two passes over
    // ONE project rather than one pass running twice.
    //
    // The noise was never the real damage. `_injectEntries` only records keys
    // it actually SET, so the app ledger for a takeover-duplicated app ends up
    // EMPTY — it claims none of the keys it appears to own, and a later
    // `_teardownApp` for it is a silent no-op while the takeover ledger still
    // holds the live copies. That is the same ownership confusion the caps
    // ledger hit in 6a7cdafd3.
    //
    // So: the takeover owns the code, this loop owns the launcher surface. The
    // app still contributes its capability union, its actions and its
    // navbar/launcher entry above and below — only the duplicate injection and
    // the empty ledger it would leave behind are skipped.
    const _ownedByTakeover = !!_activeProjectId && String(project.id) === String(_activeProjectId)
    if (_ownedByTakeover && !_injectedByApp.has(ledgerId)) {
      // Not a collision and not a warn: this is the designed division of
      // ownership, and it must stay quiet or it becomes the noise it replaced.
      _mark('wsboot:module-apps:takeover-dup:' + appId)
    }
    if (!_ownedByTakeover && !_injectedByApp.has(ledgerId) && ctx) {
      // `projectId` alongside the key ledger: the scoped-keys registry and this
      // ledger are keyed by the TENANT-scoped `ledgerId`, but the module state
      // store (WS-MODULE-STATE-SCOPE-1) is keyed by the module PROJECT id — the
      // teardown needs both, and re-deriving one from the other at teardown
      // time is exactly the kind of guess `_captureBags` exists to avoid.
      const led = { components: [], functions: [], pages: [], projectId: project.id }
      ctx.components = ctx.components || {}
      ctx.functions = ctx.functions || {}
      ctx.pages = ctx.pages || {}
      // WS-MODULE-OWN-KEYS-STALE-ACROSS-ORG-SWITCH-1 — see _captureBags.
      led.bags = _captureBags(ctx, routedEl)

      const tInject = _spanStart()
      _injectEntries(
        ctx.components,
        project.components || {},
        led.components,
        'components',
        String(project.id)
      )
      // Same translation merge as the takeover path; the ledger rides the
      // app's own entry so _teardownApp removes exactly this app's keys.
      led.translations = _mergeModuleTranslations(ctx, project)

      // The app's own functions ONLY. The shared capability BRIDGES and the
      // approval-action TRIO are NOT injected per-app — both are built once
      // over the union after the loop (a per-app copy would collide on the
      // shared fn names and only the FIRST app's grant/actions would win —
      // every later app's proposeModuleAction returned "Unknown action").
      _injectEntries(
        ctx.functions,
        { ...(project.functions || {}) },
        led.functions,
        'functions',
        String(project.id)
      )
      _span(_spanKey, 'inject', tInject)

      // WS-MODULE-FN-NAME-COLLISION-CROSS-APP — an ADDITIVE app's pages used to
      // be registered RAW here, with no context of their own, while only the
      // takeover module's pages went through `_wrapModulePages`. That is why
      // the natali failure hit installed apps specifically: with no module
      // context, an app page resolved every `el.call` and every bare component
      // key against the flat root bag, so the second app to declare a name got
      // the first app's implementation. They now carry the same module scope
      // the takeover path has always used (and its design-system config too,
      // when the app ships one — a bonus fix, module tokens previously never
      // reached an installed app's pages).
      // Boot fix step 4b: the wrap itself (_wrapModulePages) runs on the first
      // open or in the app's idle slice (_activateWorkspaceApp); each path
      // holds a placeholder until then. Key reads only — of a page def the
      // placeholder takes the `metadata` reference (the pane title), no more.
      const tRoutes = _spanStart()
      const activation = {
        root,
        ctx,
        routedEl,
        project,
        ledgerId,
        // the catalog identity (owner/key), as `loaded[].appId` publishes it
        catalogId: appId,
        spanKey: _spanKey,
        name: _appDisplayName(project, ref),
        homeProviders,
        placeholders: {},
        promise: null,
        done: false,
        // the first open intent's time (wsopen:<app>), cleared at the reopen
        openAt: null
      }
      for (const path of Object.keys(project.pages || {})) {
        const taken =
          path in ctx.pages || !!(routedEl && routedEl.routes && path in routedEl.routes)
        if (taken) {
          console.warn('[workspaceApps] page collision skipped:', path)
          continue
        }
        const placeholder = _appOpeningPage(activation, path)
        activation.placeholders[path] = placeholder
        ctx.pages[path] = placeholder
        if (routedEl && routedEl.routes) routedEl.routes[path] = placeholder
        led.pages.push(path)
      }
      _appActivations.set(ledgerId, activation)
      _span(_spanKey, 'routes', tRoutes)
      _injectedByApp.set(ledgerId, led)
      // A deep link to this app's page may be holding the boot reveal.
      _notifyRouteWaiters(root)
      // WS-MODULE-SCOPED-KEYS-CONTRACT-1: an additive app declares scoped
      // keys + a boundary hook exactly like the takeover module; keyed by the
      // tenant-scoped ledger id so a switch tears down exactly this install.
      registerModuleScopedKeys(ledgerId, {
        keys: project.scopedKeys || [],
        hook: project.hooks && project.hooks.onWorkspaceSwitch
      })
    }

    loaded.push({
      id: project.id,
      appId,
      manifestVersion: project.manifestVersion,
      // Launcher/navbar surface used by resolveWorkspaceConfig (append) + the
      // installed-apps UI. configDefaults holds the app's own declared navbar.
      navbar:
        (project.configDefaults && Array.isArray(project.configDefaults.navbar)
          ? project.configDefaults.navbar
          : []) || [],
      apps:
        (project.configDefaults && Array.isArray(project.configDefaults.apps)
          ? project.configDefaults.apps
          : []) || [],
      // The app's canvas pages (manifest `canvasRoutes`) — full-bleed with the
      // shell Zoombar (shared moduleRoutes.js isModuleCanvasRoute).
      canvasRoutes: Array.isArray(project.canvasRoutes) ? project.canvasRoutes : null,
      // Published after the app's wrap (boot fix step 4b): before it, a widget
      // would render with the shell's tokens instead of the app's.
      widgets:
        _appActivations.has(ledgerId) && !_appActivations.get(ledgerId).done
          ? null
          : project.widgets || null,
      // The widget ids still on their way (ids only, no defs): the home keeps a
      // saved cell's skeleton only for a widget that WILL arrive for this member.
      pendingWidgets:
        _appActivations.has(ledgerId) && !_appActivations.get(ledgerId).done
          ? (Array.isArray(project.widgets) ? project.widgets : [])
              .map((w) => w && w.id)
              .filter(Boolean)
          : null,
      // The home surfaces this app feeds ('signals' / 'upcoming', [] for
      // none), from this first pass on: the home waits only for a feeder.
      homeProviders,
      // The app's OWN declared default panel set (manifest configDefaults).
      // The app-mode config compose deliberately never takes panels from an
      // app (a marketplace install must never take the dashboard over), so
      // this rides the stash for the home board's per-app widget-defaults
      // rule instead: `appDefaultWidgetIds` (functions/homePanelSet.js)
      // intersects it with `widgets[].id` — an app pins only its own.
      home_default_panels:
        project.configDefaults && Array.isArray(project.configDefaults.home_default_panels)
          ? project.configDefaults.home_default_panels
          : null,
      pages: Object.keys(project.pages || {}),
      // Declared file formats — what makes installing this app teach the
      // desktop a new type (files/functions/fileHandlers.js reads it off here).
      opens: project.opens || null,
      // Declared record routes — what makes installing this app teach the
      // shell where its rows open (functions/moduleRecordRoutes.js reads it
      // off here, and `entityRefHref` off that).
      recordRoutes: project.recordRoutes || null,
      // Declared record rows — which of this app's components draws one row
      // of its collection where the shell lists records (an AI answer);
      // functions/moduleRecordRows.js reads it off here.
      recordRows: project.recordRows || null,
      // Declared read-only views for AI answers (functions/assistantAppViews.js).
      // Mounted under the app's context like a widget body, so they publish
      // with the widgets — after the app's wrap.
      views:
        _appActivations.has(ledgerId) && !_appActivations.get(ledgerId).done
          ? null
          : project.views || null,
      // Declared forms for AI answers (functions/assistantAppForms.js) — the
      // form mounts under the app's context too: published with the views.
      forms:
        _appActivations.has(ledgerId) && !_appActivations.get(ledgerId).done
          ? null
          : project.forms || null,
      // THE APP'S OWN AI SURFACE, kept per app (DESIGN-COMPOSER-FOCUS-POPOVER-
      // CONTEXT-INSPECT-1). The composed stash (`root.workspaceProject`) unions
      // every app's starters, verbs and actions and loses which app said what;
      // the composer's context selector scopes them back to ONE app (its
      // suggestions, the capabilities answer), so each entry keeps its own.
      // Data only — the call gate stays the composed stash's `aiFunctions`.
      chatStarters: Array.isArray(project.chatStarters) ? project.chatStarters : [],
      aiFunctions: Array.isArray(project.aiFunctions) ? project.aiFunctions : [],
      aiFunctionDocs:
        project.aiFunctionDocs && typeof project.aiFunctionDocs === 'object'
          ? project.aiFunctionDocs
          : {},
      actionKeys: moduleActionKeys(project.actions),
      actionDocs: moduleActionDocs(project.actions),
      actionLabels: moduleActionLabels(project.actions)
    })
  }
  _mark('wsboot:module-apps:prefetch:end')
  _measure(
    'wsboot:module-apps:prefetch',
    'wsboot:module-apps:prefetch:start',
    'wsboot:module-apps:prefetch:end'
  )

  // WS-MODULE-STATE-SCOPE-1 — (re)install the cross-chunk root getters after the
  // additive apps injected. `loadWorkspaceProject` installs them for the
  // TAKEOVER module only, so on a workspace whose personalization is apps-only
  // (the natali shape: no takeover, five installed apps) `root._moduleStateGet`
  // was never published at all and `uiCatalog` reported every installed app's
  // loaded slices as empty. Idempotent: the same closures are re-assigned.
  _installLiveGetter(root)

  // ONE merged approval-action registry over the takeover module + every
  // additive app. Action keys are module-scoped by convention (crm createDeal,
  // scheduling assignShift, …) — a cross-module duplicate keeps the first and
  // warns, matching the shell's collision policy everywhere else.
  //
  // WS-MODULE-ACTIONS-RUN-ON-THE-SHELL-ROOT-NOT-THE-MODULE-STORE-1 — the merge
  // used to discard WHICH module an action key came from, so
  // buildModuleActionFns had no way to find that module's own state root and
  // ran validate/run against whatever root the invoking (shell chat) element
  // happened to carry. `mergedActionOwners` keeps the project id so the trio
  // can look the owner's store up through `root._moduleStateGet`.
  const mergedActions = {}
  const mergedActionOwners = {} // action key → owning project id
  const _mergeActions = (actions, from, ownerId) => {
    if (!actions || typeof actions !== 'object') return
    for (const [key, def] of Object.entries(actions)) {
      // Skip serializer metadata (__order) + anything that isn't an action def.
      if (!def || typeof def !== 'object') continue
      if (typeof def.run !== 'function' && typeof def.validate !== 'function') continue
      if (key in mergedActions) {
        console.warn('[workspaceApps] action key collision skipped:', key, 'from', from)
        continue
      }
      mergedActions[key] = def
      mergedActionOwners[key] = ownerId
    }
  }
  _mergeActions(_takeoverActions, 'workspaceModule', _takeoverStash && _takeoverStash.id)
  for (const p of appProjects) _mergeActions(p.actions, p.id, p.id)

  // Build the ONE shared capability bridge set over the union of every app's
  // grants — plus the single approval-action trio over the merged registry —
  // and inject under the plain names apps call (moduleData / moduleAiChat /
  // moduleFileUpload / proposeModuleAction / …), tracked in the synthetic caps
  // ledger so the next load tears it down and rebuilds it. This is what lets
  // two record-using apps (crm + cms) both reach the records plane, and what
  // makes EVERY installed app's actions proposable — a per-app bridge/trio
  // would collide on the shared name and only the first would work.
  const hasIntegrationsGrant =
    integrationKindsWildcard ||
    integrationCapabilitiesWildcard ||
    mergedIntegrationKinds.size ||
    mergedIntegrationCapabilities.size
  // Installed surfaces that may post workspace notifications through the ONE
  // shared `moduleNotify` bridge — the takeover module (if any) plus every
  // loaded additive app. Ambient like moduleOpenPage (no capability grant:
  // the server surface already lets any member notify another member; the
  // bridge only constrains recipients to the workspace roster and stamps the
  // caller's app identity + a workspace-addressed href).
  const notifyIds = [
    ...(_takeoverStash && _takeoverStash.id ? [_takeoverStash.id] : []),
    ...loaded.map((a) => a.appId)
  ].filter(Boolean)
  if (
    ctx &&
    (mergedCollections.size ||
      hasIntegrationsGrant ||
      aiCap ||
      filesCap ||
      mergedDependencies.size ||
      Object.keys(mergedActions).length ||
      notifyIds.length)
  ) {
    const led = { components: [], functions: [], pages: [] }
    ctx.functions = ctx.functions || {}
    // WS-MODULE-OWN-KEYS-STALE-ACROSS-ORG-SWITCH-1 — see _captureBags.
    led.bags = _captureBags(ctx, routedEl)
    const capFns = {}
    if (mergedDependencies.size) {
      const deps = Object.fromEntries(mergedDependencies)
      capFns.moduleImport = buildModuleImportBridge(deps)
      capFns.moduleAssetUrl = buildModuleAssetUrlBridge(deps)
      capFns.moduleMount = buildModuleMountBridge(deps)
      capFns.moduleUnmount = buildModuleUnmountBridge()
      capFns.moduleApi = buildModuleApiBridge()
      capFns.moduleToken = buildModuleTokenBridge()
      capFns.moduleTheme = buildModuleThemeBridge()
    }
    // The page seams for INSTALLED apps (current-2a 2026-09-28): an app from
    // the App Store opens its own records in the PageSlot (moduleOpenPage)
    // and writes a same-page record open into the address (setModuleRouteQuery
    // — `/realestate?unit=…`), exactly like a takeover module. Ambient like
    // moduleNotify: both are scope-guarded to the installed modules' own
    // pages (moduleOpenBridge.js), and a takeover's own copies win below.
    capFns.moduleOpenPage = buildModuleOpenBridge(root)
    capFns.setModuleRouteQuery = buildModuleRouteQueryBridge(root)
    // Same seam for installed apps: hide the shell composer on the caller's
    // own page only (moduleShellComposer.js). Ambient like moduleOpenPage.
    capFns.setShellComposer = buildModuleShellComposerBridge(root, {
      moduleIdOf: moduleIdOfContext
    })
    // What the member may see (MODULE-ROLE-CAN-BRIDGES-1): their role and
    // whether they hold a permission key, from the workspace read's `access`
    // — so an app hides what its member cannot use (moduleAccessBridge.js).
    // Ambient like moduleOpenPage: read-only answers about the caller.
    capFns.moduleRole = buildModuleRoleBridge(root)
    capFns.moduleCan = buildModuleCanBridge(root)
    // An app asks the assistant about what it shows, the context structured
    // like an Inspect & ask pick (moduleAskBridge.js, MODULE-ASK-BRIDGE-1).
    capFns.moduleAsk = buildModuleAskBridge(root)
    // Unconditional for the same reason as the takeover branch above: the
    // bridge live-resolves grants per call and fails closed — a registration
    // gated on load-time grant state freezes "no data plane" for the session
    // whenever settings resolve late.
    {
      capFns.moduleData = buildModuleDataBridge(
        root,
        {
          ...(mergedCollections.size
            ? { records: { collections: [...mergedCollections], ops: [...mergedOps] } }
            : {}),
          ...(hasIntegrationsGrant
            ? {
                integrations: {
                  kinds: integrationKindsWildcard ? '*' : [...mergedIntegrationKinds],
                  capabilities: integrationCapabilitiesWildcard
                    ? '*'
                    : [...mergedIntegrationCapabilities]
                }
              }
            : {})
        },
        _moduleDataBridgeOpts()
      )
    }
    // Unconditional for the same cold-boot heal as the takeover path — the
    // bridge checks the live grant per call.
    capFns.moduleAiChat = buildModuleAiBridge(root, aiCap ? { ai: aiCap } : {})
    capFns.moduleAiStream = buildModuleAiStreamBridge(root, aiCap ? { ai: aiCap } : {})
    capFns.moduleAiAdmin = buildModuleAiAdminBridge(root, aiCap ? { ai: aiCap } : {})
    if (filesCap) {
      capFns.moduleFileUpload = buildModuleFileBridge(root, { files: filesCap })
      capFns.moduleFileUploadLarge = buildModuleFileUploadLargeBridge(root, { files: filesCap })
      capFns.moduleFileList = buildModuleFileListBridge(root, { files: filesCap })
      capFns.moduleFileRead = buildModuleFileReadBridge(root, { files: filesCap })
      capFns.moduleFileExtractText = buildModuleFileExtractTextBridge(root, { files: filesCap })
      capFns.moduleFileUpdate = buildModuleFileUpdateBridge(root, { files: filesCap })
      capFns.moduleFileCreate = buildModuleFileCreateBridge(root, { files: filesCap })
    }
    if (Object.keys(mergedActions).length) {
      // Resolve EACH action's validate/summarize/run root to its OWNER
      // module's own state store — falls back to the shell root when that
      // module never built one (e.g. it declared actions but its page never
      // mounted this session), same soft-fail as an unscoped module.
      Object.assign(
        capFns,
        buildModuleActionFns(mergedActions, (actionKey, shellRoot) => {
          const ownerId = mergedActionOwners[actionKey]
          return ownerId != null ? shellRoot._moduleStateGet?.(ownerId) : null
        })
      )
    }
    if (notifyIds.length) {
      capFns.moduleNotify = buildModuleNotifyBridge(
        root,
        notifyIds,
        _sdkFnOverrideForTests ? { getSdkFn: _sdkFnOverrideForTests } : undefined
      )
      // Its pair: close the open notices an app sent under one dedupKey.
      capFns.moduleNotifyClose = buildModuleNotifyCloseBridge(
        root,
        notifyIds,
        _sdkFnOverrideForTests ? { getSdkFn: _sdkFnOverrideForTests } : undefined
      )
    }
    // A name the TAKEOVER ledger already holds is the takeover loader's own
    // platform bridge over its own grant — that loader always runs first on
    // every activation path and owns it; this union covers the additive apps.
    // Not a collision and not a warn (it used to log `key collision skipped:
    // moduleData/moduleAiChat/moduleAiAdmin` on every cold takeover boot,
    // which hid the one real collision — WS-MODULEDATA-GRANT-LOST-ON-ORG-
    // RETURN-1 — in noise).
    for (const k of Object.keys(capFns)) {
      if (k in ctx.functions && _injectedFunctionKeys.includes(k)) delete capFns[k]
    }
    _injectEntries(ctx.functions, capFns, led.functions)
    _injectedByApp.set(_CAPS_LEDGER, led)
  }

  // Publish the AI-surface stash composed over the takeover module + the
  // additive apps, so the assistant's knowledge layer (uiCatalog), the client
  // tool gate (aiFunctions) and the chat starters cover app-only workspaces
  // too — the additive loader used to leave root.workspaceProject null, which
  // blanked all three for every workspace without a takeover module.
  // Definitive vs transient verdict for THIS pass. `loaded === 0` while the
  // record names refs means every install failed to load (network/auth churn,
  // channel resolution outage) — publish what we have, but keep the converge
  // retry armed and do NOT latch the 404 suppression as "settled": a settled
  // empty surface with N>0 installed apps is exactly the empty-dashboard bug.
  // Refs that are ALL unresolvable extensions land here too — the retry is
  // bounded by the converge loop's backoff, which settles the latch on
  // give-up so a genuinely dead catalog still 404s instead of blanking.
  const definitive = loaded.length === refs.length
  // Every ref that did NOT load and carries a content verdict. Their failure is
  // settled: a retry re-reads the same published payload and refuses it again.
  // Naming them is the only useful thing left to do — the owner has to
  // republish or remove the app.
  const _loadedIds = new Set(loaded.map((a) => a.appId))
  const blockedIds = refs.map(_appId).filter((id) => !_loadedIds.has(id) && _refusals.has(id))
  // Settled = nothing left that a retry could still turn into a load. Note this
  // holds `loaded.length === 0` too: an install list of one blocked app is
  // blocked, not 'error', and must not keep the converge loop running.
  const settled = !definitive && loaded.length + blockedIds.length === refs.length
  _lastAppsOutcome =
    refs.length === 0
      ? 'empty'
      : definitive
        ? 'active'
        : settled
          ? 'blocked'
          : loaded.length
            ? 'partial'
            : 'error'
  if (blockedIds.length) {
    console.warn(
      '[workspaceApps] blocked, a retry cannot change this:',
      blockedIds.map((id) => `${id} (${_refusals.get(id).code})`).join(', ')
    )
  }
  updateWorkspaceActivation(root, 'apps', {
    state: _lastAppsOutcome === 'active' ? 'ready' : _lastAppsOutcome,
    loaded: loaded.length,
    total: refs.length,
    blocked: blockedIds
  })
  // BOOT-RESHUFFLE F4: identical-republish guard, same shape as the takeover
  // publish above. `root.workspaceApps` is nulled by the switch registry —
  // a wiped root always republishes regardless of the fingerprint.
  const _appsFp = _publishFp(['apps', String(wsId || ''), loaded, _takeoverStash ? 1 : 0])
  if (_appsFp && _appsFp === _lastAppsFp && Array.isArray(root.workspaceApps)) {
    // identical republish — still re-assert the routes-settled latch when
    // this pass is definitive and the latch is down.
    if (definitive && !root._appRoutesSettled) root.update({ _appRoutesSettled: true })
  } else {
    _lastAppsFp = _appsFp
    const _composedStash = _composeAiStash(_takeoverStash, appProjects, mergedActions)
    // Hold the last-known chrome through TRANSITIONAL rebuilds: this composed
    // republish can run while the takeover reload is mid-flight
    // (_takeoverStash null), and a chrome-less frame unmounts the always-on
    // module rail + drops the immersive gates until convergence re-publishes
    // (measured live 2026-08-14: rail flicker + double-sidebar on View-as).
    // A rebuild that genuinely removes the module tears the stash down via
    // `workspaceProject: null` elsewhere, so carrying forward never
    // resurrects a dead module's chrome.
    // ⚠ ALSO LOAD-BEARING FOR THE STAGE REPAIR (BF-STAGE-ACTIVATION-
    // FLAKE-1): the takeover swallow branch calls applyStageVar, whose
    // removal path is reachable only if a chrome-less stash can sit on the
    // root while the module has chrome — exactly what this hold prevents.
    // Removing or narrowing it makes `--stage-bg` actively REMOVABLE at
    // the swallow, and the wrong-colour flake returns on a build that
    // carries the fix.
    if (
      _composedStash &&
      !_composedStash.chrome &&
      root.workspaceProject &&
      root.workspaceProject.chrome
    ) {
      _composedStash.chrome = root.workspaceProject.chrome
    }
    const tPublish = _spanStart()
    root.update({
      workspaceApps: loaded,
      workspaceProject: _composedStash,
      // Route injection is COMPLETE at this point — latch the '/*' catch-all's
      // 404 suppression here, in the function BOTH loader entrypoints share.
      // auth.js's `_ensureWorkspaceModule` also settles this after its chain,
      // but workspace switches load the module via `onWorkspaceChange`, which
      // never passes through that wrapper — on those boots the latch stayed
      // unset forever and a genuinely nonexistent path showed a blank page
      // instead of its 404 (measured: apps=9 injected, 185 routes live,
      // `_appRoutesSettled` still undefined). Setting it in the same update
      // that publishes the injected apps closes every DEFINITIVE path; a pass
      // that expected apps and loaded none stays unsettled and retries.
      // A BLOCKED pass settles here too: its unloaded apps carry content
      // verdicts, so no later pass will add a route, and leaving the latch down
      // would hide every genuinely nonexistent path behind a loader forever.
      ...(definitive || settled ? { _appRoutesSettled: true } : {})
    })
    _span('module-apps', 'publish', tPublish)
  }
  if (!definitive) {
    console.warn(
      '[workspaceApps]',
      loaded.length,
      'of',
      refs.length,
      settled
        ? 'installed apps loaded — the rest are blocked'
        : 'installed apps loaded — will retry'
    )
    // A settled pass has nothing left to converge ON: every unloaded app
    // carries a content verdict. Scheduling the loop anyway made it wake, read
    // the same verdict and stand down, once per backoff step.
    if (!settled) _scheduleModuleConverge(root)
  }

  // Same reload recovery the takeover loader gets — a hard load on an installed
  // app's route boots before these routes exist and would otherwise strand.
  _rerouteIfStranded(root, ctx && ctx.pages, 'workspaceApps')
  try {
    if (root && typeof root.update === 'function') root.update({ _wsAppsActivating: false })
  } catch (flagErr) {
    /* non-fatal — the flag is a hint for the not-found page, nothing more */
  }

  // Boot fix step 4b: a ?page=<app route> deep link opens its sheet with the
  // REAL page, so that app sets up before the restore; the rest set up in
  // idle slices after the reveal, home feeders first.
  try {
    const search =
      (typeof window !== 'undefined' && window.location && window.location.search) || ''
    const pageParam = search ? new URLSearchParams(search).get('page') : null
    const pagePath = pageParam ? String(pageParam).split('?')[0].split('#')[0] : null
    const target =
      pagePath &&
      [..._appActivations.values()].find(
        (e) => e.root === root && !e.done && e.placeholders[pagePath]
      )
    if (target) await _activateWorkspaceApp(target)
  } catch (_) {
    /* the sheet falls back to the placeholder, which opens the app itself */
  }
  // `_appsSettingUp` holds the home's saved-cell skeletons (pages/main.js
  // _moduleRoutesPending) until every app's widgets have published.
  if (_activationsPending(root)) {
    root.update({ _appsSettingUp: true })
    _runAppActivations(root, () => {
      if (root._appsSettingUp) root.update({ _appsSettingUp: false })
    })
  }

  // Deep-link restore for app-only workspaces — `ctx.pages` now carries every
  // additive app's injected routes, so a ?page=<app route> URL opens its sheet
  // instead of stranding on Home.
  _restorePageParam(root, ctx && ctx.pages)

  _mark('wsboot:module-apps:pass-end')
  _measure(
    'wsboot:module-apps:pass',
    'wsboot:module-apps:pass-start',
    'wsboot:module-apps:pass-end'
  )
  if (!isStale() && (definitive || settled)) {
    _appsSettled = { key: flightKey, root, result: loaded, surface: root.workspaceApps }
  }
  return loaded
}
